// skills/combat/combatUI.js
// Combat UI rendering and interaction

import { combatSkill } from './combat.js';
import { COMBAT_ZONES, COMBAT_TIERS, getZone, getMob, getZoneLoot, getMobLoot, getVisibleMobsForZone, getHiddenMobCount, isZoneVisible, getNextHiddenMob, getZoneMobs } from './combatData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG, COMBAT_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { router } from '../../core/router.js';
import { getItem, getItemName, getItemIcon } from '../../data/items/index.js';
import { formatPercent, formatWithCommas, formatNumber } from '../../utils/format.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { getXPPerHour, getVirtualLevelFromXP, getXPToNextLevel } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getAllSpells, getAvailableSpells, formatSpellEffect, SPELL_ELEMENTS } from '../../data/spells.js';
import { getPetData, PET_CONTRACTS } from '../husbandry/husbandryData.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { isSkillCorrupted, getSkillCorruptionInfo } from '../../systems/corruption.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { rollGoldenOpportunity } from '../pilfering/pilfering.js';
import { heavy as corruptHeavy, light as corruptLight, getZoneCorruptionPhase } from '../../utils/corruptionText.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { buildItemInfoTooltip } from '../../ui/equipmentTooltip.js';
import { getAtlasBgStyle } from '../../data/atlasManifest.js';

// Escape text for use in HTML attributes (data-tooltip="...")
function escapeAttr(text) {
    return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Current tab state
let currentTab = 'tier1';

// Auto-eat food selection dropdown state
let showFoodSelection = false;

// Potion belt selection dropdown state
let showPotionSelection = false;
let selectedPotionCategory = null; // Which category slot dropdown is open

// Track last equipment state for change detection
let lastEquipmentHash = '';

// Track last pyre level for re-rendering zone cards when it changes
let lastPyreLevel = 0;

// Track last research state hash for live updates
let lastResearchHash = '';

// Track last glyph state hash for re-rendering spell status when glyphs change
let lastGlyphHash = '';

// Tier colors for equipment UI styling (border glow, name color)
// These are for tier indication, NOT material color matching
const TIER_COLORS = {
    tier1: '#CD853F',   // Tier 1 - peru/tan
    tier2: '#3CB371',   // Tier 2 - medium sea green
    tier3: '#5B9BD5',   // Tier 3 - royal blue
    tier4: '#E85D75',   // Tier 4 - crimson
    tier5: '#A8A8A8',   // Tier 5 - silver
    tier6: '#B088F9'    // Tier 6 - purple
};

// Fire sprite HTML for Pyre Veil indicators (lazy — atlas loads after module import)
function _makeSpriteHTML(sheet, width, height, extraClass = '') {
    const atlas = getAtlasBgStyle(sheet, width, height);
    if (atlas) {
        return `<span class="sprite-icon fire-sprite${extraClass}" style="${atlas.style}"></span>`;
    }
    return `<span class="sprite-icon fire-sprite${extraClass}" style="background-image: url('${sheet}'); background-position: 0px 0px; width: ${width}px; height: ${height}px; background-size: contain;"></span>`;
}
let _fireSpriteCache = null, _fireSmSpriteCache = null;
function getFireSprite() { return _fireSpriteCache || (_fireSpriteCache = _makeSpriteHTML('assets/icons/ui_icons/fire.png', 16, 16)); }
function getFireSpriteSm() { return _fireSmSpriteCache || (_fireSmSpriteCache = _makeSpriteHTML('assets/icons/ui_icons/fire.png', 12, 12, ' fire-sprite-sm')); }

/**
 * Render a veiled/hidden zone card with corruption effects.
 * @param {Object} zone - Zone data
 * @param {'unknown'|'discovered'} phase - Corruption phase
 */
function renderVeiledZoneCard(zone, phase = 'unknown') {
    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
    const combatLevel = gameState.getSkill(SKILLS.COMBAT)?.level || 1;
    const levelMet = combatLevel >= zone.level;

    const isUnknown = phase === 'unknown';
    const cssClass = isUnknown ? 'corrupted-heavy' : 'corrupted-light';
    const corruptFn = isUnknown ? corruptHeavy : corruptLight;

    // Zone name: unknown = gibberish, discovered = partially corrupted real name
    const zoneName = isUnknown
        ? corruptHeavy('??????')
        : corruptLight(zone.name);

    // Description: unknown = mysterious, discovered = real description with light corruption
    const description = isUnknown
        ? corruptHeavy('The flames whisper of secrets yet unrevealed...')
        : corruptLight(zone.description || 'A place of ancient power awaits beyond the veil.');

    // Pyre requirement text
    const pyreText = isUnknown
        ? corruptFn(`Pyre Level ${zone.pyreLevel} Required`)
        : `Pyre Level ${zone.pyreLevel} Required`;

    return `
        <div class="zone-card veiled ${cssClass}" data-zone-id="${zone.id}">
            <div class="zone-header">
                <span class="zone-name">${zoneName}</span>
                <span class="zone-level">${isUnknown ? '??' : `Lv ${zone.level}`}</span>
            </div>
            <div class="zone-description">${description}</div>
            ${!isUnknown && zone.tiers ? `
                <div class="zone-tiers-preview">
                    ${Object.entries(zone.tiers).map(([tierKey, tier]) => `
                        <div class="tier-preview-row">
                            <span class="tier-preview-name">${corruptLight(tier.name)}</span>
                            <span class="tier-preview-mobs">${tier.mobs.length} ${tier.mobs.length === 1 ? 'foe' : 'foes'}</span>
                        </div>
                    `).join('')}
                </div>
            ` : ''}
            <div class="pyre-requirement">
                ${getFireSprite()}
                <span>${pyreText}</span>
            </div>
            ${!levelMet ? `
                <div class="level-requirement">
                    <span>⚔️ Combat Level ${zone.level} Required</span>
                </div>
            ` : ''}
            <div class="zone-locked-overlay veiled">
                <span class="lock-icon">${getFireSprite()}</span>
                <span class="lock-text">${isUnknown ? '???' : `Pyre ${zone.pyreLevel}`}</span>
            </div>
        </div>
    `;
}

/**
 * Render a tiered zone card (e.g., The Ashen Crucible with t1/t2/t3 sub-tiers)
 */
function renderTieredZoneCard(zone) {
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const pyreSkill = gameState.getSkill(SKILLS.PYRE);
    const state = combatSkill.getCombatState();
    const isLocked = skill.level < zone.level;
    const pyreMet = !zone.pyreLevel || (pyreSkill?.level || 1) >= zone.pyreLevel;
    const unlocks = gameState.player.quests?.unlocks || [];

    // Overall zone progress
    const zoneProgress = gameState.getZoneProgress(zone.id);
    const lifetimeKills = zoneProgress.lifetimeKills;

    // Build tier sections
    const tierOrder = ['t1', 't2', 't3'];
    const tierSectionsHtml = tierOrder.map(tierId => {
        const tier = zone.tiers[tierId];
        if (!tier) return '';

        const tierUnlocked = unlocks.includes(tier.unlock);
        const tierMobs = (tier.mobs || []).map(id => getMob(id)).filter(Boolean);
        const isActiveTier = state.inCombat && state.currentZone?.id === zone.id && state.currentTier === tierId;

        // Boss kill info
        const bossKillsHtml = tierMobs.map(mob => {
            const kills = gameState.getBossKillCount(mob.id);
            const defeated = kills > 0;
            return `
                <div class="tier-boss-entry ${defeated ? 'defeated' : ''}">
                    <span class="boss-icon">${getMobIcon(mob)}</span>
                    <span class="boss-name">${tierUnlocked ? mob.name : '??????'}</span>
                    ${tierUnlocked ? `<span class="boss-kills" data-tooltip="Times defeated">${kills > 0 ? `✓ ${kills}` : '—'}</span>` : ''}
                </div>
            `;
        }).join('');

        // Tier lock requirements with clickable quest links
        let lockReasonHtml = '';
        if (!tierUnlocked) {
            const questMap = { t1: 41, t2: 59, t3: 48 };
            const questNames = { t1: 'Into the Crucible', t2: 'Trial of the Three Flames', t3: 'Where All Flames Return' };
            const questNum = questMap[tierId];
            const questName = questNames[tierId];
            if (questNum) {
                lockReasonHtml = `<span class="quest-link" onclick="event.stopPropagation(); window.questModal.navigateToQuest(${questNum})" data-tooltip="Click to view quest">📜 ${questName}</span>`;
            }
        }

        return `
            <div class="zone-tier-section ${tierUnlocked ? 'unlocked' : 'locked'} ${isActiveTier ? 'active' : ''}" data-tier="${tierId}">
                <div class="tier-header">
                    <span class="tier-label">${tierId.toUpperCase()}</span>
                    <span class="tier-name">${tier.name}</span>
                    ${tierUnlocked ? `<button class="tier-info-btn" onclick="event.stopPropagation(); window.combatUI.openTierDetailsModal('${zone.id}', '${tierId}')" data-tooltip="View bosses, stats, drops">Details</button>` : ''}
                    ${tierUnlocked ? '<span class="tier-status unlocked">🔓</span>' : '<span class="tier-status locked">🔒</span>'}
                </div>
                <div class="tier-bosses">
                    ${bossKillsHtml}
                </div>
                ${tierUnlocked && !isLocked ? `
                    <div class="tier-actions">
                        ${isActiveTier ? `
                            <button class="zone-action-btn stop"
                                    onclick="window.combatUI.stopFighting()">
                                Stop
                            </button>
                        ` : `
                            <button class="zone-action-btn start"
                                    onclick="window.combatUI.openTierDetailsModal('${zone.id}', '${tierId}')">
                                Fight
                            </button>
                        `}
                    </div>
                ` : !tierUnlocked ? `
                    <div class="tier-lock-reason">${lockReasonHtml}</div>
                ` : ''}
            </div>
        `;
    }).join('');

    return `
        <div class="zone-card tiered-zone ${isLocked ? 'locked' : ''}"
             data-zone-id="${zone.id}"
             style="--zone-color: ${zone.color}">
            <div class="zone-color-strip"></div>
            <div class="zone-header">
                <span class="zone-name">${zone.name}</span>
                <span class="zone-level">Lv ${zone.level}</span>
            </div>
            <div class="zone-description">${zone.description}</div>
            ${!pyreMet && zone.pyreLevel ? `
                <div class="zone-pyre-req">
                    ${getFireSpriteSm()}
                    <span>Pyre Level ${zone.pyreLevel}</span>
                </div>
            ` : ''}
            <div class="zone-tier-sections">
                ${tierSectionsHtml}
            </div>
            <div class="zone-lifetime-kills">
                <span class="kills-label">Total Kills:</span>
                <span class="kills-value">${lifetimeKills}</span>
            </div>
            ${isLocked ? `
                <div class="zone-locked-overlay">
                    <span class="lock-icon">🔒</span>
                    <span class="lock-text">Level ${zone.level}</span>
                </div>
            ` : ''}
        </div>
    `;
}

/**
 * Get tier color based on combat level requirement
 */
function getTierColor(combatLevel) {
    if (!combatLevel || combatLevel < 15) return TIER_COLORS.tier1;
    if (combatLevel < 30) return TIER_COLORS.tier2;
    if (combatLevel < 45) return TIER_COLORS.tier3;
    if (combatLevel < 60) return TIER_COLORS.tier4;
    if (combatLevel < 75) return TIER_COLORS.tier5;
    return TIER_COLORS.tier6;
}

/**
 * Get icon for attack style
 */
function getStyleIcon(style) {
    const icons = {
        melee: '⚔️',
        ranged: '🏹',
        magic: '✨'
    };
    return icons[style] || '❓';
}

/**
 * Get style name for display
 */
function getStyleName(style) {
    if (!style) return 'None';
    return style.charAt(0).toUpperCase() + style.slice(1);
}

/**
 * Get element icon for display
 */
function getElementIcon(element) {
    const icons = {
        fire: '🔥',
        ice: '❄️',
        earth: '🪨',
        air: '💨'
    };
    return icons[element] || '❓';
}

/**
 * Get element name for display
 */
function getElementName(element) {
    if (!element) return 'None';
    return element.charAt(0).toUpperCase() + element.slice(1);
}

/**
 * Format drop rate for display (always as 1/X)
 */
function formatDropRate(chance) {
    if (!chance || chance <= 0) return '';
    if (chance >= 1) return 'Always';

    const oneIn = Math.round(1 / chance);
    // If rounds to 1/1, show as Always
    if (oneIn <= 1) return 'Always';
    return `1/${oneIn}`;
}

/**
 * Show floating damage number
 * @param {number} damage - Amount of damage
 * @param {string} target - 'player' or 'mob'
 * @param {string} type - 'damage', 'dot', 'heal', 'miss'
 */
function showDamageNumber(damage, target, type = 'damage') {
    // Find the HP bar group for proper positioning
    const side = document.querySelector(target === 'player' ? '.player-side' : '.mob-side');
    const container = side ? side.querySelector('.hp-group') : null;
    if (!container) return;

    const dmgEl = document.createElement('div');
    dmgEl.className = `damage-number damage-${type}`;

    if (type === 'miss') {
        dmgEl.textContent = 'MISS';
    } else if (type === 'evaded') {
        dmgEl.textContent = 'EVADED!';
    } else if (type === 'heal') {
        dmgEl.textContent = `+${damage}`;
    } else {
        dmgEl.textContent = `-${damage}`;
    }

    // Random horizontal offset for variety
    const offsetX = (Math.random() - 0.5) * 60;
    dmgEl.style.setProperty('--offset-x', `${offsetX}px`);

    container.appendChild(dmgEl);

    // Remove after animation completes
    dmgEl.addEventListener('animationend', () => {
        dmgEl.remove();
    });
}

/**
 * Render compact skill header with HP bar
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const xpPerHour = getXPPerHour(SKILLS.COMBAT);
    const state = combatSkill.getCombatState();

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    // HP bar calculation
    const hpPercent = (state.currentHp / state.maxHp) * 100;
    const hpClass = hpPercent > 50 ? 'hp-high' : hpPercent > 25 ? 'hp-mid' : 'hp-low';

    // Player stats
    const stats = state.playerStats;

    return `
        <div class="compact-header combat-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.COMBAT)}</span>
                    <span class="compact-name">Combat</span>
                    ${renderSkillInfo(SKILLS.COMBAT)}
                    ${renderTalentButton(SKILLS.COMBAT)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                    <span class="compact-stat kills-rate">
                        <span class="stat-label">Kills/h:</span>
                        <span class="stat-value">${formatWithCommas(gameState.getKillsPerHour())}</span>
                    </span>
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render the new v2 combat header (simpler, with style toggles)
 */
function renderCombatHeader() {
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const xpPerHour = getXPPerHour(SKILLS.COMBAT);
    const state = combatSkill.getCombatState();
    const sets = gameState.getEquipmentSets();
    const activeSet = gameState.getActiveEquipmentSet();

    // Level display
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const levelDisplay = isMax && virtualEnabled ? `${skill.level} (${skill.virtualLevel})` :
                         isMax ? `${skill.level}` : skill.level;

    // Style toggle buttons
    const styleButtons = Object.entries(sets).map(([id, set]) => {
        let setIcon = set.icon;
        if (set.gear && set.gear.weapon) {
            const weapon = getItem(set.gear.weapon);
            if (weapon && weapon.attackStyle) {
                const styleIcons = { melee: '⚔️', ranged: '🏹', magic: '🪄' };
                setIcon = styleIcons[weapon.attackStyle] || set.icon;
            }
        }
        return `<button class="style-btn ${activeSet === id ? 'active' : ''}"
                        onclick="window.combatUI.loadSet('${id}')"
                        data-tooltip="Switch to ${set.name}">${setIcon}</button>`;
    }).join('');

    return `
        <div class="combat-header-v2">
            <div class="combat-header-left">
                <span class="combat-icon">⚔️</span>
                <span class="combat-title">Combat</span>
                ${renderSkillInfo(SKILLS.COMBAT)}
                ${renderTalentButton(SKILLS.COMBAT)}
                <span class="combat-level">Lv ${levelDisplay}</span>
            </div>
            <div class="combat-header-center">
                <div class="style-toggles">${styleButtons}</div>
            </div>
            <div class="combat-header-right">
                <span class="header-stat"><span class="stat-label">XP:</span> ${formatWithCommas(skill.xp)}</span>
                <span class="header-stat"><span class="stat-label">XP/h:</span> ${formatWithCommas(xpPerHour)}</span>
                <span class="header-stat"><span class="stat-label">Kills/h:</span> ${formatWithCommas(gameState.getKillsPerHour())}</span>
            </div>
        </div>
    `;
}

/**
 * Render the player panel (left column) - equipment, stats, consumables, magic
 * Note: Player HP/attack bars moved to monster panel for easier combat tracking
 */
function renderPlayerPanel() {
    const state = combatSkill.getCombatState();
    const stats = state.playerStats;
    const gear = gameState.getEquippedGear();
    const activeSet = gameState.getActiveEquipmentSet();
    const autoEat = gameState.getAutoEat();

    // Food display
    const storedFood = autoEat.storedFood;
    const foodItem = storedFood ? getItem(storedFood.itemId) : null;
    const foodIcon = foodItem ? getItemIcon(storedFood.itemId) : '🍖';
    const foodQty = storedFood ? storedFood.quantity : 0;

    // Equipment set buttons
    const sets = gameState.getEquipmentSets();
    const setButtons = Object.entries(sets).map(([id, set]) => {
        let setIcon = set.icon;
        if (set.gear && set.gear.weapon) {
            const weapon = getItem(set.gear.weapon);
            if (weapon && weapon.attackStyle) {
                const styleIcons = { melee: '⚔️', ranged: '🏹', magic: '🪄' };
                setIcon = styleIcons[weapon.attackStyle] || set.icon;
            }
        }
        return `<button class="set-btn ${activeSet === id ? 'active' : ''}"
                        onclick="window.combatUI.loadSet('${id}')"
                        data-tooltip="${set.name}">${setIcon}</button>`;
    }).join('');

    // Magic section wrapper - fixed height to prevent layout shift when switching styles
    const magicContent = renderMagicWrapper(activeSet === 'magic');

    return `
        <div class="player-panel-v3">
            <div class="equipment-sets-row">
                ${setButtons}
            </div>

            <div class="player-equipment">
                <div class="equipment-grid-v2">
                    <div class="equip-row">
                        <div class="equip-spacer"></div>
                        ${renderEquipSlot('head', gear.head)}
                        <div class="equip-spacer"></div>
                    </div>
                    <div class="equip-row">
                        ${activeSet === 'ranged' ? renderEquipSlot('back', gear.back) : '<div class="equip-spacer"></div>'}
                        ${renderEquipSlot('neck', gear.neck)}
                        ${renderEquipSlot('shoulders', gear.shoulders)}
                    </div>
                    <div class="equip-row">
                        ${renderEquipSlot('weapon', gear.weapon)}
                        ${renderEquipSlot('body', gear.body)}
                        ${activeSet === 'ranged' ? renderEquipSlot('ammo', gear.ammo) : renderEquipSlot('offhand', gear.offhand)}
                    </div>
                    <div class="equip-row">
                        ${renderEquipSlot('hands', gear.hands)}
                        ${renderEquipSlot('legs', gear.legs)}
                        ${renderEquipSlot('ring', gear.ring)}
                    </div>
                    <div class="equip-row">
                        <div class="equip-spacer"></div>
                        ${renderEquipSlot('feet', gear.feet)}
                        <div class="equip-spacer"></div>
                    </div>
                </div>
            </div>

            <div class="player-stats-grid">
                <div class="stat-item">
                    <span class="stat-label">${stats.attackStyle === 'magic' ? 'Spell:' : 'Atk:'}</span>
                    <span class="stat-value">${stats.attackStyle === 'magic' ? combatSkill.getTotalSpellDamage().toFixed(2) + 'x' : stats.attack}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Def:</span>
                    <span class="stat-value">${stats.defense}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Speed:</span>
                    <span class="stat-value">${(stats.attackSpeed / 1000).toFixed(1)}s</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Acc:</span>
                    <span class="stat-value">${Math.floor(stats.accuracy)}%</span>
                </div>
            </div>

            <div class="player-consumables-row">
                <div class="player-consumables">
                    <div class="food-section">
                        <div class="consumable-slot food-slot ${storedFood ? 'has-food' : ''} ${!autoEat.unlocked ? 'locked' : ''}" onclick="window.combatUI.toggleFoodSelection()" data-tooltip="${!autoEat.unlocked ? `Auto-eat locked\nRequires: Combat Lv ${COMBAT_CONFIG.AUTO_EAT_UNLOCK_LEVEL} + Mess Hall` : storedFood ? `${getItemName(storedFood.itemId)} (×${foodQty})\nAuto-eat at ${autoEat.threshold}% HP\nClick to manage` : 'Click to select food'}">
                            <span class="consumable-icon">${foodIcon}</span>
                            <span class="consumable-qty">${foodQty}</span>
                        </div>
                        ${renderFoodSelectionDropdown()}
                    </div>
                    ${renderPotionSlotsCompact()}
                </div>
            </div>

            ${magicContent}
        </div>
    `;
}

/**
 * Render magic section wrapper with fixed height
 * Always reserves space to prevent layout shift when switching combat styles
 */
function renderMagicWrapper(showMagic) {
    const currentSpell = gameState.getSelectedSpell();
    const spellData = currentSpell ? getAllSpells().find(s => s.id === currentSpell) : null;

    if (!showMagic) {
        return `<div class="magic-wrapper"></div>`;
    }

    // Check spell casting status
    const canCast = combatSkill.canCastSpell();
    const builtInSpell = combatSkill.getBuiltInSpell();

    // Get spell icon - from selected spell, or element-based for built-in
    const builtInIcon = builtInSpell ? getElementIcon(builtInSpell.element) : null;
    const displayIcon = spellData?.icon || builtInIcon || '🔮';

    // Determine what spell status to show (always show to prevent layout shift)
    let spellStatusHtml = '';
    if (canCast.useBuiltIn && builtInSpell) {
        // Staff has built-in spell that will be used
        if (spellData) {
            // Player selected a spell but missing glyphs, falling back to built-in
            spellStatusHtml = `
                <div class="spell-status fallback">
                    <span class="status-icon">${builtInIcon}</span>
                    <span class="status-text">Using ${builtInSpell.name} (staff)</span>
                </div>
            `;
        } else {
            // No spell selected, using built-in
            spellStatusHtml = `
                <div class="spell-status built-in">
                    <span class="status-icon">${builtInIcon}</span>
                    <span class="status-text">${builtInSpell.name} (built-in)</span>
                </div>
            `;
        }
    } else if (!canCast.success && spellData) {
        // Selected spell can't be cast and no built-in available
        spellStatusHtml = `
            <div class="spell-status missing-glyphs">
                <span class="status-icon">⚠️</span>
                <span class="status-text">${canCast.reason || 'Missing glyphs'}</span>
            </div>
        `;
    } else if (!spellData && !builtInSpell) {
        // No spell selected and no built-in
        spellStatusHtml = `
            <div class="spell-status no-spell">
                <span class="status-icon">❓</span>
                <span class="status-text">No spell selected</span>
            </div>
        `;
    } else if (canCast.success && spellData) {
        // Spell selected and can be cast - show ready status
        spellStatusHtml = `
            <div class="spell-status ready">
                <span class="status-icon">✓</span>
                <span class="status-text">Ready</span>
            </div>
        `;
    }

    return `
        <div class="magic-wrapper">
            <div class="magic-section">
                <div class="spell-info-row">
                    <span class="current-spell-icon">${displayIcon}</span>
                    <div class="spell-details">
                        <span class="current-spell-name">${spellData?.name || 'No Spell'}</span>
                        ${spellData ? `<span class="current-spell-dmg">${spellData.baseDamage} base dmg</span>` : ''}
                    </div>
                    <button class="change-spell-btn" onclick="window.combatUI.openSpellModal()" data-tooltip="Select spell">
                        ▼
                    </button>
                </div>
                ${spellStatusHtml}
                ${renderMiniGlyphPouch()}
            </div>
        </div>
    `;
}

/**
 * Render a single equipment slot for v2 grid
 */
function renderEquipSlot(slotName, itemId) {
    const item = itemId ? getItem(itemId) : null;
    const icon = item ? getItemIcon(itemId) : '?';
    const hasItem = !!item;

    // Use full equipment stats tooltip for equipped items
    const tooltip = hasItem ? buildItemInfoTooltip(itemId) : slotName;

    // Special handling for ammo slot - show quantity
    let qtyHtml = '';
    if (slotName === 'ammo' && hasItem) {
        const ammo = gameState.getEquippedAmmo();
        if (ammo.quantity > 0) {
            const qtyDisplay = ammo.quantity >= 1000 ? formatNumber(ammo.quantity) : ammo.quantity;
            qtyHtml = `<span class="ammo-qty">${qtyDisplay}</span>`;
        }
    }

    return `
        <div class="equip-slot-v2 ${hasItem ? 'filled' : 'empty'} ${slotName === 'ammo' ? 'ammo-slot' : ''}"
             data-slot="${slotName}"
             ${hasItem ? `onclick="window.combatUI.unequipItem('${slotName}')"` : ''}
             data-tooltip="${tooltip.replace(/"/g, '&quot;')}">
            <span class="slot-icon">${icon}</span>
            ${qtyHtml}
        </div>
    `;
}

/**
 * Render compact potion slots - shows only filled belt slots
 */
function renderPotionSlotsCompact() {
    const beltUnlocked = gameState.isApothecaryBeltUnlocked();
    if (!beltUnlocked) return '';

    const slots = gameState.getApothecarySlots();
    const filledSlots = slots.map((slot, i) => [i, slot]).filter(([_, slot]) => slot && slot.quantity > 0);

    if (filledSlots.length === 0) {
        return `
            <div class="potion-section">
                <div class="consumable-slot potion-add" onclick="window.combatUI.openPotionBelt()" data-tooltip="Add potions to belt">
                    <span class="consumable-icon">🧪</span>
                    <span class="consumable-label">+</span>
                </div>
            </div>
        `;
    }

    return `
        <div class="potion-section">
            ${filledSlots.map(([slotIndex, slot]) => {
                const potionIcon = getItemIcon(slot.itemId);
                const potionName = getItemName(slot.itemId);
                return `
                    <div class="consumable-slot potion-slot filled"
                         onclick="window.combatUI.openPotionBelt(${slotIndex})"
                         data-tooltip="${potionName} (×${slot.quantity})">
                        <span class="consumable-icon">${potionIcon}</span>
                        <span class="consumable-qty">${slot.quantity}</span>
                    </div>
                `;
            }).join('')}
            ${filledSlots.length < 4 ? `
            <div class="consumable-slot potion-add" onclick="window.combatUI.openPotionBelt()" data-tooltip="Add potion">
                <span class="consumable-icon">+</span>
            </div>
            ` : ''}
        </div>
    `;
}

/**
 * Render potion selection dropdowns for active selections
 */
function renderPotionSelectionDropdowns() {
    if (!selectedPotionCategory) return '';
    return renderPotionSelectionDropdown(selectedPotionCategory);
}

/**
 * Render compact spell selection for magic
 */
function renderCompactSpellSelect() {
    const currentSpell = gameState.getSelectedSpell();
    const spellData = currentSpell ? getAllSpells().find(s => s.id === currentSpell) : null;

    return `
        <div class="spell-select-compact" onclick="window.combatUI.openSpellModal()">
            <span class="spell-icon">${spellData ? SPELL_ELEMENTS[spellData.element]?.icon || '🔮' : '🔮'}</span>
            <span class="spell-name">${spellData ? spellData.name : 'Select Spell'}</span>
            <span class="spell-arrow">▼</span>
        </div>
    `;
}

/**
 * Render player buffs in a compact row format
 */
function renderPlayerBuffsCompact(potionBuffs) {
    const buffs = [];

    // Add potion buffs
    for (const [category, buff] of Object.entries(potionBuffs)) {
        if (!buff || !buff.active) continue;
        const remainingSeconds = Math.ceil((buff.combatTimeRemaining || 0) / 1000);
        const effectText = formatPotionEffect(buff.effects, false, false);
        buffs.push({
            icon: getBuffIcon(category),
            label: getBuffAbbr(category),
            tooltip: `${buff.potionName} — ${effectText}`,
            duration: remainingSeconds,
            type: 'buff'
        });
    }

    // Add spell effect buffs (defense, crit, hot from spells)
    const state = combatSkill.getCombatState();
    const spellEffects = state.spellEffects || {};

    if (spellEffects.defense && spellEffects.defense.active) {
        buffs.push({
            icon: '🪨',
            label: 'DEF',
            tooltip: `Stone Ward +${spellEffects.defense.bonus} Def`,
            duration: Math.ceil((spellEffects.defense.remaining || 0) / 1000),
            type: 'buff'
        });
    }

    if (spellEffects.crit && spellEffects.crit.active) {
        buffs.push({
            icon: '⚡',
            label: 'CRIT',
            tooltip: `Crit Boost +${spellEffects.crit.bonus}%`,
            duration: Math.ceil((spellEffects.crit.remaining || 0) / 1000),
            type: 'buff'
        });
    }

    if (spellEffects.hot && spellEffects.hot.active) {
        buffs.push({
            icon: '💚',
            label: 'HOT',
            tooltip: `Regen +${spellEffects.hot.amount} HP/tick`,
            duration: Math.ceil((spellEffects.hot.remaining || 0) / 1000),
            type: 'buff'
        });
    }

    if (buffs.length === 0) {
        return '';
    }

    return buffs.map(buff => `
        <div class="buff-chip ${buff.type}" data-tooltip="${buff.tooltip}">
            <span class="buff-icon">${buff.icon}</span>
            <span class="buff-label">${buff.label}</span>
            <span class="buff-timer">${buff.duration}s</span>
        </div>
    `).join('');
}

/**
 * Get short abbreviation for buff category (shown on chip)
 */
function getBuffAbbr(category) {
    const abbrs = {
        attack: 'ATK', defense: 'DEF', accuracy: 'ACC', speed: 'SPD', hybrid: 'HYB',
        crit: 'CRIT', reduction: 'RES', evasion: 'EVA', magic: 'MAG', ranged: 'RNG',
        melee: 'MEL', all: 'AC'
    };
    return abbrs[category] || category.toUpperCase().slice(0, 3);
}

/**
 * Get icon for buff category
 */
function getBuffIcon(category) {
    const icons = {
        attack: '⚔️', defense: '🛡️', accuracy: '🎯', speed: '⚡', hybrid: '⚖️',
        crit: '💥', reduction: '🧱', evasion: '💨', magic: '✨', ranged: '🏹', all: '🌟'
    };
    return icons[category] || '🧪';
}

/**
 * Escape special characters for use in HTML title attributes
 * Uses title instead of data-tooltip for better multi-line support
 */
function escapeTitle(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Render boss mechanic indicators (phase, enrage timer, active effects)
 */
function renderBossIndicators(state) {
    if (!state.bossState) return '';

    const bs = state.bossState;
    const mechanics = bs.mechanics;
    const parts = [];

    // Phase indicator
    if (bs.currentPhase && mechanics.phases && mechanics.phases.length > 1) {
        const phaseIdx = mechanics.phases.findIndex(p => p.id === bs.currentPhase.id);
        const phaseCount = mechanics.phases.length;
        const phaseName = bs.currentPhase.name;
        const phase = bs.currentPhase;

        // Build compact tooltip
        const effects = [];
        if (phase.hpThreshold < 1) {
            effects.push(`<${Math.round(phase.hpThreshold * 100)}% HP`);
        }
        if (phase.mobMods) {
            const mods = phase.mobMods;
            if (mods.speedMult) effects.push(`+${Math.round((1 - mods.speedMult) * 100)}% speed`);
            if (mods.damageMult) effects.push(`+${Math.round((mods.damageMult - 1) * 100)}% dmg`);
            if (mods.defenseBonus) effects.push(`+${mods.defenseBonus} def`);
            if (mods.playerDamageReduction) effects.push(`-${Math.round(mods.playerDamageReduction * 100)}% your dmg`);
        }
        if (phase.playerEffects) {
            for (const eff of phase.playerEffects) {
                if (eff.type === 'damage_reduction') effects.push(`-${Math.round(eff.value * 100)}% your dmg`);
            }
        }

        const phaseTooltip = effects.length > 0 ? `${phaseName}: ${effects.join(', ')}` : phaseName;
        parts.push(`<span class="boss-phase" data-tooltip="${phaseTooltip}">${phaseName} (${phaseIdx + 1}/${phaseCount})</span>`);
    }

    // Enrage timer
    if (mechanics.enrage) {
        const elapsed = Date.now() - bs.fightStartTime;
        const stages = mechanics.enrage.stages;

        if (bs.enrageStage >= 0) {
            // Already enraged - build tooltip with only meaningful effects
            const stage = stages[bs.enrageStage];
            const stageLabel = stages.length > 1 ? ` ${bs.enrageStage + 1}` : '';
            const effects = [];
            if (stage.damageMult > 1) effects.push(`${stage.damageMult}x dmg`);
            if (stage.speedMult && stage.speedMult < 1) effects.push(`${(1 / stage.speedMult).toFixed(1)}x speed`);
            const enragedTooltip = effects.length > 0 ? `ENRAGED! ${effects.join(', ')}` : 'ENRAGED!';
            const displayEffect = stage.damageMult > 1 ? `${stage.damageMult}x dmg` : `${(1 / (stage.speedMult || 1)).toFixed(1)}x spd`;
            parts.push(`<span class="boss-enraged" data-tooltip="${enragedTooltip}">ENRAGED${stageLabel} (${displayEffect})</span>`);
        } else {
            // Countdown to first enrage - build tooltip with only meaningful effects
            const firstStage = stages[0];
            const remaining = Math.max(0, firstStage.timer - elapsed);
            const secs = Math.ceil(remaining / 1000);
            const effects = [];
            if (firstStage.damageMult > 1) effects.push(`${firstStage.damageMult}x dmg`);
            if (firstStage.speedMult && firstStage.speedMult < 1) effects.push(`+${Math.round((1 - firstStage.speedMult) * 100)}% speed`);
            const enrageTooltip = effects.length > 0 ? `Boss gains: ${effects.join(', ')}` : 'Boss enrages!';
            parts.push(`<span class="boss-enrage-timer" data-tooltip="${enrageTooltip}">Enrage: ${secs}s</span>`);
        }
    }

    // Damage floor
    if (mechanics.damageFloor > 0) {
        const floorTooltip = `Hits below ${mechanics.damageFloor} deal only 1 damage`;
        parts.push(`<span class="boss-damage-floor" data-tooltip="${floorTooltip}">Floor: ${mechanics.damageFloor}</span>`);
    }

    // Active periodic effects
    if (mechanics.periodicEffects) {
        for (const effect of mechanics.periodicEffects) {
            // Check if active in current phase
            const isIntensified = effect.intensify && bs.currentPhase?.id === effect.intensify.phase;
            const isActive = !effect.activePhase || bs.currentPhase?.id === effect.activePhase || isIntensified;

            if (!isActive) continue;

            const amount = isIntensified ? effect.intensify.amount : effect.amount;
            const interval = effect.interval / 1000;

            let effectTooltip = '';
            if (effect.type === 'heal_mob') {
                effectTooltip = `Boss heals ${amount} HP/${interval}s`;
            } else if (effect.type === 'damage_player' || effect.type === 'true_damage_player') {
                effectTooltip = `You take ${amount} dmg/${interval}s`;
            } else {
                effectTooltip = effect.description || effect.name;
            }
            if (isIntensified) effectTooltip += ' (Intensified)';

            const label = isIntensified ? `${effect.name}+` : effect.name;
            parts.push(`<span class="boss-effect-active" data-tooltip="${effectTooltip}">${label}</span>`);
        }
    }

    // Active player DoTs from boss
    if (bs.activePlayerDots && bs.activePlayerDots.length > 0) {
        for (const dot of bs.activePlayerDots) {
            const dotTooltip = `${dot.damage} dmg/${dot.interval / 1000}s`;
            parts.push(`<span class="boss-player-dot" data-tooltip="${dotTooltip}">${dot.name}</span>`);
        }
    }

    if (parts.length === 0) return '';

    return `<div class="boss-indicators">${parts.join('')}</div>`;
}

/**
 * Render combat warning if player cannot attack (missing ammo or glyphs)
 */
function renderCombatWarning(playerStats) {
    const attackStyle = playerStats?.attackStyle;

    // Check ranged - need arrows
    if (attackStyle === 'ranged') {
        const ammo = gameState.getEquippedAmmo();
        const builtInAmmo = combatSkill.getBuiltInAmmo();
        const hasAmmo = (ammo?.itemId && ammo?.quantity > 0) || builtInAmmo;
        if (!hasAmmo) {
            return `
                <div class="combat-warning no-attack">
                    <span class="warning-icon">⚠️</span>
                    <span class="warning-text">No arrows! Equip ammo to attack.</span>
                </div>
            `;
        }
    }

    // Check magic - need glyphs for spell (unless built-in available)
    if (attackStyle === 'magic') {
        const canCast = combatSkill.canCastSpell();
        if (!canCast.success && !canCast.useBuiltIn) {
            return `
                <div class="combat-warning no-attack">
                    <span class="warning-icon">⚠️</span>
                    <span class="warning-text">${canCast.reason || 'Cannot cast spell!'}</span>
                </div>
            `;
        }
    }

    return '';
}

/**
 * Render the monster panel (right column) - combat info, monster display, and player combat bars
 * Now includes player HP/attack bars for easier combat tracking
 */
function renderMonsterPanel() {
    const state = combatSkill.getCombatState();
    const isInCombat = state.inCombat;
    const isPreviewing = state.isPreviewing;

    // If dead, show death overlay
    if (state.isDead) {
        const remaining = Math.ceil(state.respawnRemaining / 1000);
        return `
            <div class="monster-panel-v3 death-panel">
                <div class="death-overlay-content">
                    <span class="death-icon">💀</span>
                    <h3>You have died!</h3>
                    <p>Respawning in <span class="respawn-timer">${remaining}</span> seconds...</p>
                    <div class="respawn-progress">
                        <div class="respawn-bar" style="width: ${100 - (state.respawnRemaining / COMBAT_CONFIG.RESPAWN_TIME * 100)}%"></div>
                    </div>
                </div>
            </div>
        `;
    }

    // If previewing, show the preview panel instead
    if (isPreviewing && state.previewState) {
        return renderMonsterPreview(state);
    }

    // If in combat, show monster with split layout
    if (isInCombat && state.currentMob) {
        // state.currentMob can be either a mob ID string or the full mob object
        const mob = typeof state.currentMob === 'string' ? getMob(state.currentMob) : state.currentMob;
        const zone = getZone(state.currentZone?.id || state.currentZone);
        const mobHpPercent = (state.mobCurrentHp / state.mobMaxHp) * 100;
        const mobHpClass = mobHpPercent > 50 ? 'hp-high' : mobHpPercent > 25 ? 'hp-mid' : 'hp-low';

        // Player HP info
        const playerHpPercent = (state.currentHp / state.maxHp) * 100;
        const playerHpClass = playerHpPercent > 50 ? 'hp-high' : playerHpPercent > 25 ? 'hp-mid' : 'hp-low';
        const playerStats = state.playerStats;

        // Get mob debuffs (from spell effects like DoT, Slow)
        const mobDebuffs = buildMobDebuffsFromSpellEffects(state.spellEffects);
        const mobDebuffsHtml = renderMonsterDebuffsCompact(mobDebuffs);

        // Get mob drops
        const zoneId = state.currentZone?.id || state.currentZone;
        const mobDropsHtml = renderMobDropsCompact(state.currentMob, zoneId);

        // Get mob buffs (from talents/abilities)
        const mobBuffs = state.mobBuffs || [];
        const mobBuffsHtml = renderMonsterBuffsCompact(mobBuffs);

        // Get player buffs/debuffs
        const potionBuffs = gameState.getPotionBuffs();
        const playerBuffsHtml = renderPlayerBuffsCompact(potionBuffs);
        const playerDebuffsHtml = renderPlayerDebuffsCompact(state);

        return `
            <div class="monster-panel-v3">
                <div class="monster-display-area">
                    <div class="monster-icon-large">${getMobIcon(mob)}</div>
                    <div class="monster-name">${mob?.name || 'Unknown'}</div>
                    <div class="monster-zone">${zone?.name || ''}</div>
                    <div class="monster-stats">
                        <span class="mob-stat">⚔️ ${mob?.attack || 0}-${mob?.attackMax || 0}</span>
                        <span class="mob-stat">🛡️ ${mob?.defense || 0}</span>
                        <span class="mob-stat">⚡ ${((mob?.attackSpeed || 3000) / 1000).toFixed(1)}s</span>
                    </div>
                </div>

                <div class="combat-bars-section">
                    <div class="combatant-side player-side">
                        <div class="combatant-label">You</div>
                        <div class="bar-group hp-group">
                            <span class="bar-label">HP</span>
                            <div class="combat-hp-bar ${playerHpClass}" data-combatant="player">
                                <div class="hp-fill" style="width: ${playerHpPercent}%"></div>
                                <span class="hp-text">${state.currentHp} / ${state.maxHp}</span>
                            </div>
                        </div>
                        <div class="effects-inline player-effects">
                            ${playerBuffsHtml || ''}${playerDebuffsHtml || ''}
                        </div>
                        <div class="bar-group">
                            <span class="bar-label">Attack</span>
                            <div class="combat-attack-bar" data-combatant="player">
                                <div class="attack-fill"></div>
                                <span class="attack-label">${(playerStats.attackSpeed / 1000).toFixed(1)}s</span>
                            </div>
                        </div>
                        ${renderCombatWarning(playerStats)}
                    </div>

                    <div class="combatant-side mob-side">
                        <div class="combatant-label${state.bossState ? ' boss-label' : ''}">${mob?.name || 'Monster'}${state.bossState ? ' <span class="boss-tag">BOSS</span>' : ''}</div>
                        <div class="bar-group hp-group">
                            <span class="bar-label">HP</span>
                            <div class="combat-hp-bar enemy ${mobHpClass}" data-combatant="monster">
                                <div class="hp-fill" style="width: ${mobHpPercent}%"></div>
                                <span class="hp-text">${state.mobCurrentHp} / ${state.mobMaxHp}</span>
                            </div>
                        </div>
                        ${state.bossState ? renderBossIndicators(state) : ''}
                        <div class="effects-inline mob-effects">
                            ${mobBuffsHtml || ''}${mobDebuffsHtml || ''}
                        </div>
                        <div class="bar-group">
                            <span class="bar-label">Attack</span>
                            <div class="combat-attack-bar" data-combatant="monster">
                                <div class="attack-fill"></div>
                                <span class="attack-label">${((mob?.attackSpeed || 3000) / 1000).toFixed(1)}s</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="monster-drops">
                    <div class="drops-label">Drops</div>
                    <div class="drops-grid">${mobDropsHtml}</div>
                </div>

                <div class="monster-actions">
                    <button class="stop-combat-btn" onclick="window.combatUI.stopFighting()">
                        Stop Fighting
                    </button>
                </div>
            </div>
        `;
    }

    // Idle state - no monster
    return `
        <div class="monster-panel-idle">
            <div class="idle-content">
                <div class="idle-icon">👾</div>
                <div class="idle-title">No Monster</div>
                <div class="idle-message">Select a zone below to start fighting</div>
            </div>
        </div>
    `;
}

/**
 * Render mob drops in compact grid format
 */
function renderMobDropsCompact(mobId, zoneId) {
    const mobLoot = getMobLoot(mobId, zoneId);
    if (!mobLoot) return '<span class="no-drops">No drops</span>';

    const allDrops = [...(mobLoot.common || []), ...(mobLoot.rare || [])];
    if (allDrops.length === 0) return '<span class="no-drops">No drops</span>';

    // Show up to 8 drops
    const displayDrops = allDrops.slice(0, 8);

    return displayDrops.map(drop => {
        const icon = getItemIcon(drop.itemId);
        const name = getItemName(drop.itemId);
        const isRare = (mobLoot.rare || []).includes(drop);
        const chanceText = drop.chance >= 1 ? 'Always' : `1/${Math.round(1/drop.chance)}`;

        return `
            <div class="drop-item ${isRare ? 'rare' : ''}" data-tooltip="${name}\n${chanceText}">
                ${icon}
            </div>
        `;
    }).join('');
}

/**
 * Convert spell effects state to mob debuffs array format
 */
function buildMobDebuffsFromSpellEffects(spellEffects) {
    if (!spellEffects) return [];

    const debuffs = [];

    // DoT effect (fire damage)
    if (spellEffects.dot) {
        debuffs.push({
            type: 'dot',
            name: `${spellEffects.dot.damage} Fire/tick`,
            remaining: spellEffects.dot.remaining || 0
        });
    }

    // Slow effect (frost)
    if (spellEffects.slow) {
        const slowPercent = Math.round((1 - spellEffects.slow.multiplier) * 100);
        debuffs.push({
            type: 'slow',
            name: `${slowPercent}% Slowed`,
            remaining: spellEffects.slow.remaining || 0
        });
    }

    // Corrupted Prey (active corruption timer)
    if (isSkillCorrupted(SKILLS.COMBAT)) {
        const info = getSkillCorruptionInfo(SKILLS.COMBAT);
        debuffs.push({
            type: 'corruption',
            name: 'Corrupted Prey',
            remaining: info.remainingMs || 0
        });
    }

    // Corrupted Prey marks (persistent after timer expires)
    if (combatSkill.corruptedPrey?.marked && !isSkillCorrupted(SKILLS.COMBAT)) {
        debuffs.push({
            type: 'mark',
            name: `Marked: ${combatSkill.corruptedPrey.lootMultiplier}x Loot, ${combatSkill.corruptedPrey.xpMultiplier}x XP`,
            remaining: 0
        });
    }

    return debuffs;
}

/**
 * Render monster debuffs in compact format
 */
function renderMonsterDebuffsCompact(debuffs) {
    if (!debuffs || debuffs.length === 0) {
        return '';
    }

    return debuffs.map(debuff => {
        const icon = debuff.type === 'dot' ? '🔥' :
                     debuff.type === 'slow' ? '❄️' :
                     debuff.type === 'corruption' ? '💀' :
                     debuff.type === 'mark' ? '🎯' :
                     '⚡';
        const remaining = Math.ceil((debuff.remaining || 0) / 1000);
        const timeStr = debuff.type === 'mark' ? '' :
                        remaining >= 60 ? `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, '0')}` :
                        `${remaining}s`;
        return `
            <div class="buff-chip debuff" data-tooltip="${debuff.name || debuff.type}">
                <span class="buff-icon">${icon}</span>
                ${timeStr ? `<span class="buff-timer">${timeStr}</span>` : ''}
            </div>
        `;
    }).join('');
}

/**
 * Render monster buffs in compact format (for future mob abilities)
 */
function renderMonsterBuffsCompact(buffs) {
    if (!buffs || buffs.length === 0) {
        return '';
    }

    return buffs.map(buff => {
        const icon = buff.icon || '✨';
        const remaining = Math.ceil((buff.remaining || 0) / 1000);
        return `
            <div class="buff-chip buff" data-tooltip="${buff.name || 'Buff'}">
                <span class="buff-icon">${icon}</span>
                ${remaining > 0 ? `<span class="buff-timer">${remaining}s</span>` : ''}
            </div>
        `;
    }).join('');
}

/**
 * Render player debuffs in compact format (from mob abilities)
 */
function renderPlayerDebuffsCompact(state) {
    const debuffs = state.playerDebuffs || [];
    if (!debuffs || debuffs.length === 0) {
        return '';
    }

    return debuffs.map(debuff => {
        const icon = debuff.type === 'dot' ? '🔥' :
                     debuff.type === 'slow' ? '❄️' :
                     debuff.type === 'weaken' ? '💔' :
                     '💀';
        const remaining = Math.ceil((debuff.remaining || 0) / 1000);
        return `
            <div class="buff-chip debuff" data-tooltip="${debuff.name || debuff.type}">
                <span class="buff-icon">${icon}</span>
                <span class="buff-timer">${remaining}s</span>
            </div>
        `;
    }).join('');
}

/**
 * Render combat buffs for player
 */
function renderCombatBuffs(potionBuffs, spellBuffs, side) {
    let html = '';

    // Potion buffs
    for (const [category, buff] of Object.entries(potionBuffs)) {
        if (!buff || !buff.active) continue;
        const timeLeft = Math.max(0, Math.ceil((buff.combatTimeRemaining || 0) / 1000));
        const icon = getItemIcon(buff.potionId);
        html += `<span class="combat-buff potion" data-tooltip="${getItemName(buff.potionId)} (${timeLeft}s)">${icon}</span>`;
    }

    // Spell buffs (if any self-buffs)
    for (const buff of spellBuffs) {
        html += `<span class="combat-buff spell" data-tooltip="${buff.name}">${buff.icon || '✨'}</span>`;
    }

    return html || '<span class="no-buffs">—</span>';
}

/**
 * Render combat debuffs on monster
 */
function renderCombatDebuffs(debuffs) {
    if (!debuffs || debuffs.length === 0) {
        return '<span class="no-buffs">—</span>';
    }

    return debuffs.map(debuff => {
        const timeLeft = debuff.expiresAt ? Math.max(0, Math.ceil((debuff.expiresAt - Date.now()) / 1000)) : '';
        return `<span class="combat-debuff" data-tooltip="${debuff.name}${timeLeft ? ` (${timeLeft}s)` : ''}">${debuff.icon || '💀'}</span>`;
    }).join('');
}

/**
 * Render preview state in monster panel
 */
function renderMonsterPreview(state) {
    const { zone: zoneData, zoneId, isTargetFarming, mobId: targetMobId } = state.previewState;
    const mobs = zoneData?.mobs || [];

    // Get first mob for display (or target mob if target farming)
    const displayMobId = isTargetFarming ? targetMobId : mobs[0];
    const mob = getMob(displayMobId);

    // If target farming, show that mob's loot; otherwise aggregate from all mobs
    const seenItems = new Set();
    const lootItems = [];
    const mobsToCheck = isTargetFarming ? [targetMobId] : mobs;
    for (const mobId of mobsToCheck) {
        const mobLoot = getMobLoot(mobId, zoneId);
        for (const drop of [...(mobLoot?.common || []), ...(mobLoot?.rare || [])]) {
            if (!seenItems.has(drop.itemId)) {
                seenItems.add(drop.itemId);
                lootItems.push(drop);
            }
            if (lootItems.length >= 12) break;
        }
        if (lootItems.length >= 12) break;
    }

    // Build mob names list for display
    const mobNames = mobs.slice(0, 3).map(mobId => getMob(mobId)?.name || 'Unknown').join(', ');
    const mobCountText = mobs.length > 3 ? ` +${mobs.length - 3} more` : '';

    return `
        <div class="monster-panel">
            <div class="monster-hp-bar preview">
                <span class="hp-text">${zoneData?.name || 'Unknown Zone'}</span>
            </div>
            <div class="monster-display preview">
                <div class="monster-icon-large">${mob ? getMobIcon(mob) : '❓'}</div>
                <div class="monster-name">${isTargetFarming ? `Target: ${mob?.name}` : (mob?.name || 'Unknown')}</div>
                <div class="monster-subtitle">${isTargetFarming ? '' : `${mobs.length} monster${mobs.length !== 1 ? 's' : ''} in zone`}</div>
                <div class="preview-mobs">
                    ${mobs.slice(0, 6).map(mobId => {
                        const m = getMob(mobId);
                        const isTarget = mobId === targetMobId;
                        const canTarget = gameState.isZoneCleared(zoneId);
                        return `<span class="preview-mob-icon ${isTarget ? 'targeted' : ''} ${canTarget ? 'clickable' : ''}"
                                      data-tooltip="${m?.name}${canTarget ? ' (click to target)' : ''}"
                                      ${canTarget ? `onclick="window.combatUI.startFightingMob('${zoneId}', '${mobId}')"` : ''}>${getMobIcon(m)}</span>`;
                    }).join('')}
                    ${mobs.length > 6 ? `<span class="preview-more">+${mobs.length - 6}</span>` : ''}
                </div>
                <div class="preview-loot">
                    <span class="preview-loot-label">Drops:</span>
                    <div class="preview-loot-items">
                        ${lootItems.map(drop => {
                            const itemName = getItemName(drop.itemId);
                            const itemIcon = getItemIcon(drop.itemId);
                            return `<span class="preview-loot-item" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}">${itemIcon}</span>`;
                        }).join('')}
                        ${lootItems.length === 0 ? '<span class="no-loot">No drops</span>' : ''}
                    </div>
                </div>
                <div class="preview-actions">
                    <button class="start-btn" onclick="window.combatUI.startFromPreview()">
                        ⚔️ Start Fighting
                    </button>
                    <button class="cancel-btn" onclick="window.combatUI.clearPreview()">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Render full-width loot section below combat columns
 */
function renderLootSection(lootChest, lootCount) {
    const maxSlots = gameState.getCombatLootCapacity();
    // Convert object {itemId: quantity} to array format for display
    const items = Object.entries(lootChest).map(([itemId, quantity]) => ({ itemId, quantity }));

    // Show empty slots up to capacity
    const emptySlots = Math.max(0, maxSlots - items.length);

    return `
        <div class="combat-loot-section">
            <div class="loot-header">
                <span class="loot-label">🎒 Loot</span>
                <span class="loot-count${lootCount >= maxSlots ? ' loot-full' : ''}">${lootCount}/${maxSlots}${lootCount >= maxSlots ? ' ⚠️ Full' : ''}</span>
                ${lootCount > 0 ? `
                    <button class="collect-all-btn" onclick="window.combatUI.collectAllLoot()">
                        Collect All
                    </button>
                ` : ''}
            </div>
            <div class="loot-grid">
                ${items.map(slot => `
                    <div class="loot-slot filled" onclick="window.combatUI.collectLoot('${slot.itemId}')" data-tooltip="${getItemName(slot.itemId)} ×${formatWithCommas(slot.quantity)}<br>Click to collect">
                        ${getItemIcon(slot.itemId, slot.quantity)}
                        <span class="loot-qty">${slot.quantity > 1 ? formatNumber(slot.quantity) : ''}</span>
                    </div>
                `).join('')}
                ${Array(emptySlots).fill('<div class="loot-slot empty"></div>').join('')}
            </div>
        </div>
    `;
}

/**
 * Get all foods from inventory
 */
function getFoodsFromInventory() {
    const slots = gameState.getInventorySlots();
    const foods = [];

    for (const slot of slots) {
        if (!slot) continue;
        const item = getItem(slot.itemId);
        if (item && item.healAmount) {
            foods.push({
                itemId: slot.itemId,
                name: item.name,
                icon: getItemIcon(slot.itemId),
                healAmount: item.healAmount,
                quantity: slot.quantity
            });
        }
    }

    return foods;
}

/**
 * Render food selection dropdown
 */
function renderFoodSelectionDropdown() {
    if (!showFoodSelection) return '';

    const autoEat = gameState.getAutoEat();
    const storedFood = autoEat.storedFood;
    const foods = getFoodsFromInventory();

    // Currently equipped food section
    let equippedHtml = '';
    if (storedFood && storedFood.quantity > 0) {
        const foodItem = getItem(storedFood.itemId);
        const healAmount = foodItem?.healAmount || 0;
        equippedHtml = `
            <div class="food-selection-section">
                <div class="food-section-label">Currently Equipped</div>
                <div class="food-option equipped">
                    <span class="food-option-icon">${getItemIcon(storedFood.itemId)}</span>
                    <span class="food-option-name">${getItemName(storedFood.itemId)}</span>
                    <span class="food-option-heal">+${healAmount} HP</span>
                    <span class="food-option-qty">×${storedFood.quantity}</span>
                    <button class="food-remove-btn" onclick="event.stopPropagation(); window.combatUI.returnFood()" data-tooltip="Return to inventory">×</button>
                </div>
            </div>
        `;
    }

    // Threshold slider section
    const thresholdHtml = `
        <div class="food-selection-section threshold-section">
            <div class="food-section-label">Auto-Eat Threshold</div>
            <div class="threshold-slider-row">
                <input type="range" class="threshold-slider"
                       min="${COMBAT_CONFIG.AUTO_EAT_MIN_THRESHOLD}"
                       max="${COMBAT_CONFIG.AUTO_EAT_MAX_THRESHOLD}"
                       value="${autoEat.threshold}"
                       oninput="window.combatUI.setThreshold(this.value); this.nextElementSibling.textContent = this.value + '%'">
                <span class="threshold-value">${autoEat.threshold}%</span>
            </div>
            <div class="threshold-hint">Eat when HP falls below this %</div>
        </div>
    `;

    // Inventory food section
    let inventoryHtml = '';
    if (foods.length === 0) {
        inventoryHtml = `<div class="food-selection-empty">No food in inventory</div>`;
    } else {
        const foodOptionsHtml = foods.map(food => `
            <div class="food-option" onclick="window.combatUI.selectFood('${food.itemId}')">
                <span class="food-option-icon">${food.icon}</span>
                <span class="food-option-name">${food.name}</span>
                <span class="food-option-heal">+${food.healAmount} HP</span>
                <span class="food-option-qty">×${food.quantity}</span>
            </div>
        `).join('');
        inventoryHtml = `
            <div class="food-selection-section">
                <div class="food-section-label">Inventory</div>
                <div class="food-selection-list">
                    ${foodOptionsHtml}
                </div>
            </div>
        `;
    }

    return `
        <div class="food-selection-backdrop" onclick="window.combatUI.closeFoodSelection()"></div>
        <div class="food-selection-dropdown">
            <div class="food-selection-header">
                <span>🍖 Auto-Eat Food</span>
                <button class="food-selection-close" onclick="window.combatUI.closeFoodSelection()">×</button>
            </div>
            ${equippedHtml}
            ${thresholdHtml}
            ${inventoryHtml}
        </div>
    `;
}

/**
 * Get potions from inventory for a specific effect category
 */
function getPotionsForCategory(category) {
    const slots = gameState.getInventorySlots();
    const potions = [];

    for (const slot of slots) {
        if (!slot) continue;
        const item = getItem(slot.itemId);
        if (item && item.effects && item.effects.category === category) {
            potions.push({
                itemId: slot.itemId,
                name: item.name,
                icon: getItemIcon(slot.itemId),
                color: item.color,
                effects: item.effects,
                quantity: slot.quantity
            });
        }
    }

    return potions;
}

/**
 * Format potion effect for display
 */
function formatPotionEffect(effects, useColors = true, showDuration = true) {
    const parts = [];

    // When useColors=false, return plain text (safe for data-tooltip attributes)
    const wrap = (text, color) => useColors ? `<span style="color:${color}">${text}</span>` : text;
    const offColor = '#4ade80'; // Green
    const defColor = '#60a5fa'; // Blue

    if (effects.attackBonus) parts.push(wrap(`+${effects.attackBonus} Atk`, offColor));
    if (effects.defenseBonus) parts.push(wrap(`+${effects.defenseBonus} Def`, defColor));
    if (effects.accuracyBonus) parts.push(wrap(`+${effects.accuracyBonus}% Acc`, offColor));
    if (effects.speedBonus) parts.push(wrap(`+${effects.speedBonus}% Spd`, offColor));
    if (effects.critBonus) parts.push(wrap(`+${effects.critBonus}% Crit`, offColor));
    if (effects.critDamageBonus) parts.push(wrap(`+${effects.critDamageBonus}% CritDmg`, offColor));
    if (effects.damageReduction) parts.push(wrap(`-${effects.damageReduction}% DmgTaken`, defColor));
    if (effects.enemyAccuracyReduction) parts.push(wrap(`-${effects.enemyAccuracyReduction}% EnemyAcc`, defColor));

    // Check if all 3 damage bonuses are the same (Ascendant Concoction)
    const hasMelee = effects.meleeDamageBonus;
    const hasRanged = effects.rangedDamageBonus;
    const hasMagic = effects.magicDamageBonus;
    if (hasMelee && hasRanged && hasMagic && hasMelee === hasRanged && hasRanged === hasMagic) {
        parts.push(wrap(`+${hasMelee}% AllDmg`, offColor));
    } else {
        if (hasMelee) parts.push(wrap(`+${hasMelee}% MeleeDmg`, offColor));
        if (hasMagic) parts.push(wrap(`+${hasMagic}% MagicDmg`, offColor));
        if (hasRanged) parts.push(wrap(`+${hasRanged}% RngDmg`, offColor));
    }

    if (effects.glyphSaveChance) parts.push(`${effects.glyphSaveChance}% Glyph Save`);
    if (effects.ammoSaveChance) parts.push(`${effects.ammoSaveChance}% Ammo Save`);
    if (effects.corruptionBonus) parts.push(`+${effects.corruptionBonus}% Corruption Proc`);

    // Add duration (for selection/belt UI, not active buff tooltips)
    if (showDuration && effects.duration) {
        const durMin = Math.floor(effects.duration / 60000);
        const durSec = Math.floor((effects.duration % 60000) / 1000);
        const durText = durMin > 0 ? (durSec > 0 ? `${durMin}m ${durSec}s` : `${durMin}m`) : `${durSec}s`;
        parts.push(wrap(durText, '#a78bfa'));
    }

    return parts.join(', ');
}

/**
 * Render potion selection dropdown for a specific category
 */
function renderPotionSelectionDropdown(category) {
    if (!showPotionSelection || selectedPotionCategory !== category) return '';

    const potions = getPotionsForCategory(category);
    const categoryNames = {
        attack: 'Attack', defense: 'Defense', accuracy: 'Accuracy', speed: 'Speed', hybrid: 'Hybrid',
        crit: 'Crit', reduction: 'Damage Reduction', evasion: 'Evasion', melee: 'Melee', magic: 'Magic', ranged: 'Ranged', all: 'All Stats'
    };
    const categoryName = categoryNames[category] || category;

    if (potions.length === 0) {
        return `
            <div class="potion-selection-backdrop" onclick="window.combatUI.closePotionSelection()"></div>
            <div class="potion-selection-dropdown">
                <div class="potion-selection-header">
                    <span>${categoryName} Potions</span>
                    <button class="potion-selection-close" onclick="window.combatUI.closePotionSelection()">×</button>
                </div>
                <div class="potion-selection-empty">No ${categoryName.toLowerCase()} potions in inventory</div>
            </div>
        `;
    }

    const potionOptionsHtml = potions.map(potion => `
        <div class="potion-option" onclick="window.combatUI.selectPotion('${category}', '${potion.itemId}')">
            <span class="potion-option-icon">${potion.icon}</span>
            <div class="potion-option-info">
                <span class="potion-option-name">${potion.name}</span>
                <span class="potion-option-effect">${formatPotionEffect(potion.effects)}</span>
            </div>
            <span class="potion-option-qty">×${potion.quantity}</span>
        </div>
    `).join('');

    return `
        <div class="potion-selection-backdrop" onclick="window.combatUI.closePotionSelection()"></div>
        <div class="potion-selection-dropdown">
            <div class="potion-selection-header">
                <span>${categoryName} Potions</span>
                <button class="potion-selection-close" onclick="window.combatUI.closePotionSelection()">×</button>
            </div>
            <div class="potion-selection-list">
                ${potionOptionsHtml}
            </div>
        </div>
    `;
}

/**
 * Render auto-eat panel
 */
function renderAutoEatPanel() {
    const autoEat = gameState.getAutoEat();
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const unlockLevel = COMBAT_CONFIG.AUTO_EAT_UNLOCK_LEVEL;

    // Check if auto-eat should be unlocked
    gameState.checkAutoEatUnlock();

    // Not unlocked yet - show unlock requirement
    if (!autoEat.unlocked) {
        const hasCombatLevel = skill.level >= unlockLevel;
        const hasMessHall = gameState.hasMilestone('mess_hall');

        let lockText = '🔒 Requires: ';
        const requirements = [];
        if (!hasCombatLevel) requirements.push(`Combat Lv ${unlockLevel}`);
        if (!hasMessHall) requirements.push('Mess Hall (Architecting)');
        lockText += requirements.join(' + ');

        return `
            <div class="auto-consumables-panel locked">
                <div class="auto-consumables-header">
                    <span class="auto-consumables-title">⚗️ Auto-Consumables</span>
                    <span class="auto-consumables-lock">${lockText}</span>
                </div>
            </div>
        `;
    }

    // Build stored food display
    let storedFoodHtml = '';
    if (autoEat.storedFood) {
        const food = getItemIcon(autoEat.storedFood.itemId);
        const foodName = getItemName(autoEat.storedFood.itemId);
        storedFoodHtml = `
            <div class="auto-eat-food" onclick="window.combatUI.toggleFoodSelection()" data-tooltip="${foodName} (Click to change)">
                <span class="food-icon">${food}</span>
                <span class="food-qty">×${autoEat.storedFood.quantity}</span>
            </div>
        `;
    } else {
        storedFoodHtml = `
            <div class="auto-eat-food empty" onclick="window.combatUI.toggleFoodSelection()" data-tooltip="Click to select food">
                <span class="food-icon">➕</span>
                <span class="food-qty">Empty</span>
            </div>
        `;
    }

    // Build potion belt section
    const potionBeltHtml = renderPotionBeltSection();

    return `
        <div class="auto-consumables-panel">
            <div class="auto-consumables-header">
                <span class="auto-consumables-title">⚗️ Auto-Consumables</span>
            </div>
            <div class="auto-consumables-grid">
                <div class="auto-food-column">
                    <div class="column-header">
                        <span class="column-title">🍖 Food</span>
                    </div>
                    <div class="auto-eat-food-container">
                        ${storedFoodHtml}
                        ${renderFoodSelectionDropdown()}
                    </div>
                    <div class="auto-eat-threshold">
                        <span class="threshold-label">Eat at:</span>
                        <input type="range" class="threshold-slider"
                               min="${COMBAT_CONFIG.AUTO_EAT_MIN_THRESHOLD}"
                               max="${COMBAT_CONFIG.AUTO_EAT_MAX_THRESHOLD}"
                               value="${autoEat.threshold}"
                               onchange="window.combatUI.setThreshold(this.value)">
                        <span class="threshold-value">${autoEat.threshold}%</span>
                    </div>
                </div>
                <div class="auto-consumables-divider"></div>
                ${potionBeltHtml}
            </div>
        </div>
    `;
}

/**
 * Render potion belt section (inside auto-consumables panel)
 */
function renderPotionBeltSection() {
    const beltUnlocked = gameState.isApothecaryBeltUnlocked();

    if (!beltUnlocked) {
        return `
            <div class="auto-potion-column locked">
                <div class="column-header">
                    <span class="column-title">🧪 Potions</span>
                </div>
                <div class="potion-belt-lock">🔒 Apothecary Station (Architecting Lv 50)</div>
            </div>
        `;
    }

    const belt = gameState.getApothecaryBelt();
    const slots = gameState.getApothecarySlots();

    // Build 4-slot display for current equipment set
    let slotsHtml = '';
    for (let i = 0; i < 4; i++) {
        const slot = slots[i];

        if (slot && slot.quantity > 0) {
            const potionName = getItemName(slot.itemId);
            const potionIcon = getItemIcon(slot.itemId);
            slotsHtml += `
                <div class="potion-belt-slot-container">
                    <div class="potion-belt-slot filled"
                         data-slot="${i}"
                         data-tooltip="${potionName} (×${slot.quantity})"
                         onclick="window.combatUI.openPotionBelt(${i})">
                        <span class="slot-potion-icon">${potionIcon}</span>
                        <span class="slot-qty">×${slot.quantity}</span>
                    </div>
                </div>
            `;
        } else {
            slotsHtml += `
                <div class="potion-belt-slot-container">
                    <div class="potion-belt-slot empty"
                         data-slot="${i}"
                         data-tooltip="Slot ${i + 1} - Click to select"
                         onclick="window.combatUI.openPotionBelt(${i})">
                        <span class="slot-icon">🧪</span>
                    </div>
                </div>
            `;
        }
    }

    return `
        <div class="auto-potion-column ${belt.enabled ? 'enabled' : 'disabled'}">
            <div class="column-header">
                <span class="column-title">🧪 Potions</span>
                <button class="column-toggle ${belt.enabled ? 'on' : 'off'}"
                        onclick="window.combatUI.toggleApothecaryBelt()">
                    ${belt.enabled ? 'ON' : 'OFF'}
                </button>
            </div>
            <div class="potion-belt-slots">
                ${slotsHtml}
            </div>
        </div>
    `;
}

/**
 * Render spell selection panel (only for magic weapon users)
 */
function renderSpellPanel() {
    const state = combatSkill.getCombatState();
    const stats = state.playerStats;

    // Only show for magic attack style
    if (stats.attackStyle !== 'magic') {
        return '';
    }

    const combatLevel = combatSkill.getLevel();
    const availableSpells = getAvailableSpells(combatLevel);
    const allSpells = getAllSpells();
    const activeSpell = state.activeSpell;

    // Group spells by element
    const elements = Object.values(SPELL_ELEMENTS);
    const spellsByElement = {};
    for (const element of elements) {
        spellsByElement[element] = allSpells.filter(s => s.element === element);
    }

    // Get element display info
    const elementInfo = {
        fire: { name: 'Fire', icon: '🔥', color: '#FF4500' },
        ice: { name: 'Ice', icon: '❄️', color: '#87CEEB' },
        earth: { name: 'Earth', icon: '🪨', color: '#8B4513' },
        air: { name: 'Air', icon: '💨', color: '#9370DB' }
    };

    // Build spell grid
    let spellGridHtml = '';
    for (const element of elements) {
        const spells = spellsByElement[element];
        const info = elementInfo[element];

        const spellButtonsHtml = spells.map(spell => {
            const isLocked = combatLevel < spell.level;
            const isSelected = activeSpell && activeSpell.id === spell.id;

            // Check glyph availability and build cost display
            let hasGlyphs = true;
            let glyphCostHtml = '';
            let glyphCostText = [];
            for (const cost of spell.glyphCost) {
                const totalCount = gameState.getTotalGlyphCount(cost.glyphId);
                const hasCost = totalCount >= cost.quantity;
                if (!hasCost) hasGlyphs = false;
                const glyphName = getItemName(cost.glyphId) || cost.glyphId;
                const glyphIcon = getItemIcon(cost.glyphId);
                glyphCostHtml += `<span class="glyph-cost ${hasCost ? '' : 'missing'}" data-tooltip="${glyphName}: ${totalCount}/${cost.quantity}">${glyphIcon}${cost.quantity}</span>`;
                glyphCostText.push(`${glyphName} x${cost.quantity}`);
            }

            const canCast = !isLocked && hasGlyphs;

            return `
                <button class="spell-btn ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''} ${!hasGlyphs && !isLocked ? 'no-glyphs' : ''}"
                        onclick="window.combatUI.selectSpell('${spell.id}')"
                        data-tooltip="${spell.name}${isLocked ? ' (Lv ' + spell.level + ')' : ''}<br>${spell.description}<br>Dmg: ${spell.baseDamage}<br>${formatSpellEffect(spell.effect)}<br>Cost: ${glyphCostText.join(', ')}"
                        ${isLocked ? 'disabled' : ''}>
                    <span class="spell-icon">${spell.icon}</span>
                    <span class="spell-name">${spell.name}</span>
                    <span class="spell-tier">T${spell.tier}</span>
                    <div class="spell-cost">${glyphCostHtml}</div>
                    ${isLocked ? `<span class="spell-lock">🔒${spell.level}</span>` : ''}
                </button>
            `;
        }).join('');

        spellGridHtml += `
            <div class="spell-element-group" data-element="${element}">
                <div class="element-header" style="color: ${info.color}">
                    <span class="element-icon">${info.icon}</span>
                    <span class="element-name">${info.name}</span>
                </div>
                <div class="spell-buttons">
                    ${spellButtonsHtml}
                </div>
            </div>
        `;
    }

    // Warning if no spell selected or out of glyphs
    // Note: canCastSpell.success is true if using built-in spell (no warning needed)
    let warningHtml = '';
    if (!activeSpell && !state.canCastSpell.useBuiltIn) {
        warningHtml = '<div class="spell-warning">Select a spell to attack</div>';
    } else if (!state.canCastSpell.success) {
        warningHtml = `<div class="spell-warning glyph-warning">${state.canCastSpell.reason}</div>`;
    } else if (state.canCastSpell.useBuiltIn) {
        // Show built-in spell info when using it as fallback
        const builtInSpell = combatSkill.getBuiltInSpell();
        if (builtInSpell) {
            warningHtml = `<div class="spell-info built-in-spell">Using: ${builtInSpell.name}</div>`;
        }
    }

    return `
        <div class="spell-panel">
            <div class="spell-panel-header">
                <span class="spell-panel-title">🪄 Spells</span>
                ${activeSpell ? `<span class="active-spell-display">${activeSpell.icon} ${activeSpell.name}</span>` : ''}
            </div>
            ${warningHtml}
            <div class="spell-grid">
                ${spellGridHtml}
            </div>
        </div>
    `;
}

/**
 * Get potion buff icon and description
 */
function getPotionBuffDisplay(buff) {
    const category = buff.category;
    const effects = buff.effects;

    // Icon and tooltip based on category
    const displays = {
        attack: { icon: '⚔️', color: '#dc143c' },
        defense: { icon: '🛡️', color: '#4682b4' },
        accuracy: { icon: '🎯', color: '#32cd32' },
        speed: { icon: '⚡', color: '#ffd700' },
        hybrid: { icon: '⚖️', color: '#9370db' },
        crit: { icon: '💥', color: '#ff4500' },
        reduction: { icon: '🧱', color: '#8b4513' },
        evasion: { icon: '💨', color: '#87ceeb' },
        melee: { icon: '🗡️', color: '#ff1493' },
        magic: { icon: '✨', color: '#9932cc' },
        ranged: { icon: '🏹', color: '#228b22' },
        all: { icon: '🌟', color: '#ffa500' }
    };

    const display = displays[category] || { icon: '🧪', color: '#808080' };

    // Build value text based on effects
    let valueText = '';
    if (effects.attackBonus && !effects.meleeDamageBonus) valueText = `+${buff.currentAttackBonus ?? effects.attackBonus} Atk`;
    else if (effects.meleeDamageBonus) valueText = `+${effects.meleeDamageBonus}% Melee`;
    else if (effects.defenseBonus) valueText = `+${effects.defenseBonus} Def`;
    else if (effects.accuracyBonus) valueText = `+${effects.accuracyBonus}% Acc`;
    else if (effects.speedBonus) valueText = `+${effects.speedBonus}% Spd`;
    else if (effects.critBonus) valueText = `+${effects.critBonus}% Crit`;
    else if (effects.damageReduction) valueText = `-${effects.damageReduction}% Dmg`;
    else if (effects.enemyAccuracyReduction) valueText = `-${effects.enemyAccuracyReduction}% Eva`;
    else if (effects.magicDamageBonus) valueText = `+${effects.magicDamageBonus}% Mag`;
    else if (effects.rangedDamageBonus) valueText = `+${effects.rangedDamageBonus}% Rng`;
    else if (category === 'all') valueText = formatPotionEffect(effects, false, false);

    return { ...display, valueText };
}

/**
 * Render spell buffs content (defense, crit, hot from spells)
 * Separate from potion buffs to prevent re-render cascades
 */
function renderSpellBuffsContent() {
    const state = combatSkill.getCombatState();
    const effects = state.spellEffects;

    let buffsHtml = '';

    if (effects) {
        if (effects.defense) {
            const remainingSec = Math.ceil(effects.defense.remaining / 1000);
            buffsHtml += `
                <div class="buff-indicator defense" data-tooltip="Stone Skin: +${effects.defense.bonus} Defense">
                    <span class="buff-icon">🪨</span>
                    <span class="buff-timer">${remainingSec}s</span>
                </div>
            `;
        }
        if (effects.crit) {
            const remainingSec = Math.ceil(effects.crit.remaining / 1000);
            buffsHtml += `
                <div class="buff-indicator crit" data-tooltip="Wind Focus: +${effects.crit.bonus}% Crit">
                    <span class="buff-icon">⚡</span>
                    <span class="buff-timer">${remainingSec}s</span>
                </div>
            `;
        }
        if (effects.hot) {
            const remainingSec = Math.ceil(effects.hot.remaining / 1000);
            buffsHtml += `
                <div class="buff-indicator hot" data-tooltip="Empowered Heal: +${effects.hot.amount} HP/s">
                    <span class="buff-icon">💚</span>
                    <span class="buff-timer">${remainingSec}s</span>
                </div>
            `;
        }
    }

    return buffsHtml;
}

/**
 * Render potion buffs content (from drinking potions)
 * Separate from spell buffs to prevent re-render cascades
 */
function renderPotionBuffsContent() {
    const potionBuffs = combatSkill.getPotionBuffsState();
    let buffsHtml = '';

    for (const buff of potionBuffs) {
        const remainingSec = Math.ceil(buff.combatTimeRemaining / 1000);
        const display = getPotionBuffDisplay(buff);
        buffsHtml += `
            <div class="buff-indicator potion ${buff.category}"
                 data-tooltip="${buff.potionName}: ${display.valueText}"
                 style="border-color: ${display.color}">
                <span class="buff-icon">${display.icon}</span>
                <span class="buff-timer">${remainingSec}s</span>
            </div>
        `;
    }

    return buffsHtml;
}

/**
 * Render set bonus buff indicators (Ashforged Momentum, Afterimage, Phantom Archer, Shatter, Fusions)
 */
function renderSetBonusBuffsContent() {
    const state = combatSkill.getCombatState();
    const sb = state.setBonus;
    if (!sb || Object.keys(sb.activeBonuses).length === 0) return '';

    let html = '';

    // Momentum stacks (Ashforged 2pc)
    if (sb.activeBonuses.ashforged && sb.momentum > 0) {
        const pct = sb.momentum * 2;
        html += `
            <div class="buff-indicator set-bonus momentum" data-tooltip="Momentum: +${pct}% Damage (${sb.momentum}/10)">
                <span class="buff-icon">🔺</span>
                <span class="buff-stacks">${sb.momentum}</span>
            </div>
        `;
    }

    // Afterimage active (Ashforged 6pc, 2H)
    if (sb.afterimageActive) {
        html += `
            <div class="buff-indicator set-bonus afterimage" data-tooltip="Afterimage: Clone attacks at 50% damage">
                <span class="buff-icon">👤</span>
            </div>
        `;
    }

    // Phantom Archer active (Cindermark 6pc)
    if (sb.phantomArcherActive) {
        html += `
            <div class="buff-indicator set-bonus phantom" data-tooltip="Phantom Archer: Spectral shots firing">
                <span class="buff-icon">👻</span>
            </div>
        `;
    }

    // Shatter Ready (Pyreweave 6pc fire+ice)
    if (sb.shatterReady) {
        html += `
            <div class="buff-indicator set-bonus shatter" data-tooltip="Shatter: Next hit deals 2x damage">
                <span class="buff-icon">💎</span>
            </div>
        `;
    }

    // Active fusions (Pyreweave 6pc)
    for (const name of sb.activeFusions) {
        html += `
            <div class="buff-indicator set-bonus fusion" data-tooltip="${name} Fusion">
                <span class="buff-icon">✦</span>
                <span class="buff-label">${name}</span>
            </div>
        `;
    }

    return html;
}

/**
 * Render player buffs content (inner HTML only) - combines spell and potion buffs
 */
function renderPlayerBuffsContent() {
    return renderSpellBuffsContent() + renderPotionBuffsContent();
}

/**
 * Render player buffs (defense, crit) - shown under player
 * Always renders wrapper for layout stability
 */
function renderPlayerBuffs() {
    return `<div class="buffs-bar player-buffs">${renderPlayerBuffsContent()}</div>`;
}

/**
 * Render player effects column (left side of combat arena)
 * Contains: player buffs (top), player debuffs (bottom - future)
 * Spell buffs and potion buffs are in separate containers to prevent re-render cascades
 */
function renderPlayerEffectsColumn() {
    return `
        <div class="effects-column player-effects">
            <div class="effects-section buffs-section">
                <div class="effects-label">Your Buffs</div>
                <div class="effects-list player-buffs">
                    <div class="spell-buffs">${renderSpellBuffsContent()}</div>
                    <div class="potion-buffs">${renderPotionBuffsContent()}</div>
                    <div class="set-buffs">${renderSetBonusBuffsContent()}</div>
                </div>
            </div>
            <div class="effects-section debuffs-section">
                <div class="effects-label">Your Debuffs</div>
                <div class="effects-list player-debuffs"></div>
            </div>
        </div>
    `;
}

/**
 * Render mob effects column (right side of combat arena)
 * Contains: mob buffs (top - future), mob debuffs (bottom)
 */
function renderMobEffectsColumn() {
    return `
        <div class="effects-column mob-effects">
            <div class="effects-section buffs-section">
                <div class="effects-label">Mob Buffs</div>
                <div class="effects-list mob-buffs"></div>
            </div>
            <div class="effects-section debuffs-section">
                <div class="effects-label">Mob Debuffs</div>
                <div class="effects-list mob-debuffs">${renderMobDebuffsContent()}</div>
            </div>
        </div>
    `;
}

/**
 * Render mob debuffs content (inner HTML only) - compact: icon + timer
 */
function renderMobDebuffsContent() {
    const state = combatSkill.getCombatState();
    const effects = state.spellEffects;

    if (!effects) return '';

    let debuffsHtml = '';

    if (effects.dot) {
        const remainingSec = Math.ceil(effects.dot.remaining / 1000);
        debuffsHtml += `
            <div class="debuff-indicator dot" data-tooltip="Burning: ${effects.dot.damage} dmg/tick">
                <span class="debuff-icon">🔥</span>
                <span class="debuff-timer">${remainingSec}s</span>
            </div>
        `;
    }
    if (effects.slow) {
        const slowPercent = Math.round((1 - effects.slow.multiplier) * 100);
        const remainingSec = Math.ceil(effects.slow.remaining / 1000);
        debuffsHtml += `
            <div class="debuff-indicator slow" data-tooltip="Frozen: -${slowPercent}% Attack Speed">
                <span class="debuff-icon">❄️</span>
                <span class="debuff-timer">${remainingSec}s</span>
            </div>
        `;
    }

    // Corrupted Prey debuff (temporary - has timer)
    if (isSkillCorrupted(SKILLS.COMBAT)) {
        const info = getSkillCorruptionInfo(SKILLS.COMBAT);
        const remaining = Math.ceil(info.remainingMs / 1000);
        const timeStr = remaining >= 60
            ? `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, '0')}`
            : `${remaining}s`;
        debuffsHtml += `
            <div class="debuff-indicator corruption" data-tooltip="Corrupted Prey: +30% Dmg Taken, -25% Defense, 25% Glyph Save">
                <span class="debuff-icon">💀</span>
                <span class="debuff-timer">${timeStr}</span>
            </div>
        `;
    }

    // Persistent mark indicator (shows even after timer expires)
    if (combatSkill.corruptedPrey?.marked && !isSkillCorrupted(SKILLS.COMBAT)) {
        debuffsHtml += `
            <div class="debuff-indicator mark" data-tooltip="Marked: ${combatSkill.corruptedPrey.lootMultiplier}x Loot, ${combatSkill.corruptedPrey.xpMultiplier}x XP">
                <span class="debuff-icon">🎯</span>
            </div>
        `;
    }

    // Set bonus fusion debuffs on mob (Pyreweave 6pc)
    const sb = state.setBonus;
    if (sb && sb.activeFusions.length > 0) {
        for (const name of sb.activeFusions) {
            const fusionInfo = getFusionDebuffInfo(name);
            if (fusionInfo) {
                debuffsHtml += `
                    <div class="debuff-indicator fusion" data-tooltip="${fusionInfo.tooltip}">
                        <span class="debuff-icon">${fusionInfo.icon}</span>
                    </div>
                `;
            }
        }
    }

    return debuffsHtml;
}

/**
 * Get display info for spell fusion debuffs on mob
 */
function getFusionDebuffInfo(fusionName) {
    const info = {
        'Shatter':    { icon: '💎', tooltip: 'Shatter: Target brittle — next hit 2x damage' },
        'Magma':      { icon: '🌋', tooltip: 'Magma: -15% Defense' },
        'Firestorm':  { icon: '🌪️', tooltip: 'Firestorm: DoT ticks faster, +30% damage' },
        'Permafrost': { icon: '🧊', tooltip: 'Permafrost: -25% Attack Speed' },
        'Hailstorm':  { icon: '🌨️', tooltip: 'Hailstorm: +8% Damage Taken' },
        'Harmony':    { icon: '☯️', tooltip: 'Harmony: Buffs 40% stronger, auto-refresh' }
    };
    return info[fusionName] || null;
}

/**
 * Render mob debuffs (DoT, slow) - shown under mob
 * Always renders wrapper for layout stability
 */
function renderMobDebuffs() {
    return `<div class="debuffs-bar mob-debuffs">${renderMobDebuffsContent()}</div>`;
}

/**
 * Render spell effects indicators (shown in combat) - legacy, kept for compatibility
 */
function renderSpellEffectsIndicators() {
    // Now handled by renderPlayerBuffs() and renderMobDebuffs()
    return '';
}

/**
 * Get slot icon for equipment display
 */
function getSlotIcon(slot) {
    const icons = {
        head: '🪖',
        neck: '📿',
        shoulders: '🎽',
        weapon: '⚔️',
        body: '🛡️',
        offhand: '🔰',
        hands: '🧤',
        legs: '👖',
        ring: '💍',
        feet: '👢',
        ammo: '🏹'
    };
    return icons[slot] || '❓';
}

/**
 * Render mini equipment slot with stat tooltip
 */
function renderMiniSlot(slot, itemId) {
    if (!itemId) {
        return `<span class="empty-slot">${getSlotIcon(slot)}</span>`;
    }
    const icon = getItemIcon(itemId);
    const item = getItem(itemId);
    const name = item ? item.name : itemId;
    const tierColor = item ? getTierColor(item.combatLevel) : TIER_COLORS.tier1;

    // Build stat tooltip with tier-colored name
    let statsText = '';
    if (item) {
        const stats = [];
        // For magic weapons, show spell damage instead of attack
        if (item.magicDamage) {
            stats.push(`${item.magicDamage.toFixed(1)}x Spell Damage`);
        } else if (item.attackBonus) {
            stats.push(`+${item.attackBonus} Attack`);
        }
        if (item.defenseBonus) stats.push(`+${item.defenseBonus} Defense`);
        if (item.spellDamage) stats.push(`+${(item.spellDamage * 100).toFixed(0)}% Spell Damage`);
        if (item.meleeAccuracy) stats.push(`+${item.meleeAccuracy} Melee Acc`);
        if (item.rangedAccuracy) stats.push(`+${item.rangedAccuracy} Ranged Acc`);
        if (item.magicAccuracy) stats.push(`+${item.magicAccuracy} Magic Acc`);
        if (item.attackSpeed) stats.push(`${(item.attackSpeed / 1000).toFixed(1)}s Speed`);
        if (item.attackStyle) stats.push(`Style: ${item.attackStyle}`);
        statsText = stats.length > 0 ? `\n\n${stats.join('\n')}` : '';
    }
    const coloredName = `<span style='color: ${tierColor}; font-weight: bold;'>${name}</span>`;

    // Ammo slot shows quantity
    if (slot === 'ammo') {
        const ammo = gameState.getEquippedAmmo();
        const qtyDisplay = ammo.quantity >= 1000 ? formatNumber(ammo.quantity) : ammo.quantity;
        return `<span class="equipped-item ammo-slot" data-item-id="${itemId}" data-tooltip="${coloredName}\nQty: ${formatWithCommas(ammo.quantity)}${statsText}">${icon}<span class="ammo-qty">${qtyDisplay}</span></span>`;
    }

    return `<span class="equipped-item" data-item-id="${itemId}" data-tooltip="${coloredName}${statsText}">${icon}</span>`;
}

/**
 * Render mini glyph pouch for combat equipment panel (magic set only)
 */
function renderMiniGlyphPouch() {
    const pouch = gameState.getGlyphPouch();
    const isUnlocked = gameState.isGlyphPouchUnlocked();

    if (!isUnlocked) {
        return `
            <div class="mini-glyph-pouch locked" data-tooltip="Glyph Pouch (Locked)">
                <span class="pouch-label">🔒 Glyph Pouch</span>
            </div>
        `;
    }

    // Build mini slots with colored circles
    let slotsHtml = '';

    for (const slot of pouch.slots) {
        if (slot.itemId && slot.quantity > 0) {
            const icon = getItemIcon(slot.itemId);
            const qtyDisplay = slot.quantity >= 1000 ? formatNumber(slot.quantity) : slot.quantity;
            slotsHtml += `<span class="mini-glyph-slot filled"><span class="mini-glyph-icon">${icon}</span><span class="mini-qty">${qtyDisplay}</span></span>`;
        } else {
            slotsHtml += `<span class="mini-glyph-slot empty">◇</span>`;
        }
    }

    return `
        <div class="mini-glyph-pouch clickable" onclick="window.combatUI.openGlyphPouchModal()" data-tooltip="Click to manage glyphs">
            <span class="pouch-label">🎒 Glyphs</span>
            <div class="mini-glyph-slots">
                ${slotsHtml}
            </div>
        </div>
    `;
}

/**
 * Render equipment panel with set switching
 */
function renderEquipmentPanel() {
    const gear = gameState.getEquippedGear();
    const sets = gameState.getEquipmentSets();
    const activeSet = gameState.getActiveEquipmentSet();
    const stats = combatSkill.getCombatState().playerStats;

    return `
        <div class="combat-equipment-panel">
            <div class="equipment-panel-content">
                <div class="equipment-mini-grid">
                    <div class="equip-slot head ${gear.head ? 'clickable' : ''}" data-slot="head" style="grid-row:1;grid-column:2" ${gear.head ? "onclick=\"window.combatUI.unequipItem('head')\"" : ''}>${renderMiniSlot('head', gear.head)}</div>
                    <div class="equip-slot neck ${gear.neck ? 'clickable' : ''}" data-slot="neck" style="grid-row:2;grid-column:2" ${gear.neck ? "onclick=\"window.combatUI.unequipItem('neck')\"" : ''}>${renderMiniSlot('neck', gear.neck)}</div>
                    <div class="equip-slot shoulders ${gear.shoulders ? 'clickable' : ''}" data-slot="shoulders" style="grid-row:2;grid-column:3" ${gear.shoulders ? "onclick=\"window.combatUI.unequipItem('shoulders')\"" : ''}>${renderMiniSlot('shoulders', gear.shoulders)}</div>
                    <div class="equip-slot weapon ${gear.weapon ? 'clickable' : ''}" data-slot="weapon" style="grid-row:3;grid-column:1" ${gear.weapon ? "onclick=\"window.combatUI.unequipItem('weapon')\"" : ''}>${renderMiniSlot('weapon', gear.weapon)}</div>
                    <div class="equip-slot body ${gear.body ? 'clickable' : ''}" data-slot="body" style="grid-row:3;grid-column:2" ${gear.body ? "onclick=\"window.combatUI.unequipItem('body')\"" : ''}>${renderMiniSlot('body', gear.body)}</div>
                    <div class="equip-slot offhand ${gear.offhand ? 'clickable' : ''}" data-slot="offhand" style="grid-row:3;grid-column:3" ${gear.offhand ? "onclick=\"window.combatUI.unequipItem('offhand')\"" : ''}>${renderMiniSlot('offhand', gear.offhand)}</div>
                    <div class="equip-slot hands ${gear.hands ? 'clickable' : ''}" data-slot="hands" style="grid-row:4;grid-column:1" ${gear.hands ? "onclick=\"window.combatUI.unequipItem('hands')\"" : ''}>${renderMiniSlot('hands', gear.hands)}</div>
                    <div class="equip-slot legs ${gear.legs ? 'clickable' : ''}" data-slot="legs" style="grid-row:4;grid-column:2" ${gear.legs ? "onclick=\"window.combatUI.unequipItem('legs')\"" : ''}>${renderMiniSlot('legs', gear.legs)}</div>
                    <div class="equip-slot ring ${gear.ring ? 'clickable' : ''}" data-slot="ring" style="grid-row:5;grid-column:1" ${gear.ring ? "onclick=\"window.combatUI.unequipItem('ring')\"" : ''}>${renderMiniSlot('ring', gear.ring)}</div>
                    <div class="equip-slot feet ${gear.feet ? 'clickable' : ''}" data-slot="feet" style="grid-row:5;grid-column:2" ${gear.feet ? "onclick=\"window.combatUI.unequipItem('feet')\"" : ''}>${renderMiniSlot('feet', gear.feet)}</div>
                    ${activeSet === 'ranged' ? `<div class="equip-slot ammo ${gear.ammo ? 'clickable' : ''}" data-slot="ammo" style="grid-row:5;grid-column:3" ${gear.ammo ? "onclick=\"window.combatUI.unequipItem('ammo')\"" : ''}>${renderMiniSlot('ammo', gear.ammo)}</div>` : ''}
                </div>
                ${activeSet === 'magic' ? renderMiniGlyphPouch() : ''}
                <div class="combat-stats-summary">
                    ${stats.attackStyle === 'magic'
                        ? `<div class="stat-row"><span>Spell Dmg:</span><span>${combatSkill.getTotalSpellDamage().toFixed(2)}x</span></div>`
                        : `<div class="stat-row"><span>Attack:</span><span>${stats.attack}</span></div>`
                    }
                    <div class="stat-row"><span>Defense:</span><span>${stats.defense}</span></div>
                    <div class="stat-row"><span>Speed:</span><span>${(stats.attackSpeed / 1000).toFixed(1)}s</span></div>
                    <div class="stat-row"><span>Accuracy:</span><span>${Math.floor(stats.accuracy)}%</span></div>
                    <div class="stat-row"><span>Crit:</span><span>${Math.floor(stats.critChance)}% / ${stats.critDamage}%</span></div>
                    <div class="stat-row"><span>Style:</span><span>${stats.attackStyle ? getStyleIcon(stats.attackStyle) + ' ' + getStyleName(stats.attackStyle) : 'None'}</span></div>
                </div>
                <div class="equipment-sets-section">
                    <div class="equipment-set-buttons">
                        ${Object.entries(sets).map(([id, set]) => {
                            // Get dynamic icon based on equipped weapon's attack style
                            let setIcon = set.icon; // Default to set's icon
                            if (set.gear && set.gear.weapon) {
                                const weapon = getItem(set.gear.weapon);
                                if (weapon && weapon.attackStyle) {
                                    const styleIcons = { melee: '⚔️', ranged: '🏹', magic: '🪄' };
                                    setIcon = styleIcons[weapon.attackStyle] || set.icon;
                                }
                            }
                            return `
                            <button class="set-btn ${activeSet === id ? 'active' : ''}"
                                    onclick="window.combatUI.loadSet('${id}')"
                                    data-tooltip="Switch to ${set.name} set">
                                ${setIcon} ${set.name}
                            </button>
                        `}).join('')}
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Check if any zone in a tier is unlocked
 */
function isTierUnlocked(tierNum, playerLevel) {
    const zones = Object.values(COMBAT_ZONES);
    const tierZones = zones.filter(z => z.tier === tierNum);
    return tierZones.some(z => playerLevel >= z.level);
}

/**
 * Render tab buttons with lock icons for locked tiers
 */
function renderTabs() {
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const playerLevel = skill.level;

    const tier1Unlocked = isTierUnlocked(1, playerLevel);
    const tier2Unlocked = isTierUnlocked(2, playerLevel);
    const tier3Unlocked = isTierUnlocked(3, playerLevel);
    const tier4Unlocked = isTierUnlocked(4, playerLevel);

    // Pyre Veil tab visibility and corruption based on pyre level
    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
    const tier4Zones = Object.values(COMBAT_ZONES).filter(z => z.tier === 4 && z.pyreVeilHidden);
    const allHidden = tier4Zones.length > 0 && tier4Zones.every(z => getZoneCorruptionPhase(z, pyreLevel) === 'hidden');
    const minPyreReq = tier4Zones.length > 0 ? Math.min(...tier4Zones.map(z => z.pyreLevel)) : 0;
    const discoveryLevel = minPyreReq - 10;
    let tier4Label = 'Pyre Veil';
    if (minPyreReq > 0 && pyreLevel < discoveryLevel) {
        tier4Label = corruptHeavy('???');
    } else if (minPyreReq > 0 && pyreLevel < minPyreReq) {
        tier4Label = corruptLight('Pyre Veil');
    }

    // Hide tier 4 tab entirely if player is below reveal threshold (pyre level too low)
    const tier4Tab = allHidden ? '' : `
            <button class="skill-tab ${currentTab === 'tier4' ? 'active' : ''}"
                    onclick="window.combatUI.switchTab('tier4')">
                ${COMBAT_TIERS[4].icon} ${tier4Label}${tier4Unlocked ? '' : ' \ud83d\udd12'}
            </button>`;

    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'tier1' ? 'active' : ''}"
                    onclick="window.combatUI.switchTab('tier1')">
                ${COMBAT_TIERS[1].icon} Wilderness${tier1Unlocked ? '' : ' \ud83d\udd12'}
            </button>
            <button class="skill-tab ${currentTab === 'tier2' ? 'active' : ''}"
                    onclick="window.combatUI.switchTab('tier2')">
                ${COMBAT_TIERS[2].icon} Depths${tier2Unlocked ? '' : ' \ud83d\udd12'}
            </button>
            <button class="skill-tab ${currentTab === 'tier3' ? 'active' : ''}"
                    onclick="window.combatUI.switchTab('tier3')">
                ${COMBAT_TIERS[3].icon} Forbidden${tier3Unlocked ? '' : ' \ud83d\udd12'}
            </button>${tier4Tab}
        </div>
    `;
}

/**
 * Get mob icon/emoji for display (placeholder for future artwork)
 */
function getMobIcon(mob) {
    if (!mob) return '👾';
    // Map mob types to icons (will be replaced with art later)
    const icons = {
        // Tier 1 - Goblin Camp
        goblin_raider: '👺',
        goblin_shaman: '🧙',
        goblin_skirmisher: '👺',
        camp_chicken: '🐔',
        goblin_herbalist: '🌿',
        ember_goblin: '🔥',
        goblin_trapper: '🪤',
        goblin_looter: '💰',
        goblin_firestarter: '🧨',
        // Tier 1 - Forgotten Outskirts
        scavenger: '🥷',
        stray_mercenary: '⚔️',
        feral_beast: '🐺',
        carrion_hawk: '🦅',
        ruined_sentry: '🗿',
        marsh_forager: '🐸',
        wild_hound: '🐕',
        // Tier 1 - Bandit Hideout
        bandit_thug: '👤',
        bandit_archer: '🏹',
        bandit_enforcer: '🥷',
        bandit_alchemist: '⚗️',
        black_market_runner: '🏃',
        bandit_scout: '👁️',
        bandit_smuggler: '🎒',
        bandit_hexer: '🧿',
        // Tier 2 - Troll Caves
        cave_troll: '👹',
        troll_brute: '👹',
        regenerating_troll: '👹',
        crystal_lurker: '💎',
        ashbound_troll: '🔥',
        fungal_crawler: '🍄',
        stone_golem: '🪨',
        cave_spider: '🕷️',
        // Tier 2 - Undead Crypt
        skeleton_warrior: '💀',
        rotting_zombie: '🧟',
        crypt_acolyte: '☠️',
        tomb_weaver: '🕸️',
        scorched_revenant: '🔥',
        spectral_archer: '👻',
        bone_collector: '🦴',
        cursed_knight: '⚔️',
        // Tier 2 - Demon Scar
        lesser_demon: '👿',
        flame_imp: '🔥',
        scarred_cultist: '🧛',
        infernal_arcanist: '🔮',
        cinderbound_cultist: '🔥',
        pit_fiend: '😈',
        soul_harvester: '👻',
        hellhound: '🐕‍🦺',
        // Tier 3 - Dragon Lair
        wyrmling: '🐲',
        ancient_dragon: '🐉',
        draconic_sentinel: '🛡️',
        hoard_guardian: '🛡️',
        molten_wyrm: '🌋',
        drake_whelp: '🐉',
        dragon_priest: '🧙',
        scaled_marauder: '🦎',
        // Tier 3 - Void Rift
        void_aberration: '👁️',
        rift_horror: '🌀',
        void_channeler: '💜',
        rift_weaver: '🕸️',
        void_ash_horror: '💀',
        void_stalker: '👁️',
        rift_scavenger: '🦇',
        entropy_maw: '🌀',
        // Tier 3 - Ancient Sanctum
        elder_construct: '🗿',
        corrupted_avatar: '👤',
        elder_god_fragment: '⚡',
        elder_herbkeeper: '🧓',
        sanctum_guardian: '🗿',
        ancient_scribe: '📜',
        temporal_shade: '⏳',
        relic_hoarder: '🏺',
        // Other mobs
        goblin_miner: '⛏️',
        phantom_archer: '👻',
        scarab_weaver: '🪲',
        deepclaw_scavenger: '🦀',
        voidforged_sentinel: '🛡️',
        // Ashen Crucible bosses
        ashen_warden: '🔥',
        pyre_guardian: '🛡️',
        flame_eternal: '🌋',
        searing_matriarch: '👑',
        molten_colossus: '🗿',
        cindered_knight: '⚔️',
        convergence_herald: '🌀'
    };
    return icons[mob.id] || '👾';
}

/**
 * Render inline mob drops for combat arena
 * Shows all drops with icon, quantity, and fraction-based drop rate on hover
 */
function renderInlineMobDrops(mobId, zoneId) {
    const lootTable = getMobLoot(mobId, zoneId);
    const hasCommon = lootTable.common && lootTable.common.length > 0;
    const hasRare = lootTable.rare && lootTable.rare.length > 0;

    if (!hasCommon && !hasRare) {
        return '<div class="mob-drops-inline"><span class="no-drops">No drops</span></div>';
    }

    let dropsHtml = '';

    // Render all drops (common first, then rare)
    const allDrops = [
        ...(lootTable.common || []).map(d => ({ ...d, rarity: 'common' })),
        ...(lootTable.rare || []).map(d => ({ ...d, rarity: 'rare' }))
    ];

    for (const drop of allDrops) {
        const icon = getItemIcon(drop.itemId);
        const name = getItemName(drop.itemId) || drop.itemId;
        // Skip if item doesn't exist in registry
        if (!icon && !name) {
            console.warn(`Missing item in loot table: ${drop.itemId}`);
            continue;
        }
        // Loot table uses minQty/maxQty
        const minQty = drop.minQty || 1;
        const maxQty = drop.maxQty || minQty;
        const qtyText = maxQty > minQty
            ? `${minQty}-${maxQty}`
            : (minQty > 1 ? `x${minQty}` : '');
        const fraction = formatDropRate(drop.chance);
        const rarityClass = drop.rarity === 'rare' ? 'rare' : '';

        dropsHtml += `
            <div class="inline-drop ${rarityClass}" data-tooltip="${name}${qtyText ? ' (' + qtyText + ')' : ''}\n${fraction}">
                <span class="drop-icon">${icon || '❓'}</span>
            </div>
        `;
    }

    return `<div class="mob-drops-inline">${dropsHtml}</div>`;
}

/**
 * Render research progress section for a zone
 * Shows progress bars for each mob's weakness (0-3) and resistance (0-6) research
 */
function renderResearchProgress(zone) {
    // Only show research section if there's a pet patrolling or some research has been done
    const patrollingPet = gameState.getPatrollingPet(zone.id);
    const hasAnyResearch = zone.mobs.some(mobId => gameState.getMobResearch(mobId) > 0);

    // Don't show section if no research and no patrol
    if (!hasAnyResearch && !patrollingPet) {
        return '';
    }

    // Check if fully researched
    const allMobsResearched = zone.mobs.every(mobId => gameState.getMobResearchLevel(mobId) >= 2);
    if (allMobsResearched) {
        return `
            <div class="zone-research-section complete">
                <span class="research-complete-badge">✓ Fully Researched</span>
            </div>
        `;
    }

    // Build research progress for each mob
    const mobsHtml = zone.mobs.map(mobId => {
        const mob = getMob(mobId);
        const research = gameState.getMobResearch(mobId);
        const weaknessRevealed = research >= 3;
        const resistanceRevealed = research >= 6;

        // Progress toward thresholds
        const weaknessProgress = Math.min(research / 3, 1);
        const resistanceProgress = Math.min(research / 6, 1);

        // Show progress text or revealed weakness/resistance
        let weaknessText, weaknessTooltip;
        if (weaknessRevealed) {
            if (mob?.weakTo) {
                weaknessText = `⬇️${getStyleIcon(mob.weakTo)}`;
                weaknessTooltip = `Weak to ${getStyleName(mob.weakTo)}`;
            } else {
                weaknessText = '—';
                weaknessTooltip = 'No weakness';
            }
        } else {
            weaknessText = `${Math.min(research, 3)}/3`;
            weaknessTooltip = `${Math.min(research, 3)}/3 pts to reveal`;
        }

        let resistanceText, resistanceTooltip;
        if (resistanceRevealed) {
            if (mob?.resistantTo) {
                resistanceText = `⬆️${getStyleIcon(mob.resistantTo)}`;
                resistanceTooltip = `Resistant to ${getStyleName(mob.resistantTo)}`;
            } else {
                resistanceText = '—';
                resistanceTooltip = 'No resistance';
            }
        } else {
            resistanceText = `${Math.min(research, 6)}/6`;
            resistanceTooltip = `${Math.min(research, 6)}/6 pts to reveal`;
        }

        return `
            <div class="mob-research-row" data-mob-id="${mobId}">
                <span class="mob-research-name">${getMobIcon(mob)} ${mob?.name || mobId}</span>
                <div class="mob-research-bars">
                    <div class="research-bar weakness ${weaknessRevealed ? 'revealed' : ''}" data-tooltip="${weaknessTooltip}">
                        <div class="research-bar-fill" style="width: ${weaknessProgress * 100}%"></div>
                        <span class="research-progress-text">${weaknessText}</span>
                    </div>
                    <div class="research-bar resistance ${resistanceRevealed ? 'revealed' : ''}" data-tooltip="${resistanceTooltip}">
                        <div class="research-bar-fill" style="width: ${resistanceProgress * 100}%"></div>
                        <span class="research-progress-text">${resistanceText}</span>
                    </div>
                </div>
            </div>
        `;
    }).join('');

    return `
        <div class="zone-research-section">
            <div class="research-header">
                <span class="research-label">Research Progress</span>
            </div>
            <div class="research-mobs">
                ${mobsHtml}
            </div>
        </div>
    `;
}

/**
 * Render active combat display - side-by-side with attack progress bars
 */
function renderActiveCombat() {
    const state = combatSkill.getCombatState();

    if (state.isDead) {
        const remaining = Math.ceil(state.respawnRemaining / 1000);
        return `
            <div class="active-combat death-overlay">
                <div class="death-message">
                    <span class="death-icon">\ud83d\udc80</span>
                    <h3>You have died!</h3>
                    <p>Respawning in <span class="respawn-timer">${remaining}</span> seconds...</p>
                    <div class="respawn-progress">
                        <div class="respawn-bar" style="width: ${100 - (state.respawnRemaining / COMBAT_CONFIG.RESPAWN_TIME * 100)}%"></div>
                    </div>
                </div>
            </div>
        `;
    }

    if (!state.inCombat || !state.currentMob) {
        return '';
    }

    const mob = state.currentMob;
    const stats = state.playerStats;
    const mobHpPercent = (state.mobCurrentHp / state.mobMaxHp) * 100;
    const playerHpPercent = (state.currentHp / state.maxHp) * 100;
    const playerHpClass = playerHpPercent > 50 ? 'hp-high' : playerHpPercent > 25 ? 'hp-mid' : 'hp-low';

    // Attack progress (0-100%)
    const playerAttackPercent = state.playerAttackProgress * 100;
    const mobAttackPercent = state.mobAttackProgress * 100;

    // Build weakness/resistance display (only show if researched)
    let weaknessDisplay = '';
    const weaknessRevealed = gameState.isMobWeaknessRevealed(mob.id);
    const resistanceRevealed = gameState.isMobResistanceRevealed(mob.id);

    if (mob.weakTo && weaknessRevealed) {
        let weaknessTooltip = `Weak to ${getStyleName(mob.weakTo)}`;
        // Add elemental weakness info for magic-weak mobs
        if (mob.weakTo === 'magic' && mob.elementalWeakness) {
            const elements = mob.elementalWeakness.map(e => getElementName(e)).join(', ');
            weaknessTooltip += ` (${elements})`;
        }
        weaknessDisplay += `<span class="mob-weakness" data-tooltip="${weaknessTooltip}">⬇️${getStyleIcon(mob.weakTo)}</span>`;
        // Show elemental icons for magic-weak mobs
        if (mob.weakTo === 'magic' && mob.elementalWeakness) {
            weaknessDisplay += mob.elementalWeakness.map(e =>
                `<span class="mob-elemental-weakness" data-tooltip="Weak to ${getElementName(e)} spells (+20% dmg)">${getElementIcon(e)}</span>`
            ).join('');
        }
    } else if (mob.weakTo && !weaknessRevealed) {
        weaknessDisplay += `<span class="mob-weakness unknown" data-tooltip="Weakness unknown - patrol this area">⬇️???</span>`;
    }
    if (mob.resistantTo && resistanceRevealed) {
        weaknessDisplay += `<span class="mob-resistance" data-tooltip="Resistant to ${getStyleName(mob.resistantTo)}">⬆️${getStyleIcon(mob.resistantTo)}</span>`;
    } else if (mob.resistantTo && !resistanceRevealed) {
        weaknessDisplay += `<span class="mob-resistance unknown" data-tooltip="Resistance unknown - patrol this area">⬆️???</span>`;
    }

    // Check if out of ammo for ranged
    const ammo = gameState.getEquippedAmmo();
    const isRanged = stats.attackStyle === 'ranged';
    const outOfAmmo = isRanged && (!ammo.itemId || ammo.quantity <= 0);

    // Check magic attack issues
    const isMagic = stats.attackStyle === 'magic';
    const canCast = state.canCastSpell;
    const magicWarning = isMagic && !canCast.success;
    const activeSpell = state.activeSpell;

    // Build attack label - show spell for magic, "Attack" for others
    let attackLabel = 'Attack';
    if (isMagic && activeSpell) {
        attackLabel = `<span class="active-spell-label">${activeSpell.icon} ${activeSpell.name}</span>`;
    }

    return `
        <div class="active-combat">
            ${outOfAmmo ? `
                <div class="combat-warning out-of-ammo-warning">
                    ⚠️ Out of ammo! Equip arrows to attack.
                </div>
            ` : ''}
            ${magicWarning ? `
                <div class="combat-warning magic-warning">
                    🪄 ${canCast.reason || 'Cannot cast spell'}
                </div>
            ` : ''}
            <div class="combat-layout">
                ${renderPlayerEffectsColumn()}
                <div class="combat-arena">
                    <div class="combatant player-side">
                        <div class="combatant-avatar player-avatar">
                            <span class="avatar-icon">⚔️</span>
                        </div>
                        <div class="combatant-info">
                            <div class="combatant-name">You</div>
                            <div class="combatant-hp-bar ${playerHpClass}">
                                <div class="combatant-hp-fill player-hp-fill" style="width: ${playerHpPercent}%"></div>
                                <span class="combatant-hp-text player-hp-text">${state.currentHp} / ${state.maxHp}</span>
                            </div>
                            <div class="attack-bar-container ${isMagic && activeSpell ? 'magic-attack' : ''}">
                                <div class="attack-bar-label">${attackLabel}</div>
                                <div class="attack-bar">
                                    <div class="attack-bar-fill player-attack-fill" style="width: ${playerAttackPercent}%"></div>
                                </div>
                                <span class="attack-speed-text">${(stats.attackSpeed / 1000).toFixed(1)}s</span>
                            </div>
                            <div class="combatant-stats">
                                ${stats.attackStyle === 'magic'
                                    ? `<span data-tooltip="Spell Damage Multiplier">✨ ${combatSkill.getTotalSpellDamage().toFixed(2)}x</span>`
                                    : `<span data-tooltip="Attack Power">⚔ ${stats.attack}</span>`
                                }
                                <span data-tooltip="Defense">🛡 ${stats.defense}</span>
                                <span data-tooltip="Accuracy">🎯 ${Math.floor(state.effectiveAccuracy)}%</span>
                                ${stats.attackStyle ? `<span class="style-indicator style-${stats.attackStyle}">${getStyleIcon(stats.attackStyle)}</span>` : ''}
                            </div>
                        </div>
                    </div>
                    <div class="combat-vs">VS</div>
                    <div class="combatant mob-side">
                        <div class="combatant-avatar mob-avatar">
                            <span class="avatar-icon">${getMobIcon(mob)}</span>
                        </div>
                        <div class="combatant-info">
                            <div class="combatant-name">
                                ${mob.pyreVeilLevel ? `<span class="veil-mob-name-indicator" data-tooltip="Pyre Veil creature">${getFireSpriteSm()}</span>` : ''}${mob.name} ${weaknessDisplay}
                                <button class="mob-drops-btn" onclick="window.combatUI.showMobDrops('${mob.id}', '${state.currentZone?.id || ''}')" data-tooltip="View Drops">💎</button>
                            </div>
                            <div class="combatant-hp-bar mob-hp">
                                <div class="combatant-hp-fill mob-hp-fill" style="width: ${mobHpPercent}%"></div>
                                <span class="combatant-hp-text mob-hp-text">${state.mobCurrentHp} / ${state.mobMaxHp}</span>
                            </div>
                            <div class="attack-bar-container">
                                <div class="attack-bar-label">Attack</div>
                                <div class="attack-bar mob-attack-bar">
                                    <div class="attack-bar-fill mob-attack-fill" style="width: ${mobAttackPercent}%"></div>
                                </div>
                                <span class="attack-speed-text">${(state.mobEffectiveAttackSpeed / 1000).toFixed(1)}s</span>
                            </div>
                            <div class="combatant-stats">
                                <span data-tooltip="Mob Attack">⚔ ${mob.attack}-${mob.attackMax}</span>
                                <span data-tooltip="Mob Defense">🛡 ${mob.defense}</span>
                                <span data-tooltip="Mob Accuracy">🎯 ${mob.accuracy}%</span>
                            </div>
                            ${renderInlineMobDrops(mob.id, state.currentZone?.id)}
                        </div>
                    </div>
                </div>
                ${renderMobEffectsColumn()}
            </div>
            <div class="combat-zone-bar">
                <span class="zone-label">Fighting in:</span>
                <span class="zone-name">${state.currentZone.name}</span>
                ${state.targetMob ? `<span class="target-indicator" data-tooltip="Target farming">(${getMob(state.targetMob)?.name || state.targetMob})</span>` : ''}
                <button class="stop-btn" onclick="window.combatUI.stopFighting()">Stop Fighting</button>
            </div>
            ${renderSpellEffectsIndicators()}
            ${state.pyrePenalty.deficit > 0 ? `
                <div class="pyre-penalty-warning">
                    <span class="penalty-icon">🔥</span>
                    <span class="penalty-text">Pyre deficit: -${Math.round(state.pyrePenalty.deficit * 50)}% dmg, -${Math.round(state.pyrePenalty.deficit * 50)}% atk spd, -${Math.round(state.pyrePenalty.deficit * 50)}% loot</span>
                </div>
            ` : ''}
            ${state.pyrePenalty.bonus > 0 ? `
                <div class="pyre-bonus-indicator">
                    <span class="bonus-icon">🔥</span>
                    <span class="bonus-text">Pyre bonus: +${(state.pyrePenalty.bonus * 100).toFixed(1)}% dmg / atk spd / loot</span>
                </div>
            ` : ''}
        </div>
    `;
}

/**
 * Render preview panel - shows mob info before combat starts
 */
function renderPreviewPanel() {
    const state = combatSkill.getCombatState();
    if (!state.isPreviewing || !state.previewState) {
        return '';
    }

    const { zone, pyrePenalty, isTargetFarming, mobId: targetMobId } = state.previewState;
    if (!zone) return '';

    // Check if zone is cleared (enables target farming)
    const zoneProgress = gameState.getZoneProgress(zone.id);
    const isCleared = zoneProgress.cleared;
    const clearProgress = Math.min(zoneProgress.lifetimeKills / zone.clearThreshold, 1);

    // Get visible mobs only (filtered by Pyre level)
    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
    const visibleMobIds = getVisibleMobsForZone(zone.id, pyreLevel);
    const allMobs = visibleMobIds.map(mobId => getMob(mobId)).filter(m => m);

    // Build mob cards for visible mobs in zone
    const mobCardsHtml = allMobs.map(mob => {
        const weaknessRevealed = gameState.isMobWeaknessRevealed(mob.id);
        const resistanceRevealed = gameState.isMobResistanceRevealed(mob.id);
        const kills = gameState.getKillCount(mob.id);
        const isTarget = isTargetFarming && targetMobId === mob.id;

        // Build compact weakness/resistance display
        let weakResist = '';
        if (mob.weakTo && weaknessRevealed) {
            // Show style weakness
            let weakTooltip = `Weak to ${getStyleName(mob.weakTo)}`;
            let weakDisplay = `⬇️${getStyleIcon(mob.weakTo)}`;

            // Also show elemental weakness if mob has one (and is weak to magic)
            if (mob.elementalWeakness && mob.elementalWeakness.length > 0) {
                const elements = mob.elementalWeakness.map(e => getElementName(e)).join(', ');
                const elementIcons = mob.elementalWeakness.map(e => getElementIcon(e)).join('');
                weakTooltip += ` (${elements} +20% dmg)`;
                weakDisplay += elementIcons;
            }
            weakResist += `<span class="mob-weak" data-tooltip="${weakTooltip}">${weakDisplay}</span>`;
        } else if (mob.weakTo && !weaknessRevealed) {
            weakResist += `<span class="mob-weak unknown">⬇️?</span>`;
        }
        if (mob.resistantTo && resistanceRevealed) {
            weakResist += `<span class="mob-resist" data-tooltip="Resistant to ${getStyleName(mob.resistantTo)}">⬆️${getStyleIcon(mob.resistantTo)}</span>`;
        } else if (mob.resistantTo && !resistanceRevealed) {
            weakResist += `<span class="mob-resist unknown">⬆️?</span>`;
        }
        if (!weakResist) {
            weakResist = '<span class="mob-neutral">—</span>';
        }

        // Get mob loot for inline display (show all drops)
        const mobLoot = getMobLoot(mob.id, zone.id);
        const lootIcons = [];
        // Show all common drops
        if (mobLoot.common) {
            mobLoot.common.forEach(drop => {
                const icon = getItemIcon(drop.itemId);
                const name = getItemName(drop.itemId);
                const rate = formatDropRate(drop.chance);
                lootIcons.push(`<span class="loot-icon common" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}\n${rate}">${icon}</span>`);
            });
        }
        // Show all rare drops
        if (mobLoot.rare) {
            mobLoot.rare.forEach(drop => {
                const icon = getItemIcon(drop.itemId);
                const name = getItemName(drop.itemId);
                const rate = formatDropRate(drop.chance);
                lootIcons.push(`<span class="loot-icon rare" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}\n(Rare) ${rate}">${icon}</span>`);
            });
        }
        const lootHtml = lootIcons.length > 0
            ? `<div class="preview-mob-loot">${lootIcons.join('')}</div>`
            : '';

        // If zone is cleared, mobs are clickable for target farming
        const clickable = isCleared;
        const clickHandler = clickable ? `onclick="window.combatUI.selectTargetMob('${zone.id}', '${mob.id}')"` : '';
        const clickableClass = clickable ? 'clickable' : '';
        const targetClass = isTarget ? 'targeted' : '';

        // Check if this is a Pyre Veil mob
        const isPyreVeilMob = mob.pyreVeilLevel;
        const veilBadge = isPyreVeilMob ? `<span class="preview-veil-badge" data-tooltip="Pyre Veil creature">${getFireSpriteSm()}</span>` : '';

        return `
            <div class="preview-mob-card ${clickableClass} ${targetClass} ${isPyreVeilMob ? 'pyre-veil-mob' : ''}" ${clickHandler} data-tooltip="${clickable ? 'Click to target farm this mob' : ''}">
                ${veilBadge}
                <div class="preview-mob-icon">${getMobIcon(mob)}</div>
                <div class="preview-mob-name">${mob.name}</div>
                <div class="preview-mob-stats-row">
                    <span data-tooltip="HP">❤️${mob.hp}</span>
                    <span data-tooltip="Attack">⚔️${mob.attack}-${mob.attackMax}</span>
                    <span data-tooltip="Speed">⚡${(mob.attackSpeed / 1000).toFixed(1)}s</span>
                </div>
                <div class="preview-mob-extra">
                    <span class="weak-resist">${weakResist}</span>
                    <span class="kills" data-tooltip="${formatWithCommas(kills)} kills">💀${kills >= 1000 ? formatNumber(kills) : kills}</span>
                </div>
                ${lootHtml}
            </div>
        `;
    }).join('');

    // Pyre warning or bonus
    let pyreIndicator = '';
    if (pyrePenalty.deficit > 0) {
        pyreIndicator = `
            <div class="preview-pyre-warning">
                🔥 Pyre deficit: -${Math.round(pyrePenalty.deficit * 50)}% dmg/spd/loot
            </div>
        `;
    } else if (pyrePenalty.bonus > 0) {
        pyreIndicator = `
            <div class="preview-pyre-bonus">
                🔥 Pyre bonus: +${(pyrePenalty.bonus * 100).toFixed(1)}% dmg/spd/loot
            </div>
        `;
    }

    // Target farming status
    let targetFarmingHint = '';
    if (!isCleared) {
        const killsNeeded = zone.clearThreshold - zoneProgress.lifetimeKills;
        targetFarmingHint = `
            <div class="target-farming-hint">
                <span class="hint-icon">🎯</span>
                <span class="hint-text">Target farming unlocks after clearing zone (${killsNeeded} kills remaining)</span>
            </div>
        `;
    } else if (!isTargetFarming) {
        targetFarmingHint = `
            <div class="target-farming-hint unlocked">
                <span class="hint-icon">🎯</span>
                <span class="hint-text">Click a mob above to target farm, or attack for random</span>
            </div>
        `;
    } else {
        const targetMob = getMob(targetMobId);
        targetFarmingHint = `
            <div class="target-farming-hint active">
                <span class="hint-icon">🎯</span>
                <span class="hint-text">Target: ${targetMob?.name || 'Unknown'}</span>
            </div>
        `;
    }

    // Build zone drops section (shared drops for all mobs in zone)
    const zoneLoot = getZoneLoot(zone.id);
    const zoneLootIcons = [];
    if (zoneLoot.common) {
        zoneLoot.common.forEach(drop => {
            const icon = getItemIcon(drop.itemId);
            const name = getItemName(drop.itemId);
            const rate = formatDropRate(drop.chance);
            zoneLootIcons.push(`<span class="loot-icon common" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}\n${rate}">${icon}</span>`);
        });
    }
    if (zoneLoot.rare) {
        zoneLoot.rare.forEach(drop => {
            const icon = getItemIcon(drop.itemId);
            const name = getItemName(drop.itemId);
            const rate = formatDropRate(drop.chance);
            zoneLootIcons.push(`<span class="loot-icon rare" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}\n(Rare) ${rate}">${icon}</span>`);
        });
    }
    const zoneDropsHtml = zoneLootIcons.length > 0
        ? `<div class="preview-zone-drops">
               <span class="zone-drops-label">Zone Drops:</span>
               <div class="zone-drops-icons">${zoneLootIcons.join('')}</div>
           </div>`
        : '';

    return `
        <div class="combat-preview-panel">
            <div class="preview-header">
                <h3>${zone.name}</h3>
                <span class="preview-subtitle">${isTargetFarming ? `Target: ${getMob(targetMobId)?.name}` : 'Random Encounter'}</span>
            </div>

            <div class="preview-mobs-row">
                ${mobCardsHtml}
            </div>

            ${zoneDropsHtml}

            ${targetFarmingHint}
            ${pyreIndicator}

            <div class="preview-actions">
                <button class="attack-btn" onclick="window.combatUI.startFromPreview()">
                    ⚔️ ${isTargetFarming ? 'Hunt' : 'Fight'}
                </button>
                <button class="cancel-btn" onclick="window.combatUI.clearPreview()">
                    Cancel
                </button>
            </div>
        </div>
    `;
}

/**
 * Render a single zone card (simplified - details in modal)
 */
function renderZoneCard(zone) {
    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;

    // Check corruption phase for Pyre Veil hidden zones
    const corruptionPhase = getZoneCorruptionPhase(zone, pyreLevel);
    if (corruptionPhase === 'hidden') {
        return ''; // Not visible yet - pyre level too low
    }
    if (corruptionPhase === 'unknown' || corruptionPhase === 'discovered') {
        return renderVeiledZoneCard(zone, corruptionPhase);
    }

    // Tiered zones get their own renderer
    if (zone.tiers) {
        return renderTieredZoneCard(zone);
    }

    const skill = gameState.getSkill(SKILLS.COMBAT);
    const state = combatSkill.getCombatState();
    const isLocked = skill.level < zone.level;
    const isActive = state.inCombat && state.currentZone?.id === zone.id;

    // Get zone progress
    const zoneProgress = gameState.getZoneProgress(zone.id);
    const isCleared = zoneProgress.cleared;
    const lifetimeKills = zoneProgress.lifetimeKills;
    const clearProgress = Math.min(lifetimeKills / zone.clearThreshold, 1);

    // Get Pyre penalty info
    const pyrePenalty = combatSkill.getPyrePenalty(zone.id);
    const hasPyreReq = zone.pyreLevel !== null;
    const pyreMet = !hasPyreReq || pyreLevel >= zone.pyreLevel;

    // Get visible mobs
    const visibleMobIds = getVisibleMobsForZone(zone.id, pyreLevel);
    const hiddenMobCount = getHiddenMobCount(zone.id, pyreLevel);

    // Calculate ranges across visible mobs
    const mobs = visibleMobIds.map(mobId => getMob(mobId)).filter(m => m);
    const avgXP = mobs.length > 0 ? Math.round(mobs.reduce((sum, m) => sum + m.xp, 0) / mobs.length) : 0;

    // HP range
    const hpValues = mobs.map(m => m.hp);
    const minHp = Math.min(...hpValues);
    const maxHp = Math.max(...hpValues);
    const hpDisplay = minHp === maxHp ? `${minHp}` : `${minHp}-${maxHp}`;

    // Attack speed range
    const speedValues = mobs.map(m => m.attackSpeed / 1000);
    const minSpeed = Math.min(...speedValues);
    const maxSpeed = Math.max(...speedValues);
    const speedDisplay = minSpeed === maxSpeed ? `${minSpeed.toFixed(1)}s` : `${minSpeed.toFixed(1)}-${maxSpeed.toFixed(1)}s`;

    // Max hit range
    const maxHitValues = mobs.map(m => m.attackMax || m.attack);
    const minMaxHit = Math.min(...maxHitValues);
    const maxMaxHit = Math.max(...maxHitValues);
    const maxHitDisplay = minMaxHit === maxMaxHit ? `${minMaxHit}` : `${minMaxHit}-${maxMaxHit}`;

    // Mob icons preview (show first 4 mobs)
    const mobIconsHtml = mobs.slice(0, 4).map(mob => {
        const isPyreVeil = mob.pyreVeilLevel;
        return `<span class="mob-preview-icon ${isPyreVeil ? 'pyre-veil' : ''}" data-tooltip="${mob.name}">${getMobIcon(mob)}</span>`;
    }).join('') + (hiddenMobCount > 0 ? `<span class="mob-preview-icon hidden" data-tooltip="${hiddenMobCount} hidden">+${hiddenMobCount}</span>` : '');

    // Status indicators
    let statusIndicators = '';

    // Pyre status indicator (compact)
    if (hasPyreReq) {
        if (!pyreMet) {
            const penaltyPct = Math.round(pyrePenalty.deficit * 50);
            statusIndicators += `<span class="status-indicator pyre-penalty" data-tooltip="Pyre ${zone.pyreLevel} needed (-${penaltyPct}%)">${getFireSpriteSm()}</span>`;
        } else if (pyrePenalty.bonus > 0) {
            const bonusPct = (pyrePenalty.bonus * 100).toFixed(1);
            statusIndicators += `<span class="status-indicator pyre-bonus" data-tooltip="Pyre bonus +${bonusPct}%">${getFireSpriteSm()}</span>`;
        }
    }

    // Hidden mobs indicator
    if (hiddenMobCount > 0) {
        statusIndicators += `<span class="status-indicator hidden-mobs" data-tooltip="${hiddenMobCount} hidden mob${hiddenMobCount > 1 ? 's' : ''}">${getFireSpriteSm()}+${hiddenMobCount}</span>`;
    }

    // Pet patrol indicator
    const patrollingPet = gameState.getPatrollingPet(zone.id);
    if (patrollingPet) {
        const petData = getPetData(patrollingPet.id);
        const allMobsResearched = zone.mobs.every(mobId => gameState.getMobResearchLevel(mobId) >= 2);
        let statusClass = 'researching';
        let statusText = 'Researching';
        if (allMobsResearched) {
            statusClass = 'complete';
            statusText = 'Research Complete';
        } else if (!patrollingPet.contractId) {
            statusClass = 'idle';
            statusText = 'Idle';
        }
        statusIndicators += `<span class="status-indicator patrol ${statusClass}" data-tooltip="${petData?.name || 'Pet'}: ${statusText}">${petData?.icon || '🐾'}</span>`;
    }

    // Research progress indicator
    const totalResearch = zone.mobs.reduce((sum, mobId) => sum + gameState.getMobResearch(mobId), 0);
    const maxResearch = zone.mobs.length * 6; // 6 research points per mob for full unlock
    const allMobsResearched = totalResearch >= maxResearch;
    if (totalResearch > 0 && totalResearch < maxResearch) {
        const researchPct = Math.round((totalResearch / maxResearch) * 100);
        statusIndicators += `<span class="status-indicator research" data-tooltip="Research: ${researchPct}%">📖${researchPct}%</span>`;
    } else if (allMobsResearched) {
        statusIndicators += `<span class="status-indicator research complete" data-tooltip="Fully Researched">📖✓</span>`;
    }

    // Patrol button for action row
    let patrolBtnHtml = '';
    const combatPets = gameState.getCombatCapablePets();
    if (combatPets.length > 0 && !allMobsResearched) {
        if (patrollingPet) {
            const petData = getPetData(patrollingPet.id);
            patrolBtnHtml = `
                <button class="zone-patrol-btn active" onclick="window.combatUI.showPatrolManagement('${zone.id}')" data-tooltip="${petData?.name || 'Pet'} patrolling">
                    ${petData?.icon || '🐾'}
                </button>
            `;
        } else {
            const availablePets = combatPets.filter(p => !p.patrolZone);
            if (availablePets.length > 0) {
                patrolBtnHtml = `
                    <button class="zone-patrol-btn" onclick="window.combatUI.showPatrolSelect('${zone.id}')" data-tooltip="Assign pet patrol">
                        🐾
                    </button>
                `;
            } else {
                patrolBtnHtml = `
                    <button class="zone-patrol-btn busy" data-tooltip="All pets busy">
                        🐾
                    </button>
                `;
            }
        }
    }

    // Clear progress (compact bar if not cleared)
    let clearProgressHtml = '';
    if (!isCleared) {
        clearProgressHtml = `
            <div class="zone-clear-progress compact">
                <div class="clear-progress-bar">
                    <div class="clear-progress-fill" style="width: ${clearProgress * 100}%"></div>
                </div>
                <span class="clear-progress-text">${lifetimeKills}/${zone.clearThreshold}</span>
            </div>
        `;
    }

    return `
        <div class="zone-card compact ${isLocked ? 'locked' : ''} ${isActive ? 'active' : ''} ${isCleared ? 'cleared' : ''}"
             data-zone-id="${zone.id}"
             style="--zone-color: ${zone.color}">
            <div class="zone-color-strip"></div>
            ${isCleared ? '<div class="zone-cleared-badge">CLEARED</div>' : ''}
            <div class="zone-header">
                <span class="zone-name">${zone.name}</span>
                <span class="zone-level">Lv ${zone.level}</span>
            </div>
            <div class="zone-mob-preview">${mobIconsHtml}</div>
            ${statusIndicators ? `<div class="zone-status-row">${statusIndicators}</div>` : ''}
            <div class="zone-stats-compact">
                <span class="stat-hp" data-tooltip="HP Range">❤️${hpDisplay}</span>
                <span class="stat-speed" data-tooltip="Attack Speed">⚡${speedDisplay}</span>
                <span class="stat-damage" data-tooltip="Max Hit">💥${maxHitDisplay}</span>
                <span class="stat-xp" data-tooltip="Avg XP/kill">⚔️${avgXP}</span>
            </div>
            ${clearProgressHtml}
            ${isLocked ? `
                <div class="zone-locked-overlay">
                    <span class="lock-icon">🔒</span>
                    <span class="lock-text">Level ${zone.level}</span>
                </div>
            ` : `
                <div class="zone-actions">
                    <button class="zone-action-btn ${isActive ? 'stop' : 'start'}"
                            onclick="window.combatUI.${isActive ? 'stopFighting' : 'startFighting'}('${zone.id}')">
                        ${isActive ? 'Stop' : 'Fight'}
                    </button>
                    ${patrolBtnHtml}
                    <button class="zone-details-btn" onclick="window.combatUI.openZoneDetailsModal('${zone.id}')" data-tooltip="View details, mobs, loot">
                        ℹ️
                    </button>
                </div>
            `}
        </div>
    `;
}

/**
 * Get loot preview text for a zone
 */
function getLootPreview(zoneId) {
    const loot = getZoneLoot(zoneId);
    const items = [];

    for (const drop of [...loot.common, ...loot.rare]) {
        const name = getItemName(drop.itemId) || drop.itemId;
        if (!items.includes(name)) {
            items.push(name);
        }
    }

    return items.slice(0, 3).join(', ') + (items.length > 3 ? '...' : '');
}

/**
 * Render zones for current tier
 */
function renderZones() {
    const tierNum = parseInt(currentTab.replace('tier', ''));
    let zones = Object.values(COMBAT_ZONES).filter(z => z.tier === tierNum);

    if (zones.length === 0) {
        return '<div class="no-zones">No zones available in this tier.</div>';
    }

    return `
        <div class="zone-grid">
            ${zones.map(zone => renderZoneCard(zone)).join('')}
        </div>
    `;
}

/**
 * Set auto-eat HP threshold
 */
function setAutoEatThreshold(value) {
    const threshold = parseInt(value, 10);
    gameState.setAutoEatThreshold(threshold);
    // Update just the threshold display
    const thresholdValue = document.querySelector('.threshold-value');
    if (thresholdValue) {
        thresholdValue.textContent = `${threshold}% HP`;
    }
}

/**
 * Store food for auto-eat
 */
function storeAutoEatFood(itemId, quantity = 1) {
    const result = gameState.storeAutoEatFood(itemId, quantity);
    if (result.success) {
        renderCombatUI();
    }
    return result;
}

/**
 * Return stored auto-eat food to inventory
 */
function returnAutoEatFood() {
    const result = gameState.returnAutoEatFood();
    if (result.success) {
        const itemName = getItemName(result.itemId);
        toastSuccess(`Returned ${result.quantity}x ${itemName} to inventory`);
        renderCombatUI();
    } else if (result.reason) {
        toastWarning(result.reason);
    }
    return result;
}

/**
 * Set auto-eat threshold (simple wrapper for UI)
 */
function setThreshold(value) {
    const threshold = parseInt(value, 10);
    gameState.setAutoEatThreshold(threshold);
}

/**
 * Toggle Apothecary Belt on/off
 */
function toggleApothecaryBelt() {
    gameState.toggleApothecaryBelt();
    renderCombatUI();
}

/**
 * Store potion in Apothecary Belt
 */
function storeBeltPotion(itemId, quantity) {
    const result = gameState.storeApothecaryPotion(itemId, quantity);
    if (result.success) {
        renderCombatUI();
    }
    return result;
}

/**
 * Toggle food selection dropdown
 */
function toggleFoodSelection(event) {
    showFoodSelection = !showFoodSelection;
    renderCombatUI();

    // Position the fixed dropdown after render
    if (showFoodSelection) {
        setTimeout(() => {
            const dropdown = document.querySelector('.combat-interface .food-selection-dropdown');
            const trigger = document.querySelector('.combat-interface .food-slot, .combat-interface .auto-eat-food');
            if (dropdown && trigger) {
                const rect = trigger.getBoundingClientRect();
                const dropdownRect = dropdown.getBoundingClientRect();

                // Position below the trigger button
                let top = rect.bottom + 4;
                let left = rect.left;

                // Make sure dropdown doesn't go off-screen right
                if (left + dropdownRect.width > window.innerWidth - 10) {
                    left = window.innerWidth - dropdownRect.width - 10;
                }

                // Make sure dropdown doesn't go off-screen bottom
                if (top + dropdownRect.height > window.innerHeight - 10) {
                    // Show above the trigger instead
                    top = rect.top - dropdownRect.height - 4;
                }

                dropdown.style.top = `${top}px`;
                dropdown.style.left = `${left}px`;
            }
        }, 0);
    }
}

/**
 * Close food selection dropdown
 */
function closeFoodSelection() {
    showFoodSelection = false;
    renderCombatUI();
}

/**
 * Select food from dropdown and store ALL of it for auto-eat
 */
function selectFood(itemId) {
    const playerQty = gameState.getItemCount(itemId);
    if (playerQty <= 0) {
        toastWarning('No food in inventory');
        closeFoodSelection();
        return;
    }

    // Store all of this food type from inventory
    const result = gameState.storeAutoEatFood(itemId, playerQty);
    if (result.success) {
        const itemName = getItemName(itemId);
        toastSuccess(`Stored ${playerQty}x ${itemName} for auto-eat`);
        logger.debug(`[COMBAT] Stored ${playerQty}x ${itemId} for auto-eat`);
    } else {
        toastWarning(result.reason || 'Could not store food');
    }
    showFoodSelection = false;
    renderCombatUI();
}

/**
 * Toggle potion selection dropdown for a category
 */
function togglePotionSelection(category) {
    if (showPotionSelection && selectedPotionCategory === category) {
        // Already open for this category, close it
        closePotionSelection();
    } else {
        // Open for this category
        showPotionSelection = true;
        selectedPotionCategory = category;
        showFoodSelection = false; // Close food dropdown if open
        renderCombatUI();
    }
}

/**
 * Close potion selection dropdown
 */
function closePotionSelection() {
    showPotionSelection = false;
    selectedPotionCategory = null;
    renderCombatUI();
}

/**
 * Select potion from dropdown and store ALL of it in the belt slot
 */
function selectPotion(category, itemId) {
    const playerQty = gameState.getItemCount(itemId);
    if (playerQty <= 0) {
        toastWarning('No potions in inventory');
        closePotionSelection();
        return;
    }

    // Store all of this potion type in the belt slot
    // Note: storeApothecaryPotion determines category from item's effects
    const result = gameState.storeApothecaryPotion(itemId, playerQty);
    if (result.success) {
        const itemName = getItemName(itemId);
        toastSuccess(`Stored ${playerQty}x ${itemName} in belt`);
        logger.debug(`[COMBAT] Stored ${playerQty}x ${itemId} in potion belt`);
    } else {
        toastWarning(result.reason || 'Could not store potion');
    }
    closePotionSelection();
}

/**
 * Remove potion from belt slot and return to inventory
 */
function removeBeltPotion(category) {
    // Find slot by category and remove it
    const slotInfo = gameState.findApothecarySlotByCategory(category);
    if (!slotInfo) {
        toastWarning('No potion in that slot');
        renderCombatUI();
        return;
    }

    const itemId = slotInfo.slot.itemId;
    const quantity = slotInfo.slot.quantity;

    const result = gameState.removeApothecaryPotion(slotInfo.slotIndex);
    if (result.success) {
        const itemName = getItemName(itemId);
        toastSuccess(`Returned ${quantity}x ${itemName} to inventory`);
    } else {
        toastWarning(result.reason || 'Could not remove potion');
    }
    renderCombatUI();
}

/**
 * Load equipment set
 */
function loadEquipmentSet(setId) {
    const result = gameState.loadEquipmentSet(setId);
    if (result.success) {
        if (result.warning) {
            toastWarning(result.warning);
        }
        logger.game(`Loaded equipment set: ${setId}`);
        // Handle potion belt swap mid-combat (clears non-matching buffs, drinks new)
        combatSkill.onEquipmentSetSwitch();
        // Update equipment hash to prevent double render from updateCombatUI
        const gear = gameState.getEquippedGear();
        const activeSet = gameState.getActiveEquipmentSet();
        lastEquipmentHash = JSON.stringify({ gear, activeSet });
        // Full re-render for v2 layout compatibility
        renderCombatUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update only the equipment panel and related stats without full re-render
 */
function updateEquipmentPanelOnly() {
    // Update equipment panel
    const equipmentPanel = document.querySelector('.combat-equipment-panel');
    if (equipmentPanel) {
        equipmentPanel.outerHTML = renderEquipmentPanel();
    }

    // Update header stats (attack, defense, etc.)
    const state = combatSkill.getCombatState();
    const stats = state.playerStats;
    const playerStatsEl = document.querySelector('.combat-player-stats');
    if (playerStatsEl) {
        playerStatsEl.innerHTML = `
            ${stats.attackStyle === 'magic'
                ? `<span class="player-stat" data-tooltip="Spell Damage Multiplier">✨ ${combatSkill.getTotalSpellDamage().toFixed(2)}x</span>`
                : `<span class="player-stat" data-tooltip="Attack Power">⚔ ${stats.attack}</span>`
            }
            <span class="player-stat" data-tooltip="Defense">🛡 ${stats.defense}</span>
            <span class="player-stat" data-tooltip="Attack Speed">⚡ ${(stats.attackSpeed / 1000).toFixed(1)}s</span>
            <span class="player-stat" data-tooltip="Accuracy">🎯 ${Math.floor(stats.accuracy)}%</span>
            ${stats.attackStyle ? `<span class="player-stat style-${stats.attackStyle}" data-tooltip="Attack Style">${getStyleIcon(stats.attackStyle)}</span>` : ''}
        `;
    }

    // Update spell panel visibility (show/hide based on magic weapon)
    const spellPanelContainer = document.querySelector('.combat-player-section');
    const existingSpellPanel = document.querySelector('.spell-panel');
    if (stats.attackStyle === 'magic') {
        if (!existingSpellPanel && spellPanelContainer) {
            // Add spell panel if switching to magic
            spellPanelContainer.insertAdjacentHTML('beforeend', renderSpellPanel());
        }
    } else if (existingSpellPanel) {
        // Remove spell panel if switching away from magic
        existingSpellPanel.remove();
    }

    // Update active combat display if in combat (to reflect new stats)
    if (state.inCombat) {
        const activeCombat = document.querySelector('.active-combat');
        if (activeCombat) {
            activeCombat.outerHTML = renderActiveCombat();
        }
    }
}

/**
 * Show save set modal
 */
function showSaveSetModal() {
    const sets = gameState.getEquipmentSets();
    const modalHtml = `
        <div class="save-set-modal" id="save-set-modal">
            <div class="save-set-content">
                <h3>Save Current Equipment</h3>
                <p>Choose a set to save your current equipment to:</p>
                <div class="save-set-options">
                    ${Object.entries(sets).map(([id, set]) => `
                        <button class="save-set-option" onclick="window.combatUI.saveToSet('${id}')">
                            ${set.icon} ${set.name}
                        </button>
                    `).join('')}
                </div>
                <button class="save-set-cancel" onclick="window.combatUI.closeSaveSetModal()">Cancel</button>
            </div>
        </div>
    `;

    // Remove existing modal if any
    const existing = document.getElementById('save-set-modal');
    if (existing) existing.remove();

    // Add modal to body
    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

/**
 * Save equipment to set
 */
function saveToSet(setId) {
    const result = gameState.saveEquipmentSet(setId);
    if (result.success) {
        logger.game(`Saved equipment to ${result.setName}`);
        closeSaveSetModal();
        renderCombatUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Close save set modal
 */
function closeSaveSetModal() {
    const modal = document.getElementById('save-set-modal');
    if (modal) modal.remove();
}

/**
 * Render combat loot chest section
 * Shows items collected from combat that player can collect to inventory
 */
function renderCombatLootChest() {
    const chest = gameState.getCombatLootChest();
    const slotCount = gameState.getCombatLootSlotCount();
    const capacity = gameState.getCombatLootCapacity();
    const isEmpty = slotCount === 0;

    // Build item slots
    const itemsHtml = Object.entries(chest).map(([itemId, quantity]) => {
        const icon = getItemIcon(itemId);
        const name = getItemName(itemId);
        const item = getItem(itemId);
        const value = item?.value ? formatWithCommas(item.value * quantity) : '0';

        return `
            <div class="loot-chest-slot" onclick="window.combatUI.collectLoot('${itemId}')" data-tooltip="${name}<br>×${formatWithCommas(quantity)}<br>Value: ${value}g">
                <span class="slot-icon">${icon}</span>
                <span class="slot-qty">${quantity >= 1000 ? formatNumber(quantity) : quantity}</span>
            </div>
        `;
    }).join('');

    // Calculate total value
    const totalValue = gameState.getCombatLootValue();

    return `
        <div class="combat-loot-chest ${isEmpty ? 'empty' : ''}">
            <div class="loot-chest-header">
                <span class="loot-chest-title">⚔️ Combat Loot</span>
                <span class="loot-chest-slots">${slotCount}/${capacity} slots</span>
                ${!isEmpty ? `<span class="loot-chest-value">${formatWithCommas(totalValue)}g</span>` : ''}
            </div>
            ${isEmpty ? `
                <div class="loot-chest-empty">
                    <span class="empty-icon">📦</span>
                    <span class="empty-text">No loot collected yet</span>
                </div>
            ` : `
                <div class="loot-chest-grid">
                    ${itemsHtml}
                </div>
                <div class="loot-chest-actions">
                    <button class="collect-all-btn" onclick="window.combatUI.collectAllLoot()">
                        Collect All
                    </button>
                </div>
            `}
        </div>
    `;
}

/**
 * Collect single item from loot chest
 */
function collectLoot(itemId) {
    const chestBefore = gameState.getCombatLootChest()[itemId] || 0;
    const result = gameState.collectCombatLoot(itemId);
    if (result.success) {
        const name = getItemName(itemId);
        toastSuccess(`Collected ${formatWithCommas(result.collected)}x ${name}`);
        // Warn if only partial collection (stack limit hit)
        if (result.collected < chestBefore) {
            toastWarning(`Inventory full! ${formatWithCommas(chestBefore - result.collected)}x ${name} left in chest.`);
        }
        // Re-render to update chest display
        renderCombatUI();
    } else {
        toastWarning(result.reason || 'Inventory full! Make room before collecting.');
    }
}

/**
 * Collect all items from loot chest
 */
function collectAllLoot() {
    const chest = gameState.getCombatLootChest();
    const hadItems = Object.keys(chest).length > 0;
    const itemsBefore = Object.keys(chest).length;

    const result = gameState.collectAllCombatLoot();
    if (result.success && result.collectedItems.length > 0) {
        const totalItems = result.collectedItems.reduce((sum, item) => sum + item.quantity, 0);
        toastSuccess(`Collected ${formatWithCommas(totalItems)} items`);

        // Golden Opportunity: chance for bonus gold when collecting combat loot
        rollGoldenOpportunity(result.collectedItems);

        // Check if some items couldn't be collected (inventory filled up mid-collection)
        const remainingItems = Object.keys(gameState.getCombatLootChest()).length;
        if (remainingItems > 0) {
            toastWarning(`Inventory full! ${remainingItems} item${remainingItems > 1 ? ' types' : ''} left in chest.`);
        }

        // Re-render to update chest display
        renderCombatUI();
    } else if (hadItems && result.collectedItems.length === 0) {
        // Chest had items but nothing was collected - inventory is full
        toastWarning('Inventory full! Make room before collecting loot.');
    } else {
        toastWarning('Loot chest is empty');
    }
}

export function renderCombatUI(params = {}) {
    // Handle tab from params
    if (params.tab) {
        currentTab = params.tab;
    }

    // Guard: if tier4 is selected but all tier 4 zones are hidden, fall back to tier1
    if (currentTab === 'tier4') {
        const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
        const tier4Zones = Object.values(COMBAT_ZONES).filter(z => z.tier === 4 && z.pyreVeilHidden);
        const allHidden = tier4Zones.length > 0 && tier4Zones.every(z => getZoneCorruptionPhase(z, pyreLevel) === 'hidden');
        if (allHidden) {
            currentTab = 'tier1';
        }
    }

    const container = document.getElementById('skill-content');
    if (!container) return;

    // Get loot data for the full-width loot section
    const lootChest = gameState.getCombatLootChest();
    const lootCount = Object.keys(lootChest).length;

    container.innerHTML = `
        <div class="combat-interface combat-v2">
            ${renderCompactHeader()}
            <div class="combat-main">
                <div class="combat-column player-column">
                    ${renderPlayerPanel()}
                </div>
                <div class="combat-column monster-column">
                    ${renderMonsterPanel()}
                </div>
            </div>
            ${renderLootSection(lootChest, lootCount)}
            <div class="combat-zones-section">
                ${renderTabs()}
                <div class="zones-container">
                    ${renderZones()}
                </div>
            </div>
        </div>
    `;

    // Set up global handlers
    window.combatUI = {
        switchTab,
        startFighting,
        stopFighting,
        startFightingMob,
        startFromPreview,
        clearPreview,
        selectTargetMob,
        unequipItem,
        setThreshold: setThreshold,
        storeFood: storeAutoEatFood,
        returnFood: returnAutoEatFood,
        toggleFoodSelection,
        closeFoodSelection,
        selectFood,
        toggleApothecaryBelt,
        togglePotionSelection,
        closePotionSelection,
        selectPotion,
        removeBeltPotion,
        loadSet: loadEquipmentSet,
        showSaveSetModal,
        saveToSet,
        closeSaveSetModal,
        selectSpell: selectSpellHandler,
        openSpellModal,
        openPotionBelt,
        selectBeltPotion,
        removeBeltPotionAndRefresh,
        toggleBeltAndRefresh,
        openGlyphPouchModal,
        addGlyphToPouch,
        removeGlyphAndRefresh,
        showPatrolManagement,
        confirmStopPatrol,
        showPatrolSelect,
        assignPatrol,
        showMobDrops,
        collectLoot,
        collectAllLoot,
        openZoneDetailsModal,
        openTierDetailsModal,
        startFightingBoss,
        upgradeTool: () => {} // Placeholder for future
    };
}

/**
 * Show patrol management modal for a zone (confirm cancel or change pet)
 */
function showPatrolManagement(zoneId) {
    const pet = gameState.getPatrollingPet(zoneId);
    if (!pet) return;

    const petData = getPetData(pet.id);
    const zone = getZone(zoneId);
    const combatPets = gameState.getCombatCapablePets();
    const otherAvailablePets = combatPets.filter(p => !p.patrolZone && p.id !== pet.id);

    // Get current research progress for this zone
    const research = gameState.getMobResearch();
    const zoneMobs = zone.mobs;
    let researchStatus = '';
    if (zoneMobs && zoneMobs.length > 0) {
        const totalProgress = zoneMobs.reduce((sum, mobId) => sum + (research[mobId] || 0), 0);
        const maxProgress = zoneMobs.length * 6; // 6 points for full research per mob
        researchStatus = `<p class="patrol-research-status">Research: ${totalProgress}/${maxProgress} pts</p>`;
    }

    const modal = document.createElement('div');
    modal.className = 'patrol-select-modal';
    modal.innerHTML = `
        <div class="patrol-select-content patrol-manage">
            <div class="patrol-manage-header">
                <h3>Patrol: ${zone?.name || 'Zone'}</h3>
                <button class="patrol-close-btn" onclick="this.closest('.patrol-select-modal').remove();">✕</button>
            </div>
            <div class="patrol-current-pet">
                <span class="pet-icon-large">${petData?.icon || '🐾'}</span>
                <span class="pet-name">${petData?.name || pet.id}</span>
            </div>
            ${researchStatus}
            <p class="patrol-description">This pet researches mobs when completing combat contracts.</p>
            <div class="patrol-actions">
                <button class="patrol-stop-btn" onclick="window.combatUI.confirmStopPatrol('${zoneId}'); this.closest('.patrol-select-modal').remove();">
                    Stop Patrol
                </button>
                ${otherAvailablePets.length > 0 ? `
                    <button class="patrol-change-btn" onclick="window.combatUI.showPatrolSelect('${zoneId}'); this.closest('.patrol-select-modal').remove();">
                        Change Pet
                    </button>
                ` : ''}
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.remove();
        }
    });
}

/**
 * Confirm stop patrol for a zone
 */
function confirmStopPatrol(zoneId) {
    const pet = gameState.getPatrollingPet(zoneId);
    if (pet) {
        gameState.setPetPatrolZone(pet.id, null);
        toastSuccess(`${getPetData(pet.id)?.name || 'Pet'} stopped patrolling`);
        renderCombatUI();
    }
}

/**
 * Show patrol pet selection for a zone
 */
function showPatrolSelect(zoneId) {
    const combatPets = gameState.getCombatCapablePets();
    const availablePets = combatPets.filter(p => !p.patrolZone);

    if (availablePets.length === 0) {
        toastWarning('No available combat pets');
        return;
    }

    if (availablePets.length === 1) {
        // Only one pet, assign directly
        assignPatrol(zoneId, availablePets[0].id);
        return;
    }

    // Multiple pets - show selection dropdown
    const zone = getZone(zoneId);
    const modal = document.createElement('div');
    modal.className = 'patrol-select-modal';
    modal.innerHTML = `
        <div class="patrol-select-content">
            <h3>Assign Patrol to ${zone?.name || 'Zone'}</h3>
            <p class="patrol-description">Patrolling pets research mobs when completing combat contracts, revealing their weaknesses and resistances.</p>
            <div class="patrol-pet-list">
                ${availablePets.map(pet => {
                    const petData = getPetData(pet.id);
                    return `
                        <button class="patrol-pet-btn" onclick="window.combatUI.assignPatrol('${zoneId}', '${pet.id}'); this.closest('.patrol-select-modal').remove();">
                            <span class="pet-icon">${petData?.icon || '🐾'}</span>
                            <span class="pet-name">${petData?.name || pet.id}</span>
                        </button>
                    `;
                }).join('')}
            </div>
            <button class="patrol-cancel-btn" onclick="this.closest('.patrol-select-modal').remove();">Cancel</button>
        </div>
    `;
    document.body.appendChild(modal);
}

/**
 * Assign a pet to patrol a zone
 */
function assignPatrol(zoneId, petId) {
    // Guard: zone already has a pet assigned
    const existingPet = gameState.getPatrollingPet(zoneId);
    if (existingPet) {
        toastWarning('This zone already has a pet patrolling');
        return;
    }

    // First clear any existing patrol for this pet
    const pet = gameState.getPet(petId);
    if (pet?.patrolZone) {
        gameState.setPetPatrolZone(petId, null);
    }

    // Assign new patrol
    gameState.setPetPatrolZone(petId, zoneId);
    const petData = getPetData(petId);
    const zone = getZone(zoneId);
    toastSuccess(`${petData?.name || 'Pet'} now patrolling ${zone?.name || 'zone'}`);
    renderCombatUI();
}

/**
 * Show mob drops modal during combat
 */
function showMobDrops(mobId, zoneId) {
    const mob = getMob(mobId);
    if (!mob) return;

    const lootTable = getMobLoot(mobId, zoneId);
    const hasCommon = lootTable.common && lootTable.common.length > 0;
    const hasRare = lootTable.rare && lootTable.rare.length > 0;

    if (!hasCommon && !hasRare) {
        toastWarning('No drops available for this mob');
        return;
    }

    // Helper to render drop items
    const renderDropItem = (drop) => {
        const itemIcon = getItemIcon(drop.itemId);
        const itemName = getItemName(drop.itemId);
        const fraction = formatDropRate(drop.chance);
        const minQty = drop.minQty || 1;
        const maxQty = drop.maxQty || minQty;
        const qtyText = maxQty > minQty
            ? `${minQty}-${maxQty}`
            : `${minQty}`;
        return `
            <div class="mob-drop-item" data-tooltip="${escapeAttr(buildItemInfoTooltip(drop.itemId))}">
                <span class="drop-icon">${itemIcon}</span>
                <span class="drop-name">${itemName}</span>
                <span class="drop-qty">x${qtyText}</span>
                <span class="drop-chance">${fraction}</span>
            </div>
        `;
    };

    const modal = document.createElement('div');
    modal.className = 'mob-drops-modal';
    modal.innerHTML = `
        <div class="mob-drops-content">
            <div class="mob-drops-header">
                <h3>${mob.name} Drops</h3>
                <button class="mob-drops-close" onclick="this.closest('.mob-drops-modal').remove();">✕</button>
            </div>
            <div class="mob-drops-list">
                ${hasCommon ? `
                    <div class="drop-category-label">Common</div>
                    ${lootTable.common.map(renderDropItem).join('')}
                ` : ''}
                ${hasRare ? `
                    <div class="drop-category-label rare">Rare</div>
                    ${lootTable.rare.map(renderDropItem).join('')}
                ` : ''}
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.remove();
        }
    });
}

/**
 * Update function for tick updates
 */
export function updateCombatUI() {
    if (getCurrentSkillId() !== SKILLS.COMBAT) return;

    const state = combatSkill.getCombatState();

    // Check if pyre level changed - re-render zones if so (for pyre bonus updates)
    const currentPyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
    if (currentPyreLevel !== lastPyreLevel) {
        lastPyreLevel = currentPyreLevel;
        // Re-render zone cards to update pyre bonuses/penalties
        const zonesContainer = document.querySelector('.combat-interface .zone-grid');
        if (zonesContainer) {
            const zones = Object.values(COMBAT_ZONES).filter(z => `tier${z.tier}` === currentTab);
            zonesContainer.innerHTML = zones.map(zone => renderZoneCard(zone)).join('');
        }
    }

    // Check if research changed - update research progress sections live
    const allResearch = gameState.getMobResearch();
    const currentResearchHash = JSON.stringify(allResearch);
    if (currentResearchHash !== lastResearchHash) {
        lastResearchHash = currentResearchHash;
        // Update all visible research sections
        const researchSections = document.querySelectorAll('.zone-research-section');
        researchSections.forEach(section => {
            const zoneCard = section.closest('.zone-card');
            if (zoneCard) {
                const zoneId = zoneCard.dataset.zoneId;
                const zone = getZone(zoneId);
                if (zone) {
                    section.outerHTML = renderResearchProgress(zone);
                }
            }
        });
    }

    // Update header HP bar
    const hpContainer = document.querySelector('.hp-bar-container');
    const hpFill = document.querySelector('.hp-bar-fill');
    const hpText = document.querySelector('.hp-text');
    if (hpFill && hpText && hpContainer) {
        const hpPercent = (state.currentHp / state.maxHp) * 100;
        hpFill.style.width = `${hpPercent}%`;
        hpText.textContent = `${state.currentHp} / ${state.maxHp} HP`;

        // Update HP class
        hpContainer.classList.remove('hp-high', 'hp-mid', 'hp-low');
        if (hpPercent > 50) {
            hpContainer.classList.add('hp-high');
        } else if (hpPercent > 25) {
            hpContainer.classList.add('hp-mid');
        } else {
            hpContainer.classList.add('hp-low');
        }
    }

    // Update XP header values (live XP updates during combat)
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const xpTotalEl = document.querySelector('.combat-header .xp-total .stat-value');
    const xpRateEl = document.querySelector('.combat-header .xp-rate .stat-value');
    const killsRateEl = document.querySelector('.combat-header .kills-rate .stat-value');
    const levelEl = document.querySelector('.combat-header .compact-level');
    const progressFill = document.querySelector('.combat-header .compact-progress-fill');
    const progressPercent = document.querySelector('.combat-header .compact-progress-percent');
    const progressText = document.querySelector('.combat-header .compact-progress-text');

    if (xpTotalEl) {
        xpTotalEl.textContent = formatWithCommas(skill.xp);
    }
    if (xpRateEl) {
        xpRateEl.textContent = formatWithCommas(getXPPerHour(SKILLS.COMBAT));
    }
    if (killsRateEl) {
        killsRateEl.textContent = formatWithCommas(gameState.getKillsPerHour());
    }
    if (levelEl) {
        const virtualLevel = getVirtualLevelFromXP(skill.xp);
        const displayLevel = virtualLevel > 99 ? `${skill.level} (${virtualLevel})` : skill.level;
        levelEl.textContent = `Lv ${displayLevel}`;
    }
    if (progressFill && progressPercent && progressText) {
        updateCompactHeaderProgress('.combat-interface', SKILLS.COMBAT);
    }

    // Update player combat bars (now in monster panel - v3 layout)
    const playerSide = document.querySelector('.player-side');
    const playerHpBar = playerSide ? playerSide.querySelector('.combat-hp-bar') : null;
    const playerHpFill = playerSide ? playerSide.querySelector('.hp-fill') : null;
    const playerHpText = playerSide ? playerSide.querySelector('.hp-text') : null;
    if (playerHpFill && playerHpText) {
        const playerHpPercent = (state.currentHp / state.maxHp) * 100;
        playerHpFill.style.width = `${playerHpPercent}%`;
        playerHpText.textContent = `${state.currentHp} / ${state.maxHp}`;

        if (playerHpBar) {
            playerHpBar.classList.remove('hp-high', 'hp-mid', 'hp-low');
            if (playerHpPercent > 50) {
                playerHpBar.classList.add('hp-high');
            } else if (playerHpPercent > 25) {
                playerHpBar.classList.add('hp-mid');
            } else {
                playerHpBar.classList.add('hp-low');
            }
        }
    }

    // Player attack speed bar (now in monster panel)
    const playerAttackFill = playerSide ? playerSide.querySelector('.combat-attack-bar .attack-fill') : null;
    if (playerAttackFill && state.inCombat) {
        playerAttackFill.style.width = `${(state.playerAttackProgress || 0) * 100}%`;
    } else if (playerAttackFill) {
        playerAttackFill.style.width = '0%';
    }

    // Player effects (inline below HP bar) - only update if changed to preserve tooltips
    const playerEffectsInline = document.querySelector('.player-side .effects-inline');
    if (playerEffectsInline) {
        const potionBuffs = gameState.getPotionBuffs();
        const buffsHtml = renderPlayerBuffsCompact(potionBuffs);
        const debuffsHtml = renderPlayerDebuffsCompact(state);
        const newContent = (buffsHtml || '') + (debuffsHtml || '');
        if (playerEffectsInline.innerHTML !== newContent) {
            playerEffectsInline.innerHTML = newContent;
        }
    }

    // Update monster combat bars (in v3 layout)
    if (state.inCombat && state.currentMob) {
        const mob = typeof state.currentMob === 'string' ? getMob(state.currentMob) : state.currentMob;

        // Monster HP bar (now in mob-side section)
        const mobSide = document.querySelector('.mob-side');
        const mobHpBar = mobSide ? mobSide.querySelector('.combat-hp-bar') : null;
        const mobHpFill = mobSide ? mobSide.querySelector('.hp-fill') : null;
        const mobHpText = mobSide ? mobSide.querySelector('.hp-text') : null;
        if (mobHpFill && mobHpText) {
            const mobHpPercent = (state.mobCurrentHp / state.mobMaxHp) * 100;
            mobHpFill.style.width = `${mobHpPercent}%`;
            mobHpText.textContent = `${state.mobCurrentHp} / ${state.mobMaxHp}`;

            // Update HP bar class (reversed for enemy - green when low means almost dead)
            if (mobHpBar) {
                mobHpBar.classList.remove('hp-high', 'hp-mid', 'hp-low');
                if (mobHpPercent > 50) {
                    mobHpBar.classList.add('hp-high');
                } else if (mobHpPercent > 25) {
                    mobHpBar.classList.add('hp-mid');
                } else {
                    mobHpBar.classList.add('hp-low');
                }
            }
        }

        // Monster attack progress bar (now in mob-side section)
        const mobAttackFill = mobSide ? mobSide.querySelector('.combat-attack-bar .attack-fill') : null;
        if (mobAttackFill) {
            mobAttackFill.style.width = `${(state.mobAttackProgress || 0) * 100}%`;
        }

        // Monster effects (inline below HP bar) - only update if changed to preserve tooltips
        const mobEffectsInline = document.querySelector('.mob-side .effects-inline');
        if (mobEffectsInline) {
            const mobDebuffs = buildMobDebuffsFromSpellEffects(state.spellEffects);
            const mobBuffs = state.mobBuffs || [];
            const debuffsHtml = renderMonsterDebuffsCompact(mobDebuffs);
            const buffsHtml = renderMonsterBuffsCompact(mobBuffs);
            const newContent = (buffsHtml || '') + (debuffsHtml || '');
            if (mobEffectsInline.innerHTML !== newContent) {
                mobEffectsInline.innerHTML = newContent;
            }
        }

        // Update boss indicators (phase, enrage timer, active effects)
        const bossIndicators = document.querySelector('.mob-side .boss-indicators');
        if (bossIndicators && state.bossState) {
            const bs = state.bossState;

            // Update enrage timer text directly (without re-rendering entire structure)
            const enrageTimer = bossIndicators.querySelector('.boss-enrage-timer');
            if (enrageTimer && bs.mechanics?.enrage && bs.enrageStage < 0) {
                const elapsed = Date.now() - bs.fightStartTime;
                const firstStage = bs.mechanics.enrage.stages[0];
                const remaining = Math.max(0, firstStage.timer - elapsed);
                const secs = Math.ceil(remaining / 1000);
                enrageTimer.textContent = `Enrage: ${secs}s`;
            }

            // Check if structure changed (phase, enrage stage, active effects count)
            const currentPhase = bossIndicators.querySelector('.boss-phase')?.textContent || '';
            const newPhase = bs.currentPhase ? `${bs.currentPhase.name} (${bs.mechanics.phases.findIndex(p => p.id === bs.currentPhase.id) + 1}/${bs.mechanics.phases.length})` : '';
            const hasEnraged = !!bossIndicators.querySelector('.boss-enraged');
            const shouldBeEnraged = bs.enrageStage >= 0;

            if (currentPhase !== newPhase || hasEnraged !== shouldBeEnraged) {
                const newIndicators = renderBossIndicators(state).replace(/<\/?div[^>]*>/g, '');
                bossIndicators.innerHTML = newIndicators;
            }
        } else if (!state.bossState && bossIndicators) {
            bossIndicators.remove();
        }

        // Update mob attack speed text (reflects slow debuff)
        const mobAttackSpeedText = document.querySelector('.mob-side .attack-speed-text');
        if (mobAttackSpeedText) {
            mobAttackSpeedText.textContent = `${(state.mobEffectiveAttackSpeed / 1000).toFixed(1)}s`;
            // Cyan/blue color when slowed (matches ice theme)
            if (state.spellEffects.slow) {
                mobAttackSpeedText.style.color = '#87ceeb';
            } else {
                mobAttackSpeedText.style.color = '';
            }
        }

        // Update player defense stat (reflects defense buff)
        const playerDefenseStat = document.querySelector('.player-side .combatant-stats span[data-tooltip="Defense"]');
        if (playerDefenseStat) {
            const defenseBonus = state.spellEffects.defense?.bonus || 0;
            const totalDefense = state.playerStats.defense + defenseBonus;
            playerDefenseStat.textContent = `🛡 ${totalDefense}`;
            // Green color when buffed
            if (defenseBonus > 0) {
                playerDefenseStat.style.color = '#4ade80';
            } else {
                playerDefenseStat.style.color = '';
            }
        }

        // Update buff/debuff timers and visibility
        const effects = state.spellEffects;

        // Spell buffs (defense, crit, hot) - in separate container from potion buffs
        const spellBuffsContainer = document.querySelector('.player-effects .spell-buffs');
        if (spellBuffsContainer) {
            // Defense buff
            const defenseBuff = spellBuffsContainer.querySelector('.buff-indicator.defense');
            if (effects.defense) {
                if (!defenseBuff) {
                    // Re-render only spell buffs container
                    spellBuffsContainer.innerHTML = renderSpellBuffsContent();
                } else {
                    const timer = defenseBuff.querySelector('.buff-timer');
                    if (timer) timer.textContent = `${Math.ceil(effects.defense.remaining / 1000)}s`;
                }
            } else if (defenseBuff) {
                defenseBuff.remove();
            }

            // Crit buff
            const critBuff = spellBuffsContainer.querySelector('.buff-indicator.crit');
            if (effects.crit) {
                if (!critBuff) {
                    spellBuffsContainer.innerHTML = renderSpellBuffsContent();
                } else {
                    const timer = critBuff.querySelector('.buff-timer');
                    if (timer) timer.textContent = `${Math.ceil(effects.crit.remaining / 1000)}s`;
                }
            } else if (critBuff) {
                critBuff.remove();
            }

            // HoT buff (from empowered food)
            const hotBuff = spellBuffsContainer.querySelector('.buff-indicator.hot');
            if (effects.hot) {
                if (!hotBuff) {
                    spellBuffsContainer.innerHTML = renderSpellBuffsContent();
                } else {
                    const timer = hotBuff.querySelector('.buff-timer');
                    if (timer) timer.textContent = `${Math.ceil(effects.hot.remaining / 1000)}s`;
                }
            } else if (hotBuff) {
                hotBuff.remove();
            }
        }

        // Potion buffs - in separate container, only re-render when count changes
        const potionBuffsContainer = document.querySelector('.player-effects .potion-buffs');
        if (potionBuffsContainer) {
            const potionBuffs = combatSkill.getPotionBuffsState();
            const existingPotionBuffs = potionBuffsContainer.querySelectorAll('.buff-indicator.potion');

            // Check if we need to re-render (buff count changed)
            if (potionBuffs.length !== existingPotionBuffs.length) {
                potionBuffsContainer.innerHTML = renderPotionBuffsContent();
            } else {
                // Update existing timers only
                for (const buff of potionBuffs) {
                    const buffEl = potionBuffsContainer.querySelector(`.buff-indicator.potion.${buff.category}`);
                    if (buffEl) {
                        const timer = buffEl.querySelector('.buff-timer');
                        if (timer) timer.textContent = `${Math.ceil(buff.combatTimeRemaining / 1000)}s`;
                    }
                }
            }
        }

        // Set bonus buffs - re-render each tick (lightweight, state changes often)
        const setBuffsContainer = document.querySelector('.player-effects .set-buffs');
        if (setBuffsContainer) {
            setBuffsContainer.innerHTML = renderSetBonusBuffsContent();
        }

        // Mob debuffs (now in side column)
        const mobDebuffsBar = document.querySelector('.mob-effects .mob-debuffs');
        if (mobDebuffsBar) {
            // DoT debuff
            const dotDebuff = mobDebuffsBar.querySelector('.debuff-indicator.dot');
            if (effects.dot) {
                if (!dotDebuff) {
                    mobDebuffsBar.innerHTML = renderMobDebuffsContent();
                } else {
                    const timer = dotDebuff.querySelector('.debuff-timer');
                    if (timer) timer.textContent = `${Math.ceil(effects.dot.remaining / 1000)}s`;
                }
            } else if (dotDebuff) {
                dotDebuff.remove();
            }

            // Slow debuff
            const slowDebuff = mobDebuffsBar.querySelector('.debuff-indicator.slow');
            if (effects.slow) {
                if (!slowDebuff) {
                    mobDebuffsBar.innerHTML = renderMobDebuffsContent();
                } else {
                    const timer = slowDebuff.querySelector('.debuff-timer');
                    if (timer) timer.textContent = `${Math.ceil(effects.slow.remaining / 1000)}s`;
                }
            } else if (slowDebuff) {
                slowDebuff.remove();
            }

            // Corruption debuff
            const corruptionDebuff = mobDebuffsBar.querySelector('.debuff-indicator.corruption');
            const markDebuff = mobDebuffsBar.querySelector('.debuff-indicator.mark');
            const isCorrupted = isSkillCorrupted(SKILLS.COMBAT);
            const hasMarks = combatSkill.corruptedPrey?.marked && !isCorrupted;

            if (isCorrupted) {
                if (!corruptionDebuff) {
                    mobDebuffsBar.innerHTML = renderMobDebuffsContent();
                } else {
                    const info = getSkillCorruptionInfo(SKILLS.COMBAT);
                    const remaining = Math.ceil(info.remainingMs / 1000);
                    const timeStr = remaining >= 60
                        ? `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, '0')}`
                        : `${remaining}s`;
                    const timer = corruptionDebuff.querySelector('.debuff-timer');
                    if (timer) timer.textContent = timeStr;
                }
                // Remove mark indicator if corruption is active (mark shows only after timer expires)
                if (markDebuff) markDebuff.remove();
            } else {
                if (corruptionDebuff) corruptionDebuff.remove();
                // Show persistent mark if timer expired but mob is marked
                if (hasMarks && !markDebuff) {
                    mobDebuffsBar.innerHTML = renderMobDebuffsContent();
                } else if (!hasMarks && markDebuff) {
                    markDebuff.remove();
                }
            }
        }
    }

    // Update respawn timer if dead
    if (state.isDead) {
        const respawnTimer = document.querySelector('.respawn-timer');
        const respawnBar = document.querySelector('.respawn-bar');
        if (respawnTimer) {
            respawnTimer.textContent = Math.ceil(state.respawnRemaining / 1000);
        }
        if (respawnBar) {
            respawnBar.style.width = `${100 - (state.respawnRemaining / COMBAT_CONFIG.RESPAWN_TIME * 100)}%`;
        }
    }

    // Update kills/h
    const killsRateStat = document.querySelector('.compact-stat.kills-rate .stat-value');
    if (killsRateStat) {
        killsRateStat.textContent = formatWithCommas(gameState.getKillsPerHour());
    }

    // Update XP/hr
    const xpPerHour = getXPPerHour(SKILLS.COMBAT);
    const xpRateStat = document.querySelector('.compact-stat.xp-rate .stat-value');
    if (xpRateStat) {
        xpRateStat.textContent = formatWithCommas(xpPerHour);
    }

    // Update auto-eat food quantity
    const autoEat = gameState.getAutoEat();
    const foodQtyEl = document.querySelector('.auto-eat-food .food-qty');
    if (foodQtyEl) {
        if (autoEat.storedFood) {
            foodQtyEl.textContent = `×${autoEat.storedFood.quantity}`;
        } else {
            foodQtyEl.textContent = 'Empty';
        }
    }

    // Update ammo quantity in equipment panel (both v1 and v2 grids)
    const ammoSlots = document.querySelectorAll('.equip-slot.ammo .ammo-qty, .equip-slot-v2.ammo-slot .ammo-qty');
    if (ammoSlots.length > 0) {
        const ammo = gameState.getEquippedAmmo();
        const qtyDisplay = (ammo.itemId && ammo.quantity > 0)
            ? (ammo.quantity >= 1000 ? formatNumber(ammo.quantity) : ammo.quantity)
            : '0';
        ammoSlots.forEach(slot => slot.textContent = qtyDisplay);
    }

    // Update potion belt slot quantities
    const potionSlotEls = document.querySelectorAll('.potion-section .potion-slot.filled');
    if (potionSlotEls.length > 0) {
        const beltSlots = gameState.getApothecarySlots();
        potionSlotEls.forEach((el, i) => {
            const slotIndex = parseInt(el.getAttribute('onclick')?.match(/openPotionBelt\((\d+)\)/)?.[1]);
            if (slotIndex !== undefined && !isNaN(slotIndex)) {
                const slot = beltSlots[slotIndex];
                const qtyEl = el.querySelector('.consumable-qty');
                if (qtyEl && slot) {
                    qtyEl.textContent = slot.quantity;
                }
            }
        });
    }

    // Update out of ammo warning visibility
    if (state.inCombat) {
        const stats = combatSkill.getPlayerStats();
        const ammo = gameState.getEquippedAmmo();
        const builtInAmmo = combatSkill.getBuiltInAmmo();
        const isRanged = stats.attackStyle === 'ranged';
        const hasAmmo = (ammo.itemId && ammo.quantity > 0) || builtInAmmo;
        const outOfAmmo = isRanged && !hasAmmo;

        const activeCombat = document.querySelector('.active-combat');
        let warningEl = document.querySelector('.out-of-ammo-warning');

        if (outOfAmmo && activeCombat) {
            if (!warningEl) {
                // Add warning if it doesn't exist
                warningEl = document.createElement('div');
                warningEl.className = 'combat-warning out-of-ammo-warning';
                warningEl.textContent = '⚠️ Out of ammo! Equip arrows to attack.';
                activeCombat.insertBefore(warningEl, activeCombat.firstChild);
            }
        } else if (warningEl) {
            // Remove warning if ammo is available
            warningEl.remove();
        }
    }

    // Check if equipment changed and re-render if needed
    const gear = gameState.getEquippedGear();
    const activeSet = gameState.getActiveEquipmentSet();
    const currentHash = JSON.stringify({ gear, activeSet });

    if (currentHash !== lastEquipmentHash) {
        lastEquipmentHash = currentHash;
        // Equipment changed - re-render the combat UI
        renderCombatUI();
        return; // Skip glyph check since we just re-rendered
    }

    // Check if glyph availability changed (for magic style spell status)
    if (activeSet === 'magic') {
        const selectedSpell = gameState.getSelectedSpell();
        if (selectedSpell) {
            const pouch = gameState.getGlyphPouch();
            const spell = getAllSpells().find(s => s.id === selectedSpell);
            // Build hash from glyph pouch + relevant inventory glyphs
            const glyphIds = spell?.glyphCost?.map(c => c.glyphId) || [];
            const inventoryGlyphs = glyphIds.map(id => ({ id, qty: gameState.getItemCount(id) }));
            const glyphHash = JSON.stringify({ pouch: pouch.slots, inv: inventoryGlyphs });

            if (glyphHash !== lastGlyphHash) {
                lastGlyphHash = glyphHash;
                // Glyph availability changed - re-render spell status
                const magicWrapper = document.querySelector('.magic-wrapper');
                if (magicWrapper) {
                    magicWrapper.outerHTML = renderMagicWrapper(true);
                }
            }
        }
    }
}

/**
 * Initialize combat UI
 */
export function initCombatUI() {
    // Initialize pyre level tracking
    lastPyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;

    // Set up callbacks
    combatSkill.onCombatUpdate = () => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                updateCombatUI();
            }
        }
    };

    combatSkill.onMobKilled = (mob, xpResult, loot) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Combat level ${xpResult.newLevel}!`);
        }
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            // Re-render active combat area to show new mob
            const activeCombat = document.querySelector('.active-combat');
            if (activeCombat) {
                activeCombat.outerHTML = renderActiveCombat();
            }

            // Update zone card clear progress
            const state = combatSkill.getCombatState();
            if (state.currentZone) {
                const zoneCard = document.querySelector(`.zone-card[data-zone-id="${state.currentZone}"]`);
                if (zoneCard) {
                    const zone = getZone(state.currentZone);
                    const zoneProgress = gameState.getZoneProgress(state.currentZone);
                    const lifetimeKills = zoneProgress.lifetimeKills;
                    const clearProgress = Math.min(lifetimeKills / zone.clearThreshold, 1);

                    // Update progress bar
                    const progressFill = zoneCard.querySelector('.clear-progress-fill');
                    const progressText = zoneCard.querySelector('.clear-progress-text');
                    if (progressFill) {
                        progressFill.style.width = `${clearProgress * 100}%`;
                    }
                    if (progressText) {
                        progressText.textContent = `${lifetimeKills}/${zone.clearThreshold} to clear`;
                    }

                    // Update kill badge
                    let killBadge = zoneCard.querySelector('.zone-kill-badge');
                    const killDisplay = lifetimeKills >= 1000 ? formatNumber(lifetimeKills) : lifetimeKills;
                    if (killBadge) {
                        killBadge.textContent = `${killDisplay}x`;
                        killBadge.dataset.tooltip = `${formatWithCommas(lifetimeKills)} lifetime kills`;
                    } else if (lifetimeKills > 0) {
                        // Create kill badge if it doesn't exist
                        const badge = document.createElement('div');
                        badge.className = 'zone-kill-badge';
                        badge.dataset.tooltip = `${formatWithCommas(lifetimeKills)} lifetime kills`;
                        badge.textContent = `${killDisplay}x`;
                        zoneCard.insertBefore(badge, zoneCard.firstChild);
                    }

                    // Update target farming kill counts
                    const targetButtons = zoneCard.querySelectorAll('.target-btn');
                    targetButtons.forEach(btn => {
                        const mobId = btn.dataset.mobId;
                        if (mobId) {
                            const mobKills = gameState.getKillCount(mobId);
                            const mobKillText = mobKills >= 1000 ? formatNumber(mobKills) : mobKills;
                            const killsSpan = btn.querySelector('.target-kills');
                            if (killsSpan) {
                                killsSpan.textContent = `💀${mobKillText}`;
                            }
                            // Update tooltip with full number
                            const mobData = getMob(mobId);
                            btn.dataset.tooltip = `${mobData?.name || mobId}<br><span class='tooltip-stat-value'>${formatWithCommas(mobKills)}</span> kills`;
                        }
                    });
                }
            }

            // Update loot section UI when loot is added
            const lootSection = document.querySelector('.combat-loot-section');
            if (lootSection) {
                const lootChest = gameState.getCombatLootChest();
                const lootCount = Object.keys(lootChest).length;
                lootSection.outerHTML = renderLootSection(lootChest, lootCount);
            }
        }
    };

    combatSkill.onPlayerDeath = () => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            renderCombatUI();
        }
    };

    combatSkill.onRespawn = () => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            renderCombatUI();
        }
    };

    combatSkill.onZoneCleared = (zone) => {
        toastSuccess(`Zone cleared: ${zone.name}! You can now target specific mobs.`);
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            renderCombatUI();
        }
    };

    // Damage number callbacks
    combatSkill.onDamageDealt = (damage, type) => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            showDamageNumber(damage, 'mob', type);
        }
    };

    combatSkill.onDamageTaken = (damage, type) => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen()) {
            showDamageNumber(damage, 'player', type);
        }
    };

    combatSkill.onHeal = (amount) => {
        if (getCurrentSkillId() === SKILLS.COMBAT && !isTalentTreeOpen() && amount > 0) {
            showDamageNumber(amount, 'player', 'heal');
        }
    };
}

/**
 * Switch tier tab
 */
function switchTab(tab) {
    currentTab = tab;
    // Save tab preference for sidebar navigation
    localStorage.setItem('idleRealms_tab_combat', tab);
    // Update URL to persist tab selection
    router.navigate(`#/combat/${tab}`, true);
}

/**
 * Start fighting in a zone (optionally in a specific tier for tiered zones)
 */
function startFighting(zoneId, tier = null) {
    // Select zone for preview instead of starting immediately
    const result = combatSkill.selectZone(zoneId, tier);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderCombatUI();
}

/**
 * Stop fighting
 */
function stopFighting() {
    combatSkill.stopFighting();
    renderCombatUI();
}

/**
 * Start fighting a specific mob (target farming)
 */
function startFightingMob(zoneId, mobId) {
    // Select specific mob for preview instead of starting immediately
    const result = combatSkill.selectMob(zoneId, mobId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderCombatUI();
}

/**
 * Start fighting a specific boss in a tiered zone (e.g., Ashen Crucible)
 * Bypasses the "zone cleared" requirement since bosses are the main content
 */
function startFightingBoss(zoneId, tierId, mobId) {
    // For tiered zones, we start fighting with the specific tier and target mob
    const result = combatSkill.selectBoss(zoneId, tierId, mobId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderCombatUI();
}

/**
 * Start combat from preview state
 */
function startFromPreview() {
    const result = combatSkill.startFromPreview();
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderCombatUI();
}

/**
 * Clear preview state
 */
function clearPreview() {
    combatSkill.clearPreview();
    renderCombatUI();
}

/**
 * Select a specific mob for target farming from the preview panel
 */
function selectTargetMob(zoneId, mobId) {
    const result = combatSkill.selectMob(zoneId, mobId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderCombatUI();
}

/**
 * Unequip an item from a slot (called from combat equipment panel)
 * @param {string} slot - The slot to unequip
 */
function unequipItem(slot) {
    const result = gameState.unequipItem(slot);
    if (result.success) {
        const item = getItem(result.itemId);
        const name = item ? item.name : result.itemId;
        toastSuccess(`Unequipped ${name}`);
        renderCombatUI();
    } else {
        toastWarning(result.reason || 'Could not unequip item');
    }
}

/**
 * Handle spell selection from UI
 * @param {string} spellId - The spell ID to select
 */
function selectSpellHandler(spellId) {
    const result = combatSkill.selectSpell(spellId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    // Close modal if open
    const modal = document.querySelector('.spell-modal');
    if (modal) modal.remove();
    renderCombatUI();
}

/**
 * Open spell selection modal
 */
function openSpellModal() {
    // Remove existing modal
    const existing = document.querySelector('.spell-modal');
    if (existing) {
        existing.remove();
        return;
    }

    const combatLevel = gameState.getSkill(SKILLS.COMBAT).level;
    const allSpells = getAllSpells();
    const currentSpell = gameState.getSelectedSpell();

    // Element order: Air, Ice, Earth, Fire
    const elementOrder = ['air', 'ice', 'earth', 'fire'];
    const elementIcons = {
        air: '💨',
        ice: '❄️',
        earth: '🪨',
        fire: '🔥'
    };

    // Group spells by element, sorted by tier
    const spellsByElement = {};
    for (const element of elementOrder) {
        spellsByElement[element] = allSpells
            .filter(s => s.element === element)
            .sort((a, b) => a.tier - b.tier);
    }

    // Build column HTML for each element
    const columnsHtml = elementOrder.map(element => {
        const spells = spellsByElement[element] || [];
        const elementIcon = elementIcons[element];

        const spellsHtml = spells.map(spell => {
            const isLocked = spell.level > combatLevel;
            const isSelected = spell.id === currentSpell;

            // Build glyph cost display
            let glyphCostHtml = '';
            let hasGlyphs = true;
            const glyphCosts = spell.glyphCost || [];

            for (const cost of glyphCosts) {
                const totalCount = gameState.getTotalGlyphCount(cost.glyphId);
                const hasCost = totalCount >= cost.quantity;
                if (!hasCost) hasGlyphs = false;
                const glyphIcon = getItemIcon(cost.glyphId);
                glyphCostHtml += `<span class="spell-glyph-cost ${hasCost ? '' : 'missing'}">${glyphIcon}<span class="glyph-qty">${cost.quantity}</span></span>`;
            }

            // Build effect text for display
            const effectText = spell.effect ? formatSpellEffect(spell.effect) : '';
            const glyphText = glyphCosts.map(c => `${getItemName(c.glyphId)} x${c.quantity}`).join(', ');

            return `
                <div class="spell-card ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''} ${!hasGlyphs && !isLocked ? 'no-glyphs' : ''}"
                     onclick="${isLocked ? '' : `window.combatUI.selectSpell('${spell.id}')`}"
                     data-tooltip="${spell.description}${glyphText ? ' | Cost: ' + glyphText : ''}">
                    <div class="spell-card-icon">${elementIcon}</div>
                    <div class="spell-card-name">${spell.name}</div>
                    <div class="spell-card-dmg">${spell.baseDamage} dmg</div>
                    ${effectText ? `<div class="spell-card-effect">${effectText}</div>` : ''}
                    <div class="spell-card-costs">${glyphCostHtml || '<span class="no-cost">Free</span>'}</div>
                    ${isLocked ? `<div class="spell-card-lock">🔒 Lv ${spell.level}</div>` : ''}
                </div>
            `;
        }).join('');

        return `
            <div class="spell-element-column">
                ${spellsHtml || '<div class="no-spells">No spells</div>'}
            </div>
        `;
    }).join('');

    const modal = document.createElement('div');
    modal.className = 'spell-modal';
    modal.innerHTML = `
        <div class="spell-modal-content spell-modal-wide">
            <div class="spell-modal-header">
                <span>Select Spell</span>
                <button class="close-modal-btn" onclick="document.querySelector('.spell-modal').remove()">×</button>
            </div>
            <div class="spell-modal-columns">
                ${columnsHtml}
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Close on click outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Open potion belt management modal (4 slots per equipment set)
 * @param {number|null} expandedSlot - Slot to expand, null for none
 * @param {boolean} forceRefresh - If true, refresh modal instead of toggle off
 */
function openPotionBelt(expandedSlot = null, forceRefresh = false) {
    // Remove existing modal
    const existing = document.querySelector('.potion-belt-modal');
    if (existing) {
        existing.remove();
        // Only toggle off if not forcing refresh and no slot specified
        if (expandedSlot === null && !forceRefresh) return;
    }

    const belt = gameState.getApothecaryBelt();
    const slots = gameState.getApothecarySlots();
    const activeSet = gameState.getActiveEquipmentSet() || 'melee';

    // Get all potions from inventory
    const allPotions = getAllPotionsFromInventory();

    const slotsHtml = [0, 1, 2, 3].map(slotIndex => {
        const slot = slots[slotIndex];
        const isExpanded = expandedSlot === slotIndex;

        if (slot && slot.quantity > 0) {
            // Filled slot - show potion with remove button
            const potionIcon = getItemIcon(slot.itemId);
            const potionName = getItemName(slot.itemId);
            const potion = getItem(slot.itemId);
            const effectText = potion?.effects ? formatPotionEffect(potion.effects) : '';

            return `
                <div class="belt-slot filled" data-slot="${slotIndex}">
                    <div class="belt-slot-header">
                        <span class="slot-number">Slot ${slotIndex + 1}:</span>
                        <button class="slot-remove" onclick="event.stopPropagation(); window.combatUI.removeBeltPotionAndRefresh(${slotIndex})" data-tooltip="Return to inventory">×</button>
                    </div>
                    <div class="slot-content">
                        <span class="slot-potion">${potionIcon}</span>
                        <div class="slot-potion-info">
                            <span class="slot-potion-name">${potionName}</span>
                            <span class="slot-potion-effect">${effectText}</span>
                        </div>
                        <span class="slot-qty">×${slot.quantity}</span>
                    </div>
                </div>
            `;
        } else {
            // Build map of categories already on belt
            const beltCategories = {};
            slots.forEach((s, i) => {
                if (s && s.quantity > 0 && i !== slotIndex) {
                    const p = getItem(s.itemId);
                    if (p?.effects?.category) beltCategories[p.effects.category] = p;
                }
            });

            // Empty slot - show available potions
            const potionOptionsHtml = allPotions.length > 0
                ? allPotions.map(potion => {
                    const cat = potion.effects.category;
                    const existing = beltCategories[cat];
                    let dupNote = '';
                    if (existing) {
                        dupNote = `<span class="option-dup-note">⚠ ${existing.name} already on belt</span>`;
                    }
                    return `
                    <div class="belt-potion-option" onclick="window.combatUI.selectBeltPotion(${slotIndex}, '${potion.itemId}')">
                        <span class="option-icon">${potion.icon}</span>
                        <span class="option-name">${potion.name}</span>
                        <span class="option-effect">${formatPotionEffect(potion.effects)}</span>
                        <span class="option-qty">×${potion.quantity}</span>
                        ${dupNote}
                    </div>
                `}).join('')
                : `<div class="belt-no-potions">No potions in inventory</div>`;

            return `
                <div class="belt-slot empty ${isExpanded ? 'expanded' : ''}" data-slot="${slotIndex}">
                    <div class="belt-slot-header" onclick="window.combatUI.openPotionBelt(${isExpanded ? 'null' : slotIndex})">
                        <span class="slot-number">Slot ${slotIndex + 1}:</span>
                        <span class="slot-empty-text">Empty</span>
                        <span class="slot-arrow">${isExpanded ? '▼' : '▶'}</span>
                    </div>
                    ${isExpanded ? `
                        <div class="belt-potion-options">
                            ${potionOptionsHtml}
                        </div>
                    ` : ''}
                </div>
            `;
        }
    }).join('');

    const modal = document.createElement('div');
    modal.className = 'potion-belt-modal';
    modal.innerHTML = `
        <div class="potion-belt-modal-content">
            <div class="potion-belt-modal-header">
                <span>🧪 Potion Belt (${activeSet.charAt(0).toUpperCase() + activeSet.slice(1)})</span>
                <div class="belt-toggle">
                    <span>Auto-use:</span>
                    <button class="belt-toggle-btn ${belt.enabled ? 'on' : 'off'}"
                            onclick="window.combatUI.toggleBeltAndRefresh()">
                        ${belt.enabled ? 'ON' : 'OFF'}
                    </button>
                </div>
                <button class="close-modal-btn" onclick="document.querySelector('.potion-belt-modal').remove()">×</button>
            </div>
            <div class="potion-belt-modal-body">
                <div class="belt-info">Each equipment set has its own potion belt. Auto-use drinks when combat starts and when buffs expire.</div>
                <div class="belt-slots-grid">
                    ${slotsHtml}
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Close on click outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Get all potions from inventory
 */
function getAllPotionsFromInventory() {
    const slots = gameState.getInventorySlots();
    const potions = [];

    for (const slot of slots) {
        if (!slot) continue;
        const item = getItem(slot.itemId);
        if (item && item.effects && item.effects.category && item.effects.duration) {
            potions.push({
                itemId: slot.itemId,
                name: item.name,
                icon: getItemIcon(slot.itemId),
                effects: item.effects,
                quantity: slot.quantity
            });
        }
    }

    return potions;
}

/**
 * Select potion for belt slot from modal and refresh
 */
function selectBeltPotion(slotIndex, itemId) {
    const playerQty = gameState.getItemCount(itemId);
    if (playerQty <= 0) {
        toastWarning('No potions in inventory');
        return;
    }

    const result = gameState.storeApothecaryPotion(slotIndex, itemId, playerQty);
    if (result.success) {
        const itemName = getItemName(itemId);
        toastSuccess(`Stored ${playerQty}x ${itemName} in belt`);
    } else {
        toastWarning(result.reason || 'Could not store potion');
    }
    // Refresh modal by reopening (forceRefresh to not toggle off)
    setTimeout(() => openPotionBelt(null, true), 10);
    renderCombatUI(); // Update player panel
}

/**
 * Remove potion from belt slot and refresh modal
 */
function removeBeltPotionAndRefresh(slotIndex) {
    const slots = gameState.getApothecarySlots();
    const slot = slots[slotIndex];
    if (!slot) return;

    const itemId = slot.itemId;
    const quantity = slot.quantity;

    const result = gameState.removeApothecaryPotion(slotIndex);
    if (result.success) {
        const itemName = getItemName(itemId);
        toastSuccess(`Returned ${quantity}x ${itemName} to inventory`);
    } else if (result.reason) {
        toastWarning(result.reason);
    }
    // Refresh modal by reopening (forceRefresh to not toggle off)
    setTimeout(() => openPotionBelt(null, true), 10);
    renderCombatUI(); // Update player panel
}

/**
 * Toggle belt enabled and refresh modal
 */
function toggleBeltAndRefresh() {
    gameState.toggleApothecaryBelt();
    // Refresh modal by reopening (forceRefresh to not toggle off)
    setTimeout(() => openPotionBelt(null, true), 10);
    renderCombatUI(); // Update player panel
}

/**
 * Open glyph pouch management modal
 */
export function openGlyphPouchModal() {
    // Remove existing modal (toggle off)
    const existing = document.querySelector('.glyph-pouch-modal');
    if (existing) {
        existing.remove();
        return;
    }

    const pouch = gameState.getGlyphPouch();
    const slots = pouch.slots;

    // Get all glyphs from inventory grouped by category
    const inventoryGlyphs = getAllGlyphsFromInventory();

    // Build pouch slots display (top section)
    const pouchSlotsHtml = slots.map((slot, slotIndex) => {
        if (slot.itemId && slot.quantity > 0) {
            const glyphIcon = getItemIcon(slot.itemId);
            const glyphName = getItemName(slot.itemId);
            const qtyDisplay = slot.quantity >= 1000 ? formatNumber(slot.quantity) : slot.quantity;
            return `
                <div class="pouch-slot filled" onclick="window.combatUI.removeGlyphAndRefresh(${slotIndex})" data-tooltip="${glyphName}\n×${formatWithCommas(slot.quantity)}\nClick to remove">
                    <span class="pouch-slot-icon">${glyphIcon}</span>
                    <span class="pouch-slot-qty">${qtyDisplay}</span>
                </div>
            `;
        } else {
            return `
                <div class="pouch-slot empty" data-tooltip="Empty slot">
                    <span class="pouch-slot-empty">◇</span>
                </div>
            `;
        }
    }).join('');

    // Glyph categories
    const glyphCategories = [
        { name: 'Elemental', ids: ['storm_glyph', 'frost_glyph', 'stone_glyph', 'flame_glyph'] },
        { name: 'Aspect', ids: ['vigor_glyph'] },
        { name: 'Primal', ids: ['verdant_glyph', 'entropy_glyph'] },
        { name: 'Cosmic', ids: ['void_glyph', 'binding_glyph'] },
        { name: 'Forbidden', ids: ['doom_glyph', 'sanguine_glyph', 'spirit_glyph'] }
    ];

    // Build inventory glyphs grid (bottom section)
    let inventoryHtml = '';
    for (const category of glyphCategories) {
        const categoryGlyphs = category.ids.map(id => {
            const invGlyph = inventoryGlyphs.find(g => g.itemId === id);
            const item = getItem(id);
            if (!item) return '';

            const hasInInventory = invGlyph && invGlyph.quantity > 0;
            const icon = getItemIcon(id);
            const qty = invGlyph ? invGlyph.quantity : 0;

            return `
                <div class="inv-glyph ${hasInInventory ? 'available' : 'unavailable'}"
                     ${hasInInventory ? `onclick="window.combatUI.addGlyphToPouch('${id}')"` : ''}
                     data-tooltip="${item.name}${hasInInventory ? '\nClick to add all' : '\nNone in inventory'}">
                    <span class="inv-glyph-icon">${icon}</span>
                    <span class="inv-glyph-qty">${qty > 0 ? (qty >= 1000 ? formatNumber(qty) : qty) : '-'}</span>
                </div>
            `;
        }).join('');

        inventoryHtml += `
            <div class="glyph-category">
                <div class="glyph-category-name">${category.name}</div>
                <div class="glyph-category-items">${categoryGlyphs}</div>
            </div>
        `;
    }

    const modal = document.createElement('div');
    modal.className = 'glyph-pouch-modal';
    modal.innerHTML = `
        <div class="glyph-pouch-modal-content">
            <div class="glyph-pouch-modal-header">
                <span>🎒 Glyph Pouch</span>
                <button class="close-modal-btn" onclick="document.querySelector('.glyph-pouch-modal').remove()">×</button>
            </div>
            <div class="glyph-pouch-modal-body">
                <div class="pouch-section">
                    <div class="pouch-section-label">Stored Glyphs <span class="pouch-hint">(click to remove)</span></div>
                    <div class="pouch-slots-row">${pouchSlotsHtml}</div>
                </div>
                <div class="inventory-section">
                    <div class="inventory-section-label">Inventory <span class="inventory-hint">(click to add)</span></div>
                    <div class="glyph-categories-grid">${inventoryHtml}</div>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Close on click outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Get all glyphs from inventory
 */
function getAllGlyphsFromInventory() {
    const slots = gameState.getInventorySlots();
    const glyphs = [];

    for (const slot of slots) {
        if (!slot) continue;
        const item = getItem(slot.itemId);
        if (item && item.category === 'glyph') {
            glyphs.push({
                itemId: slot.itemId,
                name: item.name,
                color: item.color || '#9932CC',
                quantity: slot.quantity
            });
        }
    }

    return glyphs;
}

/**
 * Add glyph from inventory to pouch
 */
function addGlyphToPouch(itemId) {
    const playerQty = gameState.getItemCount(itemId);
    if (playerQty <= 0) {
        toastWarning('No glyphs in inventory');
        return;
    }

    const result = gameState.addToGlyphPouch(itemId, playerQty);
    if (!result.success) {
        toastWarning(result.reason || 'Could not store glyph');
    }
    // Refresh modal content in place
    refreshGlyphPouchModal();
    renderCombatUI();
}

/**
 * Remove glyph from pouch back to inventory
 */
function removeGlyphAndRefresh(slotIndex) {
    const pouch = gameState.getGlyphPouch();
    const slot = pouch.slots[slotIndex];
    if (!slot || !slot.itemId) return;

    const result = gameState.removeFromGlyphPouch(slotIndex, 0); // 0 = remove all
    if (!result.success) {
        toastWarning(result.reason || 'Inventory full');
    }
    // Refresh modal content in place
    refreshGlyphPouchModal();
    renderCombatUI();
}

/**
 * Refresh glyph pouch modal content without removing the modal
 */
function refreshGlyphPouchModal() {
    const modal = document.querySelector('.glyph-pouch-modal');
    if (!modal) return;

    const body = modal.querySelector('.glyph-pouch-modal-body');
    if (!body) return;

    const pouch = gameState.getGlyphPouch();
    const slots = pouch.slots;
    const inventoryGlyphs = getAllGlyphsFromInventory();

    // Build pouch slots display
    const pouchSlotsHtml = slots.map((slot, slotIndex) => {
        if (slot.itemId && slot.quantity > 0) {
            const glyphIcon = getItemIcon(slot.itemId);
            const glyphName = getItemName(slot.itemId);
            const qtyDisplay = slot.quantity >= 1000 ? formatNumber(slot.quantity) : slot.quantity;
            return `
                <div class="pouch-slot filled" onclick="window.combatUI.removeGlyphAndRefresh(${slotIndex})" data-tooltip="${glyphName}\n×${formatWithCommas(slot.quantity)}\nClick to remove">
                    <span class="pouch-slot-icon">${glyphIcon}</span>
                    <span class="pouch-slot-qty">${qtyDisplay}</span>
                </div>
            `;
        } else {
            return `
                <div class="pouch-slot empty" data-tooltip="Empty slot">
                    <span class="pouch-slot-empty">◇</span>
                </div>
            `;
        }
    }).join('');

    // Glyph categories
    const glyphCategories = [
        { name: 'Elemental', ids: ['storm_glyph', 'frost_glyph', 'stone_glyph', 'flame_glyph'] },
        { name: 'Aspect', ids: ['vigor_glyph'] },
        { name: 'Primal', ids: ['verdant_glyph', 'entropy_glyph'] },
        { name: 'Cosmic', ids: ['void_glyph', 'binding_glyph'] },
        { name: 'Forbidden', ids: ['doom_glyph', 'sanguine_glyph', 'spirit_glyph'] }
    ];

    let inventoryHtml = '';
    for (const category of glyphCategories) {
        const categoryGlyphs = category.ids.map(id => {
            const invGlyph = inventoryGlyphs.find(g => g.itemId === id);
            const item = getItem(id);
            if (!item) return '';

            const hasInInventory = invGlyph && invGlyph.quantity > 0;
            const icon = getItemIcon(id);
            const qty = invGlyph ? invGlyph.quantity : 0;

            return `
                <div class="inv-glyph ${hasInInventory ? 'available' : 'unavailable'}"
                     ${hasInInventory ? `onclick="window.combatUI.addGlyphToPouch('${id}')"` : ''}
                     data-tooltip="${item.name}${hasInInventory ? '\nClick to add all' : '\nNone in inventory'}">
                    <span class="inv-glyph-icon">${icon}</span>
                    <span class="inv-glyph-qty">${qty > 0 ? (qty >= 1000 ? formatNumber(qty) : qty) : '-'}</span>
                </div>
            `;
        }).join('');

        inventoryHtml += `
            <div class="glyph-category">
                <div class="glyph-category-name">${category.name}</div>
                <div class="glyph-category-items">${categoryGlyphs}</div>
            </div>
        `;
    }

    body.innerHTML = `
        <div class="pouch-section">
            <div class="pouch-section-label">Stored Glyphs <span class="pouch-hint">(click to remove)</span></div>
            <div class="pouch-slots-row">${pouchSlotsHtml}</div>
        </div>
        <div class="inventory-section">
            <div class="inventory-section-label">Inventory <span class="inventory-hint">(click to add)</span></div>
            <div class="glyph-categories-grid">${inventoryHtml}</div>
        </div>
    `;
}

/**
 * Open zone details modal with full information
 */
function openZoneDetailsModal(zoneId) {
    const zone = getZone(zoneId);
    if (!zone) return;

    // Remove existing modal
    const existingModal = document.querySelector('.zone-details-modal');
    if (existingModal) existingModal.remove();

    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
    const skill = gameState.getSkill(SKILLS.COMBAT);
    const state = combatSkill.getCombatState();
    const isLocked = skill.level < zone.level;
    const isActive = state.inCombat && state.currentZone?.id === zone.id;

    // Get zone progress
    const zoneProgress = gameState.getZoneProgress(zone.id);
    const isCleared = zoneProgress.cleared;
    const lifetimeKills = zoneProgress.lifetimeKills;

    // Get Pyre penalty info
    const pyrePenalty = combatSkill.getPyrePenalty(zone.id);
    const hasPyreReq = zone.pyreLevel !== null;
    const pyreMet = !hasPyreReq || pyreLevel >= zone.pyreLevel;

    // Get visible mobs
    const visibleMobIds = getVisibleMobsForZone(zone.id, pyreLevel);
    const hiddenMobCount = getHiddenMobCount(zone.id, pyreLevel);

    // Calculate mob stats
    const mobs = visibleMobIds.map(mobId => getMob(mobId)).filter(m => m);
    const avgXP = mobs.length > 0 ? Math.round(mobs.reduce((sum, m) => sum + m.xp, 0) / mobs.length) : 0;

    // HP range
    const hpValues = mobs.map(m => m.hp);
    const minHp = Math.min(...hpValues);
    const maxHp = Math.max(...hpValues);
    const hpDisplay = minHp === maxHp ? `${minHp}` : `${minHp}-${maxHp}`;

    // Attack speed range
    const speedValues = mobs.map(m => m.attackSpeed / 1000);
    const minSpeed = Math.min(...speedValues);
    const maxSpeed = Math.max(...speedValues);
    const speedDisplay = minSpeed === maxSpeed ? `${minSpeed.toFixed(1)}s` : `${minSpeed.toFixed(1)}-${maxSpeed.toFixed(1)}s`;

    // Max hit range
    const maxHitValues = mobs.map(m => m.attackMax || m.attack);
    const minMaxHit = Math.min(...maxHitValues);
    const maxMaxHit = Math.max(...maxHitValues);
    const maxHitDisplay = minMaxHit === maxMaxHit ? `${minMaxHit}` : `${minMaxHit}-${maxMaxHit}`;

    // Pyre section
    let pyreHtml = '';
    if (hasPyreReq) {
        if (!pyreMet) {
            const penaltyPct = Math.round(pyrePenalty.deficit * 50);
            pyreHtml = `
                <div class="modal-pyre-req insufficient">
                    <span class="pyre-icon">${getFireSpriteSm()}</span>
                    <span>Pyre ${zone.pyreLevel} recommended</span>
                    <span class="pyre-penalty">-${penaltyPct}% damage, attack speed, loot</span>
                </div>
            `;
        } else if (pyrePenalty.bonus > 0) {
            const bonusPct = (pyrePenalty.bonus * 100).toFixed(1);
            pyreHtml = `
                <div class="modal-pyre-req bonus">
                    <span class="pyre-icon">${getFireSpriteSm()}</span>
                    <span>Pyre bonus active</span>
                    <span class="pyre-bonus">+${bonusPct}% damage, attack speed, loot</span>
                </div>
            `;
        } else {
            pyreHtml = `
                <div class="modal-pyre-req sufficient">
                    <span class="pyre-icon">${getFireSpriteSm()}</span>
                    <span>Pyre ${zone.pyreLevel} - Requirement met</span>
                </div>
            `;
        }
    }

    // Mobs section with individual stats, kill counts, and drops
    const mobsHtml = visibleMobIds.map(mobId => {
        const mob = getMob(mobId);
        if (!mob) return '';
        const mobKills = gameState.getKillCount(mobId);
        const isPyreVeil = mob.pyreVeilLevel;
        const research = gameState.getMobResearch(mobId);
        const researchLevel = gameState.getMobResearchLevel(mobId);

        // Research status
        let researchStatus = '';
        if (researchLevel >= 2) {
            researchStatus = '<span class="research-badge complete">Researched</span>';
        } else if (researchLevel >= 1) {
            researchStatus = `<span class="research-badge partial">${research}/6</span>`;
        } else if (research > 0) {
            researchStatus = `<span class="research-badge partial">${research}/3</span>`;
        }

        // Weakness/Resistance display
        let weakResistHtml = '';
        if (researchLevel >= 1 && mob.weakTo) {
            weakResistHtml += `<span class="mob-weakness" data-tooltip="Weak to ${getStyleName(mob.weakTo)}">⬇️${getStyleIcon(mob.weakTo)}</span>`;
        }
        if (researchLevel >= 2 && mob.resistantTo) {
            weakResistHtml += `<span class="mob-resistance" data-tooltip="Resistant to ${getStyleName(mob.resistantTo)}">⬆️${getStyleIcon(mob.resistantTo)}</span>`;
        }

        // Mob-specific drops (show all)
        const mobLoot = getMobLoot(mobId, zone.id);
        const mobDrops = [...(mobLoot.common || []), ...(mobLoot.rare || [])];
        const dropsHtml = mobDrops.length > 0 ? `
            <div class="mob-drops">
                ${mobDrops.map(drop => {
                    const icon = getItemIcon(drop.itemId);
                    const itemTooltip = buildItemInfoTooltip(drop.itemId);
                    const isRare = mobLoot.rare?.includes(drop);
                    const minQty = drop.minQty || 1;
                    const maxQty = drop.maxQty || minQty;
                    const qtyText = maxQty > minQty ? ` (${minQty}-${maxQty})` : (minQty > 1 ? ` (x${minQty})` : '');
                    const fraction = formatDropRate(drop.chance);
                    return `<span class="mob-drop-icon ${isRare ? 'rare' : ''}" data-tooltip="${escapeAttr(itemTooltip)}${qtyText}\n${fraction}">${icon}</span>`;
                }).join('')}
            </div>
        ` : '';

        return `
            <div class="modal-mob-row ${isPyreVeil ? 'pyre-veil' : ''}">
                <div class="mob-info">
                    <span class="mob-icon">${getMobIcon(mob)}</span>
                    <span class="mob-name">${isPyreVeil ? getFireSpriteSm() : ''}${mob.name}</span>
                    ${researchStatus}
                </div>
                <div class="mob-stats-row">
                    <span data-tooltip="HP">❤️${mob.hp}</span>
                    <span data-tooltip="Attack Speed">⚡${(mob.attackSpeed / 1000).toFixed(1)}s</span>
                    <span data-tooltip="Max Hit">💥${mob.attackMax || mob.attack}</span>
                    <span data-tooltip="XP">⚔️${mob.xp}</span>
                    ${weakResistHtml}
                </div>
                ${dropsHtml}
                <div class="mob-kills">💀 ${formatWithCommas(mobKills)} kills</div>
            </div>
        `;
    }).join('');

    // Hidden mobs indicator
    let hiddenHtml = '';
    if (hiddenMobCount > 0) {
        const nextHidden = getNextHiddenMob(zone.id, pyreLevel);
        const pyreGap = nextHidden ? nextHidden.pyreRequired - pyreLevel : 0;
        const hint = pyreGap <= 10 ? `Pyre ${nextHidden.pyreRequired}` : 'Pyre ????';
        hiddenHtml = `
            <div class="modal-hidden-mobs">
                ${getFireSpriteSm()} ${hiddenMobCount} hidden mob${hiddenMobCount > 1 ? 's' : ''} - Next at ${hint}
            </div>
        `;
    }

    // Loot section
    const zoneLoot = getZoneLoot(zone.id);
    const lootItems = [...zoneLoot.common, ...zoneLoot.rare];
    const lootHtml = lootItems.map(drop => {
        const icon = getItemIcon(drop.itemId);
        const itemTooltip = buildItemInfoTooltip(drop.itemId);
        const isRare = zoneLoot.rare.includes(drop);
        const minQty = drop.minQty || 1;
        const maxQty = drop.maxQty || minQty;
        const qtyText = maxQty > minQty ? ` (${minQty}-${maxQty})` : (minQty > 1 ? ` (x${minQty})` : '');
        const fraction = formatDropRate(drop.chance);
        return `<span class="loot-item ${isRare ? 'rare' : ''}" data-tooltip="${escapeAttr(itemTooltip)}${qtyText}\n${fraction}">${icon}</span>`;
    }).join('');

    // Patrol section
    let patrolHtml = '';
    const combatPets = gameState.getCombatCapablePets();
    const patrollingPet = gameState.getPatrollingPet(zone.id);
    const allMobsResearched = zone.mobs.every(mobId => gameState.getMobResearchLevel(mobId) >= 2);

    if (combatPets.length > 0 && !isLocked) {
        if (patrollingPet) {
            const petData = getPetData(patrollingPet.id);
            let statusText = 'Researching';
            let statusClass = 'researching';
            if (allMobsResearched) {
                statusText = 'Research Complete';
                statusClass = 'complete';
            } else if (!patrollingPet.contractId) {
                statusText = 'Idle - No Contract';
                statusClass = 'idle';
            }
            patrolHtml = `
                <div class="modal-patrol-section">
                    <div class="patrol-current">
                        <span class="patrol-pet-icon">${petData?.icon || '🐾'}</span>
                        <span class="patrol-pet-name">${petData?.name || 'Pet'}</span>
                        <span class="patrol-status ${statusClass}">${statusText}</span>
                    </div>
                    <button class="patrol-manage-btn" onclick="window.combatUI.showPatrolManagement('${zone.id}'); document.querySelector('.zone-details-modal').remove();">Manage Patrol</button>
                </div>
            `;
        } else if (!allMobsResearched) {
            const availablePets = combatPets.filter(p => !p.patrolZone);
            if (availablePets.length > 0) {
                patrolHtml = `
                    <div class="modal-patrol-section">
                        <button class="patrol-assign-btn" onclick="window.combatUI.showPatrolSelect('${zone.id}'); document.querySelector('.zone-details-modal').remove();"
                                data-tooltip="Send a pet to research this zone's mobs. Discovered weaknesses and resistances help in combat!">
                            ${getSkillIcon(SKILLS.HUSBANDRY)} Assign Pet Patrol
                        </button>
                    </div>
                `;
            }
        }
    }

    // Target farming section (only if cleared)
    let targetFarmingHtml = '';
    if (isCleared) {
        const targetButtons = visibleMobIds.map(mobId => {
            const mob = getMob(mobId);
            const mobKills = gameState.getKillCount(mobId);
            const isPyreVeil = mob?.pyreVeilLevel;
            return `
                <button class="modal-target-btn ${isPyreVeil ? 'pyre-veil' : ''}"
                        onclick="window.combatUI.startFightingMob('${zone.id}', '${mobId}'); document.querySelector('.zone-details-modal').remove();">
                    ${isPyreVeil ? getFireSpriteSm() : ''}${getMobIcon(mob)} ${mob?.name || mobId}
                </button>
            `;
        }).join('');

        targetFarmingHtml = `
            <div class="modal-target-section">
                <div class="section-label">Target Farming</div>
                <div class="target-buttons">${targetButtons}</div>
            </div>
        `;
    }

    const modal = document.createElement('div');
    modal.className = 'zone-details-modal';
    modal.innerHTML = `
        <div class="zone-details-content" style="--zone-color: ${zone.color}">
            <div class="zone-details-header">
                <div class="zone-title">
                    <span class="zone-name">${zone.name}</span>
                    <span class="zone-level">Level ${zone.level}</span>
                    ${isCleared ? '<span class="cleared-badge">CLEARED</span>' : ''}
                </div>
                <button class="close-modal-btn" onclick="document.querySelector('.zone-details-modal').remove()">×</button>
            </div>

            <div class="zone-details-body">
                <div class="zone-description">${zone.description}</div>

                ${pyreHtml}

                <div class="modal-columns">
                    <div class="modal-column">
                        <div class="modal-section">
                            <div class="section-label">Combat Stats</div>
                            <div class="zone-combat-stats">
                                <div class="combat-stat">
                                    <span class="stat-icon">❤️</span>
                                    <span class="stat-label">HP</span>
                                    <span class="stat-value">${hpDisplay}</span>
                                </div>
                                <div class="combat-stat">
                                    <span class="stat-icon">⚡</span>
                                    <span class="stat-label">Speed</span>
                                    <span class="stat-value">${speedDisplay}</span>
                                </div>
                                <div class="combat-stat">
                                    <span class="stat-icon">💥</span>
                                    <span class="stat-label">Max Hit</span>
                                    <span class="stat-value">${maxHitDisplay}</span>
                                </div>
                                <div class="combat-stat">
                                    <span class="stat-icon">⚔️</span>
                                    <span class="stat-label">Avg XP</span>
                                    <span class="stat-value">${avgXP}/kill</span>
                                </div>
                            </div>
                        </div>

                        <div class="modal-section">
                            <div class="section-label">Zone Drops</div>
                            <div class="zone-loot-grid">${lootHtml}</div>
                        </div>

                        ${patrolHtml}
                    </div>

                    <div class="modal-column">
                        <div class="modal-section">
                            <div class="section-label">Mobs (${formatWithCommas(lifetimeKills)} total kills)</div>
                            <div class="zone-mobs-list">
                                ${mobsHtml}
                                ${hiddenHtml}
                            </div>
                        </div>

                        ${targetFarmingHtml}
                    </div>
                </div>
            </div>

            <div class="zone-details-footer">
                ${isLocked ? `
                    <div class="zone-locked-msg">🔒 Requires Combat Level ${zone.level}</div>
                ` : `
                    <button class="zone-fight-btn ${isActive ? 'stop' : 'start'}"
                            onclick="window.combatUI.${isActive ? 'stopFighting' : 'startFighting'}('${zone.id}'); document.querySelector('.zone-details-modal').remove();">
                        ${isActive ? 'Stop Fighting' : '⚔️ Start Fighting'}
                    </button>
                `}
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Close on click outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });
}

/**
 * Open tier details modal for tiered zones (e.g., Ashen Crucible)
 * Shows boss info, stats, drops, research status, and boss targeting
 */
function openTierDetailsModal(zoneId, tierId) {
    const zone = getZone(zoneId);
    if (!zone || !zone.tiers) return;

    const tier = zone.tiers[tierId];
    if (!tier) return;

    // Remove existing modal
    const existingModal = document.querySelector('.zone-details-modal');
    if (existingModal) existingModal.remove();

    const skill = gameState.getSkill(SKILLS.COMBAT);
    const state = combatSkill.getCombatState();
    const unlocks = gameState.player.quests?.unlocks || [];
    const tierUnlocked = unlocks.includes(tier.unlock);
    const isLocked = skill.level < zone.level || !tierUnlocked;
    const isActiveTier = state.inCombat && state.currentZone?.id === zone.id && state.currentTier === tierId;

    // Get tier mobs
    const tierMobs = (tier.mobs || []).map(id => getMob(id)).filter(Boolean);

    // Calculate stats
    const avgXP = tierMobs.length > 0 ? Math.round(tierMobs.reduce((sum, m) => sum + m.xp, 0) / tierMobs.length) : 0;
    const hpValues = tierMobs.map(m => m.hp);
    const minHp = Math.min(...hpValues);
    const maxHp = Math.max(...hpValues);
    const hpDisplay = minHp === maxHp ? `${minHp}` : `${minHp}-${maxHp}`;

    const speedValues = tierMobs.map(m => m.attackSpeed / 1000);
    const minSpeed = Math.min(...speedValues);
    const maxSpeed = Math.max(...speedValues);
    const speedDisplay = minSpeed === maxSpeed ? `${minSpeed.toFixed(1)}s` : `${minSpeed.toFixed(1)}-${maxSpeed.toFixed(1)}s`;

    const maxHitValues = tierMobs.map(m => m.attackMax || m.attack);
    const minMaxHit = Math.min(...maxHitValues);
    const maxMaxHit = Math.max(...maxHitValues);
    const maxHitDisplay = minMaxHit === maxMaxHit ? `${minMaxHit}` : `${minMaxHit}-${maxMaxHit}`;

    // Build boss info HTML
    const bossesHtml = tierMobs.map(mob => {
        const bossKills = gameState.getBossKillCount(mob.id);
        const research = gameState.getMobResearch(mob.id);
        const researchLevel = gameState.getMobResearchLevel(mob.id);

        // Research status
        let researchStatus = '';
        if (researchLevel >= 2) {
            researchStatus = '<span class="research-badge complete">Researched</span>';
        } else if (researchLevel >= 1) {
            researchStatus = `<span class="research-badge partial">${research}/6</span>`;
        } else if (research > 0) {
            researchStatus = `<span class="research-badge partial">${research}/3</span>`;
        }

        // Weakness/Resistance display
        let weakResistHtml = '';
        if (researchLevel >= 1 && mob.weakTo) {
            weakResistHtml += `<span class="mob-weakness" data-tooltip="Weak to ${getStyleName(mob.weakTo)}">⬇️${getStyleIcon(mob.weakTo)}</span>`;
        }
        if (researchLevel >= 2 && mob.resistantTo) {
            weakResistHtml += `<span class="mob-resistance" data-tooltip="Resistant to ${getStyleName(mob.resistantTo)}">⬆️${getStyleIcon(mob.resistantTo)}</span>`;
        }

        // Boss-specific drops
        const mobLoot = getMobLoot(mob.id, zoneId);
        const mobDrops = [...(mobLoot.common || []), ...(mobLoot.rare || [])];
        const dropsHtml = mobDrops.length > 0 ? `
            <div class="mob-drops">
                ${mobDrops.map(drop => {
                    const icon = getItemIcon(drop.itemId);
                    const itemTooltip = buildItemInfoTooltip(drop.itemId);
                    const isRare = mobLoot.rare?.includes(drop);
                    const minQty = drop.minQty || 1;
                    const maxQty = drop.maxQty || minQty;
                    const qtyText = maxQty > minQty ? ` (${minQty}-${maxQty})` : (minQty > 1 ? ` (x${minQty})` : '');
                    const fraction = formatDropRate(drop.chance);
                    return `<span class="mob-drop-icon ${isRare ? 'rare' : ''}" data-tooltip="${escapeAttr(itemTooltip)}${qtyText}\n${fraction}">${icon}</span>`;
                }).join('')}
            </div>
        ` : '';

        return `
            <div class="modal-mob-row boss-row">
                <div class="mob-info">
                    <span class="mob-icon">${getMobIcon(mob)}</span>
                    <span class="mob-name">${tierUnlocked ? mob.name : '??????'}</span>
                    ${researchStatus}
                </div>
                <div class="mob-stats-row">
                    <span data-tooltip="HP">❤️${mob.hp}</span>
                    <span data-tooltip="Attack Speed">⚡${(mob.attackSpeed / 1000).toFixed(1)}s</span>
                    <span data-tooltip="Max Hit">💥${mob.attackMax || mob.attack}</span>
                    <span data-tooltip="XP">⚔️${mob.xp}</span>
                    ${weakResistHtml}
                </div>
                ${dropsHtml}
                <div class="mob-kills">💀 ${formatWithCommas(bossKills)} defeats</div>
                ${tierUnlocked && !isLocked ? `
                    <button class="modal-target-btn boss-target"
                            onclick="window.combatUI.startFightingBoss('${zoneId}', '${tierId}', '${mob.id}'); document.querySelector('.zone-details-modal').remove();">
                        ⚔️ Fight ${mob.name}
                    </button>
                ` : ''}
            </div>
        `;
    }).join('');

    // Patrol section for this tier
    let patrolHtml = '';
    const combatPets = gameState.getCombatCapablePets();
    const patrollingPet = gameState.getPatrollingPet(zoneId);
    const allMobsResearched = tier.mobs.every(mobId => gameState.getMobResearchLevel(mobId) >= 2);

    if (combatPets.length > 0 && tierUnlocked) {
        if (patrollingPet) {
            const petData = getPetData(patrollingPet.id);
            let statusText = 'Researching';
            let statusClass = 'researching';
            if (allMobsResearched) {
                statusText = 'Research Complete';
                statusClass = 'complete';
            } else if (!patrollingPet.contractId) {
                statusText = 'Idle - No Contract';
                statusClass = 'idle';
            }
            patrolHtml = `
                <div class="modal-patrol-section">
                    <div class="patrol-current">
                        <span class="patrol-pet-icon">${petData?.icon || '🐾'}</span>
                        <span class="patrol-pet-name">${petData?.name || 'Pet'}</span>
                        <span class="patrol-status ${statusClass}">${statusText}</span>
                    </div>
                    <button class="patrol-manage-btn" onclick="window.combatUI.showPatrolManagement('${zoneId}'); document.querySelector('.zone-details-modal').remove();">Manage Patrol</button>
                </div>
            `;
        } else if (!allMobsResearched) {
            const availablePets = combatPets.filter(p => !p.patrolZone);
            if (availablePets.length > 0) {
                patrolHtml = `
                    <div class="modal-patrol-section">
                        <button class="patrol-assign-btn" onclick="window.combatUI.showPatrolSelect('${zoneId}'); document.querySelector('.zone-details-modal').remove();"
                                data-tooltip="Send a pet to research this zone's bosses. Discovered weaknesses and resistances help in combat!">
                            ${getSkillIcon(SKILLS.HUSBANDRY)} Assign Pet Patrol
                        </button>
                    </div>
                `;
            }
        }
    }

    const modal = document.createElement('div');
    modal.className = 'zone-details-modal';
    modal.innerHTML = `
        <div class="zone-details-content tiered" style="--zone-color: ${zone.color}">
            <div class="zone-details-header">
                <div class="zone-title">
                    <span class="zone-name">${zone.name} - ${tier.name}</span>
                    <span class="zone-level">${tierId.toUpperCase()}</span>
                </div>
                <button class="close-modal-btn" onclick="document.querySelector('.zone-details-modal').remove()">×</button>
            </div>

            <div class="zone-details-body">
                <div class="modal-section">
                    <div class="section-label">Combat Stats</div>
                    <div class="zone-combat-stats">
                        <div class="combat-stat">
                            <span class="stat-icon">❤️</span>
                            <span class="stat-label">HP</span>
                            <span class="stat-value">${hpDisplay}</span>
                        </div>
                        <div class="combat-stat">
                            <span class="stat-icon">⚡</span>
                            <span class="stat-label">Speed</span>
                            <span class="stat-value">${speedDisplay}</span>
                        </div>
                        <div class="combat-stat">
                            <span class="stat-icon">💥</span>
                            <span class="stat-label">Max Hit</span>
                            <span class="stat-value">${maxHitDisplay}</span>
                        </div>
                        <div class="combat-stat">
                            <span class="stat-icon">⚔️</span>
                            <span class="stat-label">Avg XP</span>
                            <span class="stat-value">${avgXP}/kill</span>
                        </div>
                    </div>
                </div>

                ${patrolHtml}

                <div class="modal-section">
                    <div class="section-label">Bosses</div>
                    <div class="zone-mobs-list">
                        ${bossesHtml}
                    </div>
                </div>
            </div>

            <div class="zone-details-footer">
                ${!tierUnlocked ? `
                    <div class="zone-locked-msg">🔒 Complete quest to unlock</div>
                ` : isLocked ? `
                    <div class="zone-locked-msg">🔒 Requires Combat Level ${zone.level}</div>
                ` : isActiveTier ? `
                    <button class="zone-fight-btn stop"
                            onclick="window.combatUI.stopFighting(); document.querySelector('.zone-details-modal').remove();">
                        Stop Fighting
                    </button>
                ` : `
                    <div class="zone-hint">Select a boss above to fight</div>
                `}
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Close on click outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.remove();
    });

    // Close on escape key
    const escHandler = (e) => {
        if (e.key === 'Escape') {
            modal.remove();
            document.removeEventListener('keydown', escHandler);
        }
    };
    document.addEventListener('keydown', escHandler);
}
